/**
 * Product Feature Catalog
 *
 * Code-level SSOT for product capabilities. Add new customer-visible
 * functionality here first, then derive runtime flags, package availability,
 * and documentation links from the same record.
 */

export const PRODUCT_PACKAGE_KEYS = [
  'free',
  'starter',
  'pro',
  'business',
  'scale',
  'enterprise',
] as const;

export type ProductPackageKey = (typeof PRODUCT_PACKAGE_KEYS)[number];

/**
 * Тариф организации как ключ каталога, а не строка из колонки.
 *
 * `orgs.subscription_tier` — свободный TEXT со значением по умолчанию. Незнакомое
 * значение («trial», пустая строка) сделало бы `feature.packages[tier]`
 * `undefined` для КАЖДОЙ возможности каталога — то есть всё подряд выглядело бы
 * недоступным. Сведение к `free` честнее: пометка «доступно на Pro и выше» на
 * Free-организации хотя бы не ложь.
 *
 * Одна функция на всех, кто отдаёт тариф клиенту: веб (`/users/me`) и встроенное
 * приложение (`/embedded/whats-new`). Две копии этого правила уже однажды
 * разошлись бы на первом же новом тарифе.
 */
export function toProductPackageKey(value: string | null | undefined): ProductPackageKey {
  return (PRODUCT_PACKAGE_KEYS as readonly string[]).includes(value ?? '')
    ? (value as ProductPackageKey)
    : 'free';
}

export type ProductFeatureArea =
  | 'access'
  | 'admin'
  | 'analytics'
  | 'automation'
  | 'billing'
  | 'coaching'
  | 'collaboration'
  | 'compliance'
  | 'core_pipeline'
  | 'data'
  | 'demo'
  | 'integrations'
  | 'notifications'
  | 'onboarding'
  | 'operations'
  | 'prompting'
  | 'records'
  | 'reporting'
  | 'review'
  | 'search'
  | 'settings'
  | 'sharing'
  | 'ui';

export type ProductFeatureStatus =
  | 'available'
  | 'beta'
  | 'partial'
  | 'planned'
  | 'prototype'
  | 'not_implemented'
  | 'deprecated';

export type ProductSurface =
  | 'admin_console'
  | 'bitrix24_embedded'
  | 'edge_api'
  | 'public_docs'
  | 'saas_app'
  | 'website'
  | 'workflows_api';

export type ProductFeatureAvailability =
  'included' | 'not_included' | 'add_on' | 'custom' | 'internal';

export type ProductFeatureFlagCategory = 'ui' | 'page' | 'business' | 'beta' | 'admin';

export interface ProductFeatureReference {
  readonly label: string;
  readonly path: string;
}

export interface ProductFeatureFlagSpec {
  /** Stable key used everywhere: DB rows, API responses, config checks. */
  readonly key: string;
  /** Platform-level default before package defaults and org overrides. */
  readonly defaultValue: boolean;
  /** Whether the flag is wired to real behavior. */
  readonly isImplemented: boolean;
  /** Group used by the admin UI to organize the flags table. */
  readonly category: ProductFeatureFlagCategory;
  /** Operator-facing implementation note. */
  readonly note?: string;
  /**
   * Only the platform owner may write this flag (PUT/DELETE
   * /api/v1/feature-flags/:key). Without it an org member with
   * `manage_settings` may set any known flag for their own org — right for a
   * customer preference, wrong for a gate that hides data from that very
   * customer until Aelo opens it.
   */
  readonly platformOwnerOnly?: boolean;
}

export interface ProductFeatureDefinition {
  /** Stable feature id for docs, packaging, changelog, and future telemetry. */
  readonly id: string;
  readonly title: string;
  readonly summary: string;
  readonly area: ProductFeatureArea;
  readonly status: ProductFeatureStatus;
  readonly customerVisible: boolean;
  readonly surfaces: readonly ProductSurface[];
  readonly packages: Readonly<Record<ProductPackageKey, ProductFeatureAvailability>>;
  readonly flag?: ProductFeatureFlagSpec;
  readonly limitKeys?: readonly string[];
  readonly docs?: readonly ProductFeatureReference[];
  readonly code?: readonly ProductFeatureReference[];
  readonly notes?: readonly string[];
}

function packageAvailability(
  fallback: ProductFeatureAvailability,
  overrides: Partial<Record<ProductPackageKey, ProductFeatureAvailability>> = {}
): Readonly<Record<ProductPackageKey, ProductFeatureAvailability>> {
  const result = PRODUCT_PACKAGE_KEYS.reduce(
    (acc, key) => {
      acc[key] = overrides[key] ?? fallback;
      return acc;
    },
    {} as Record<ProductPackageKey, ProductFeatureAvailability>
  );
  return Object.freeze(result);
}

const includedEverywhere = () => packageAvailability('included');
const unavailableEverywhere = () => packageAvailability('not_included');

/**
 * ВНИМАНИЕ: матрица пакетов сама по себе НИЧЕГО не запрещает.
 *
 * Единственный путь от каталога к рантайму — `flag`:
 * `getFeatureFlagDefaultsForPackage()` превращает в дефолты флагов только те
 * записи, у которых он есть, и уже флаг проверяется на месте. У фичи без
 * `flag` строка `starterPlus()` — это обещание, которое никто не исполняет:
 * продукт работает одинаково на всех тарифах, а витрины (карточки тарифов в
 * приложении, страница цен) показывают ограничение, которого нет.
 *
 * Именно так и вышло: одиннадцать клиентских фич (Bitrix24 и smart process,
 * кэш CRM-полей, правила маршрутизации, запросы доступа и онбординг из
 * портала, аннотации и ревью, прямая телефония, импорт из CRM, email-дайджесты,
 * управление инцидентами) значились платными, не будучи ограниченными нигде.
 * Все они переведены в `includedEverywhere()` — это фиксация факта, а не
 * изменение продукта: поведение не поменялось, поменялось описание.
 *
 * Поэтому: ставя `starterPlus()`/`proPlus()` клиентской фиче
 * со статусом `available`, дай ей `flag` — иначе тест
 * «платная фича обязана иметь рычаг» не пропустит запись.
 */
const starterPlus = () =>
  packageAvailability('not_included', {
    starter: 'included',
    pro: 'included',
    business: 'included',
    scale: 'included',
    enterprise: 'included',
  });
const proPlus = () =>
  packageAvailability('not_included', {
    pro: 'included',
    business: 'included',
    scale: 'included',
    enterprise: 'included',
  });
// Every paid tier, regardless of floor. Reads as "gated on paid vs free" —
// today that's identical to starterPlus() (starter is the lowest paid tier),
// but the name documents intent independent of which tier introduces access.
const paidOnly = () => starterPlus();

const uiDomainClient = (label: string, fileName: string): ProductFeatureReference => ({
  label,
  path: `apps/ui-worker/src/lib/api/domains/${fileName}`,
});

const databaseRepository = (
  label: string,
  repositoryPath: string
): ProductFeatureReference => ({
  label,
  path: `packages/database/src/repositories/${repositoryPath}`,
});

export const PRODUCT_FEATURE_CATALOG = [
  {
    id: 'pipeline.ingest_transcribe_analyze',
    title: 'Ingest, Transcribe, Analyze Pipeline',
    summary:
      'Core record lifecycle from ingress through transcription, AI analysis, delivery, and side effects.',
    area: 'core_pipeline',
    status: 'available',
    customerVisible: true,
    surfaces: ['edge_api', 'workflows_api', 'saas_app', 'bitrix24_embedded'],
    packages: includedEverywhere(),
    limitKeys: ['included_minutes'],
    docs: [
      { label: 'Architecture', path: 'ARCHITECTURE.md' },
      { label: 'Quick start', path: 'docs/architecture/ARCHITECTURE_QUICK_START.md' },
    ],
    code: [
      databaseRepository('Record repository', 'RecordRepository.ts'),
      databaseRepository('Transcript repository', 'TranscriptRepository.ts'),
      databaseRepository('Ingest receipt repository', 'IngestReceiptRepository.ts'),
      databaseRepository('Ingest checkpoint repository', 'IngestCheckpointRepository.ts'),
    ],
  },
  {
    id: 'pipeline.analysis_verification',
    title: 'Analysis Verification And Grounding',
    summary:
      'Second pass over a finished analysis before it is published: a safety verifier re-reads the transcript on every analysis, and a fact verifier joins it whenever the analysis carries something your CRM will act on — a card field this delivery will fill, or an agreement your settings put to work as a follow-up task or as a check that a promised call-back really happened. A field the delivery does not write — a Smart Process destination, autofill off, a field outside the autofill list, a low-confidence value — is not checked and is not written either. It keeps the claims whose quote can be located in the transcript (adding the quote and its timestamp as evidence) and withholds the ones that cannot be grounded instead of delivering them. A free-text field that states several facts is checked fact by fact: each fact needs its own quote, a statement that something was not agreed passes when nothing in the conversation says otherwise, though such a field is not written to the card, and one refuted fact removes the whole field; when the checked facts do not add up to the text of the field, it stays unconfirmed. When an analysis carries neither, there is nothing to guard, and the fact pass is skipped rather than spent on grading the wording of the report. A call whose category is ambiguous gets one extra adjudication pass. Free narrative is never rewritten: a high-impact statement stays in the analysis as drafted and gets an objection only when the transcript shows it is wrong — words that say otherwise, a unit or currency the speaker never said, a date the spoken words do not fix, or a different product or party; a statement the conversation simply never mentions gets no objection, because an absence cannot be shown with a quote. What withholds the automatic part — card field updates and follow-up tasks — is an unconfirmed claim that was meant for the CRM (a card field or an agreement), a safety signal, a role conflict or an ambiguous category; an objection to the wording of the report does not withhold it on its own. The report still reaches your CRM either way, carrying a note whenever the automatic part was withheld. On the record screen the state sits as a badge beside the overall score: hovering shows the status and the reasons, clicking opens the individual statements — the wording the verifier objected to and the line from the conversation that shows the error (hidden only when it contained personal data that minimisation removed). The printed PDF always shows that breakdown expanded. Verification is not a setting you can switch off, and the verifier calls that run are charged to the organization budget alongside the analysis itself.',
    area: 'core_pipeline',
    status: 'available',
    customerVisible: true,
    surfaces: ['workflows_api', 'saas_app'],
    packages: includedEverywhere(),
    // No flag: verification is part of producing an analysis, not an option.
    // `enableHumanReview` (review.human_review_queue) only decides where the
    // CRM automation that verification withheld goes — it never turns the
    // passes off.
    docs: [
      {
        label: 'Analysis quality fixes (verification passes)',
        path: 'docs/plans/2026-08-12-analysis-quality-fixes.md',
      },
      {
        label: 'Feature overview (generated customer page)',
        path: 'apps/website/src/content/docs/en/docs/user-guide/features.md',
      },
    ],
    code: [
      {
        label: 'Safety and fact verification steps',
        path: 'apps/workflows-api/src/workflows/record-pipeline/steps/verification-step.ts',
      },
      {
        label: 'Verification merge (controlled repair, review reasons)',
        path: 'apps/workflows-api/src/workers/analyze/verification/merge.ts',
      },
      {
        label: 'Quote grounding against transcript and diarization',
        path: 'apps/workflows-api/src/workers/analyze/verification/grounding.ts',
      },
      {
        label: 'Category adjudication step (ambiguous categories only)',
        path: 'apps/workflows-api/src/workflows/record-pipeline/steps/category-adjudication-step.ts',
      },
      {
        label: 'Authoritative write of the verified analysis',
        path: 'apps/workflows-api/src/workflows/record-pipeline/steps/finalize-verification-step.ts',
      },
      {
        label: 'Record pipeline workflow (step wiring, verifier spend)',
        path: 'apps/workflows-api/src/workflows/record-pipeline/workflow.ts',
      },
      {
        label: 'Verification notice on the record screen',
        path: 'apps/ui-worker/src/components/details/v2/analysis/VerificationNotice.tsx',
      },
    ],
    notes: [
      'Cost is customer-observable: each pass that runs books its own spend under a stable `verification` ledger key on top of the primary analyze spend, so a record costs more than the analyze call alone. The safety pass runs on every analysis; the fact pass is billed only when the analysis carries a CRM claim to guard, and adjudication only for the ambiguous calls that trigger it.',
      'Degradation is fail-closed for automation, open for reading: if the balance or the provider is unavailable, the analysis is still published and the customer still reads it, but it carries VERIFIER_UNAVAILABLE and its automation claims (extractedCrmFields, agreements) are kept yet held back: nothing is written until a human approves the review.',
      'Records without a primary draft — empty or very short calls, and a reused cached analysis — skip the passes entirely and keep their existing fail-closed metadata, exactly as before this stage existed.',
      'What the skipped fact pass leaves unchecked, decided deliberately (#810): the summary does travel outside Aelo — into the CRM timeline report and the Smart Process summary field — so an analysis carrying neither card fields nor agreements now sends prose no fact verifier read. That is accepted rather than overlooked. What `verificationStatus` governs is whether the analysis may act by itself, and that is wider than the two claim arrays: the same gate also holds the card fields a routing rule would write, whose conditions are JsonLogic over the unverified score, sentiment and category. That path used to be live despite this catalog calling delivery.crm_delivery_rules unavailable — the flag hid the settings section only, while /api/v1/delivery-rules accepted a non-empty fieldsToUpdate and routingStep loaded enabled rules without consulting the flag, so an organization holding an API-created rule wrote CRM fields off a no-claim analysis under an open gate. Both ends now enforce the flag (#814): the routing step refuses before reading the rules, and creating, editing, toggling or reordering a rule is refused by the API. On the record screen and in the CRM the product claims nothing: the badge appears only for a non-verified status or a non-empty objection list, the CRM note only when the automatic part was withheld. The webhook channel used to claim it — it sends the summary beside verification.status "verified", which a customer integration reads as "this text was checked". Its payload now carries fact_check alongside the status (ran / skipped_no_claims / unavailable / unknown), so a skipped pass is distinguishable; status itself is unchanged because connected integrations read it. The routing gap that made this wider scope reachable is closed (#814). The safety pass still re-reads every analysis. Reviewing the summary prose is a responsibility deliberately dropped for no-claim analyses, not one that never existed — the pass\'s own prompt instructs it to inspect narrative sections for unsupported high-impact facts, and design requirement W-04 has the verifier check the primary narrative against the transcript; it still does both whenever claims are present. Dropping it for no-claim analyses bought back two records in three that the old behaviour withheld. Summary quality is tracked separately (#773).',
    ],
  },
  {
    id: 'pipeline.stage_aware_scoring',
    title: 'Stage-Aware Scoring — Deal Stage, Outcome & Progression',
    summary:
      "Captures a Bitrix24 or Uspacy deal's CRM pipeline/stage at analysis time (crm_stage_id/crm_pipeline_id/crm_stage_semantic/crm_stage_source) and grades the call against the scorecard bound to that stage — exact binding first, stage-phase semantic as fallback, category binding when neither matches. The stage phase comes from the CRM's own marker (Bitrix24's won/lost semantics; for Uspacy, only a funnel's closing success/fail stage), else from the phase the customer chose for the stage on the Deal Stages tab, else is guessed by AI from the org's pipeline and stage names (only the names are sent to the AI model, asked again only when a stage is added or renamed or an earlier attempt gave no answer) and stored as a guess (crm_stage_semantic_origin), marked as such on the Deal Stages tab and the Objections screen; changing a stage's phase re-phases the calls already recorded there (their scores are not recalculated). On a recurring schedule it also syncs each linked Bitrix24 or Uspacy deal's outcome (open/won/lost; for Uspacy from the funnel's closing success/fail stage) to drive a deal-outcome funnel (call volume by won/lost, broken down by average quality score) and derives a per-call progression verdict (advanced/held/regressed) of the deal's stage ~7 days after the call against its stage at the call, shown as a chip beside the call — for Bitrix24 from the deal's stage history, for Uspacy (its portal keeps no stage history) from the stages Aelo itself saw the deal in on its scheduled sync (kept for 90 days after the deal was last seen there), once the installation has accepted processing instruction 1.19 or later — an installation still on an earlier one is read by the deal's stage right after the window ends, and only then — so a Uspacy call whose 7-day window ends at a time Aelo had not seen the deal at (e.g. before the feature was switched on, or in a gap between two syncs) gets no verdict. Deal-only in v1; a no-op for orgs without a Bitrix24 or Uspacy connection. Opt-in per org (flag default off). The stage badge is hidden on the Bitrix24 embedded surface; re-analysis rehydrates the stored at-call snapshot rather than re-capturing, so a record's stage always reflects the moment of the call.",
    area: 'core_pipeline',
    status: 'available',
    customerVisible: true,
    surfaces: ['saas_app', 'edge_api', 'workflows_api'],
    // not_included everywhere: tier defaults derive from packages and would
    // silently re-enable the flag in every tier if this said 'included'
    packages: unavailableEverywhere(),
    flag: {
      key: 'stageAwareScoring',
      defaultValue: false,
      isImplemented: true,
      category: 'beta',
      note: 'Default-off opt-in (beta), NOT a known-risk hold. The B-3 wire risk this note used to carry is closed: on 2026-07-16 both methods were verified against a live portal (b24.auspex.cloud). crm.status.list returns SEMANTICS exactly as the mapping assumes — DEAL_STAGE WON=S, LOSE=F, every intermediate stage null (no other value appears in the whole 341-row catalog). crm.stagehistory.list returns result:{items:[...]} with ID/OWNER_ID/TYPE_ID/STAGE_ID/CATEGORY_ID/CREATED_TIME/STAGE_SEMANTIC_ID; entityTypeId=2, filter[OWNER_ID] and next/total pagination all behave as coded. Remaining default-off rationale is ordinary beta caution: stage capture writes per-record snapshots and drives scorecard selection, so enable per-org deliberately. isImplemented stays true so an explicit override is NOT force-hidden by enforceUnimplemented.',
    },
    docs: [
      {
        label: 'User guide - evaluation criteria (stage-based scorecard selection)',
        path: 'apps/website/src/content/docs/en/docs/user-guide/evaluation-criteria.md',
      },
      {
        label: 'DPA - data categories (deal-stage data)',
        path: 'apps/website/src/pages/[lang]/legal/dpa.astro',
      },
      {
        label: 'Privacy Policy - data we process on behalf of customers',
        path: 'apps/website/src/pages/[lang]/legal/privacy.astro',
      },
    ],
    code: [
      {
        label: 'Stage capture pipeline step',
        path: 'apps/workflows-api/src/workflows/record-pipeline/steps/stage-capture-step.ts',
      },
      {
        label: 'Record pipeline workflow (step wiring)',
        path: 'apps/workflows-api/src/workflows/record-pipeline/workflow.ts',
      },
      {
        label: 'Bitrix24 deal stage read-side',
        path: 'packages/connectors/src/crm/field-service/bitrix24-stage.ts',
      },
      {
        label: 'AI stage-phase classifier (platform key, cached per org)',
        path: 'apps/workflows-api/src/workers/stage-phases/classify.ts',
      },
      databaseRepository(
        'AI stage-phase guess cache repository',
        'CrmStagePhaseGuessRepository.ts'
      ),
      {
        label: 'D1 records stage snapshot columns',
        path: 'packages/database/src/schema/d1/state.ts',
      },
      {
        label: 'D1 stage scorecard bindings schema',
        path: 'packages/database/src/schema/d1/configuration.ts',
      },
      databaseRepository(
        'Stage scorecard binding repository',
        'StageScorecardBindingRepository.ts'
      ),
      uiDomainClient('Stage bindings domain client', 'stage-bindings.api.ts'),
      {
        label: 'Deal-outcome + stage-progression sync cron',
        path: 'apps/workflows-api/src/scheduled/outcome-sync.ts',
      },
      databaseRepository(
        'Call series outcome repository',
        'CallSeriesOutcomeRepository.ts'
      ),
    ],
    notes: [
      'v1 captures deal stage only (channel===call && crmEntityType===deal && a resolved Bitrix24 or Uspacy connection — for Uspacy, only while its processing instruction is active); leads are a deliberate follow-up — a lead has a different lifecycle, and conversion to a deal is the natural trigger point (WS7 T-15), not covered here.',
      'Capture failures (Bitrix API error, no resolvable stage) are non-fatal: crm_stage_source is written as "unknown" and the pipeline continues without retrying the (expensive) analyze step. Any gate miss (flag off, wrong channel/entity, no CRM connection) writes nothing at all — records_metadata stage columns stay null, same as before this feature existed.',
      "Resolver consumption of the captured stage for scorecard selection (B-5), authoring CRUD routes (B-6), and the UI badge/tab (B-7) have all shipped on this branch alongside capture (B-3/B-4). This entry's `code` list was extended with the UI domain client (stage-bindings.api.ts) to satisfy the catalog-completeness test, but does not yet enumerate every B-5/B-6/B-7 file individually — see those commits directly, or the whole-branch Phase B review, for a fuller code inventory. Phase C (outcome sync, funnel, progression chip) is represented by the sync cron and the outcome repository rather than an exhaustive file list, on the same convention.",
      "Stage phase (#1206): Bitrix24 marks only won (S) and lost (F) stages, so every intermediate stage used to capture no phase at all (prod 2026-09-29: 21 of 20,577 staged calls had one). Capture now resolves the phase in one order — the CRM marker, then the customer mapping (crm_stage_semantic_map, set on the Deal Stages tab via PUT /api/v1/crm/stage-semantics — a change re-phases the calls already recorded at the stage, D1 and the Postgres mirror, only where the origin is customer or name), then the phase an AI model guesses from the org's pipeline and stage names (`classifyStagePhases`, workflows-api; the word dictionary is gone — customers rename the default stages) — and records where it came from in crm_stage_semantic_origin (crm | customer | name). Only the pipeline and stage names (no customer or conversation data) go to OpenAI, on the platform key, once per org until a stage is added or renamed; the answer is cached in crm_stage_phase_guesses by a hash of the stage name (capture checks it with the name it already read, so a fresh row costs no catalog read); only for an org with stageAwareScoring on, stages the customer mapped are not sent, one classification per org at a time (D1 lease), GET /crm/stages reads the cache and only the Deal Stages tab (?classify=1, for whoever may change stage phases) asks for a classification; clearing a mapping takes the cached guess or, without one, leaves the stage's calls without a phase and asks in the background. A scheduled catch-up (workflows-api, every 15 min, cache only) gives the cached guess to calls captured without a phase and to name-guessed calls the guess contradicts (docs/plans/2026-10-07-ai-stage-phases.md).",
      'Historical records carry no stage snapshot and never gain one: capture runs at analysis time and re-analysis deliberately rehydrates the stored at-call value instead of re-capturing. Stage-aware scoring and the stage-derived dashboards therefore describe the flag-on cohort only — an org with a long archive sees them apply to a small slice of its history until the B-9 historical backfill (crm.stagehistory.list → stage at call time, crm_stage_source="stagehistory") runs. Measured on the first production org (2026-07-17, ~19h after enablement): 192 of 57,468 deal-linked records had a snapshot.',
    ],
  },
  {
    id: 'records.conversation_workspace',
    title: 'Conversation Records Workspace',
    summary:
      'Record list, record detail, audio access, transcript access, uploads, and per-record metadata views.',
    area: 'records',
    status: 'available',
    customerVisible: true,
    surfaces: ['saas_app', 'bitrix24_embedded', 'edge_api', 'workflows_api'],
    packages: includedEverywhere(),
    code: [
      { label: 'Edge routes', path: 'apps/edge-api/src/routes/records/index.ts' },
      uiDomainClient('Records domain client', 'records.api.ts'),
      { label: 'UI route', path: 'apps/ui-worker/src/routes/records.tsx' },
      { label: 'D1 state schema', path: 'packages/database/src/schema/d1/state.ts' },
      databaseRepository('Record repository', 'RecordRepository.ts'),
      databaseRepository('Transcript repository', 'TranscriptRepository.ts'),
      databaseRepository('Record metadata repository', 'RecordMetadataRepository.ts'),
    ],
  },
  {
    id: 'records.reprocessing_controls',
    title: 'Record Reprocessing Controls',
    summary:
      'Cost estimation, re-analysis, media retry, and workflow-triggered record processing controls.',
    area: 'records',
    status: 'available',
    customerVisible: true,
    surfaces: ['edge_api', 'workflows_api', 'saas_app'],
    packages: includedEverywhere(),
    code: [
      {
        label: 'Processing routes',
        path: 'apps/edge-api/src/routes/records/processing.ts',
      },
      uiDomainClient('Records domain client', 'records.api.ts'),
      {
        label: 'Record pipeline workflow',
        path: 'apps/workflows-api/src/workflows/record-pipeline/workflow.ts',
      },
      databaseRepository('Record repository', 'RecordRepository.ts'),
    ],
  },
  {
    id: 'records.explicit_processing_start',
    title: 'Automatic and Manual Analysis Modes',
    summary:
      "An organization chooses between two modes. In automatic mode every new record is transcribed and analyzed on arrival. In manual mode records are accepted and listed but wait in `awaiting_start` without spending minutes, and an organization owner or admin starts the ones they want — a single record from its card, a selection from the records list, or the accumulated backlog, each with its price shown before the charge; every manual start — a single record, a selection or the backlog batch — re-prices the set on the server and does not start if the price has risen since it was shown (for example the included minutes ran out while the confirmation was open), or if the set now holds more records or more calls with no known duration than were priced, returning the new price instead; the backlog run starts exactly the batch that was shown, so records that arrive after the confirmation opened wait for the next run; if the price cannot be computed (billing unavailable) nothing starts and the reason is named; an organization billed under a contract (postpaid) sees the amount as charged under its contract rather than beyond its plan — one run takes at most 200 records and reports what remains, so a larger backlog is cleared by repeating the run. A manual start overrides both the organization-level gate and a project whose automatic analysis is switched off. New organizations begin in manual mode. CRM bulk import is available only in automatic mode; it does not queue records for a later manual start, so manual-mode organizations must switch to automatic before running one. The mode can be switched from the records-list banner or from organization settings. A paying organization that exhausts its included minutes AND its wallet balance is switched to manual mode by the system itself, with the reason and the moment recorded so the banner can tell an imposed pause from a chosen one; whichever records had already cleared the organization gate and were mid-pipeline when funds ran out land in `awaiting_funds` — there can be more than one at once, since the balance check applies per record passing through the gate, not just to a single record — while every record arriving after that — the pause's main, ongoing outcome — is stopped by the organization gate itself and lands in `awaiting_start` instead. Both start the exact same way as any other pending record — individually, by selection, or through a backlog run under the same 200-record limit. A record that ran out of funds only after it had already been transcribed keeps that transcript: starting it resumes from there instead of transcribing again, and because its minutes were already charged at transcription, it is quoted and charged nothing on that start. If that charge never went through and the call's duration is known, the call is quoted and charged once on that start — by the duration of the conversation, or by the length of the recording when the conversation was not measured — and never again. When neither length is known, the start does not analyse the saved transcript: the call waits for its recording, like a call that arrived without one. Records that were already sitting in `awaiting_funds` from before this mode existed are startable the same way too — nothing already stalled there needs a separate fix. Topping up the balance does not clear the pause reason or switch the mode back to automatic by itself — only an explicit mode change from an organization owner or admin does either (turning automatic mode back on, or resubmitting manual), and until then processing stays manual with the pause still shown as the reason. What the reason SAYS does change once the organization can pay again — whether it topped up, was given bonus minutes, or simply reached a new billing cycle: the banner and the settings screen stop stating a shortage in the present tense and instead explain that the mode was taken automatically and that turning it back on is now the organization owner's call. Organization settings show that explanation too, not only the records-list banner: an owner who opens settings directly would otherwise see a manual mode they never chose, with no hint why. A free-tier organization is never switched to manual mode for this: its included-minute grant resets with the calendar month, so running it out is a normal monthly event rather than an imposed pause, and processing resumes on its own once the new cycle starts.",
    area: 'records',
    status: 'available',
    customerVisible: true,
    surfaces: ['saas_app', 'bitrix24_embedded', 'edge_api', 'workflows_api'],
    packages: includedEverywhere(),
    code: [
      { label: 'Mode flag', path: 'packages/core/src/processing-gate.ts' },
      {
        label: 'Pipeline gate + manual override',
        path: 'apps/workflows-api/src/workflows/record-pipeline/workflow.ts',
      },
      {
        label: 'Autopause on exhausted minutes + balance',
        path: 'apps/workflows-api/src/workflows/record-pipeline/budget/minute-events.ts',
      },
      {
        label: 'CRM bulk import gate',
        path: 'apps/workflows-api/src/workflows/crm-import.ts',
      },
      { label: 'Record start executor', path: 'apps/edge-api/src/utils/record-start.ts' },
      {
        label: 'Manual start + price quote routes',
        path: 'apps/edge-api/src/routes/records/start.ts',
      },
      {
        label: 'Backlog summary + run-all route',
        path: 'apps/edge-api/src/routes/records/backlog.ts',
      },
      { label: 'Mode toggle API', path: 'apps/edge-api/src/routes/settings.ts' },
      {
        label: 'Mode banner (records list)',
        path: 'apps/ui-worker/src/features/processing/ProcessingToggleBanner.tsx',
      },
      {
        label: 'Selection start with price (records page)',
        path: 'apps/ui-worker/src/routes/records/RecordsPage.tsx',
      },
      {
        label: 'Selection start with price (bulk actions hook)',
        path: 'apps/ui-worker/src/hooks/records/useBulkRecordActions.ts',
      },
      {
        label: 'Quoted start dialog (re-opens with the new price)',
        path: 'apps/ui-worker/src/features/processing/useQuotedStart.ts',
      },
      {
        label: 'Price ceiling for every manual start',
        path: 'apps/edge-api/src/utils/accepted-price.ts',
      },
      {
        label: 'Mode toggle (organization settings)',
        path: 'apps/ui-worker/src/routes/settings/components/AnalysisModeSection.tsx',
      },
      {
        label: 'Record card start button',
        path: 'apps/ui-worker/src/components/records/RecordHaltedNotice.tsx',
      },
    ],
  },
  {
    id: 'analyses.analysis_workspace',
    title: 'Analysis Workspace',
    summary:
      'Analysis detail APIs and UI surfaced inside the Conversations screen: per-record analysis panel, quality metric columns and QA/sentiment filters in the records table, and re-analysis trigger. The standalone Analyses screen merged into Conversations (2026-07); /analyses redirects to /records.',
    area: 'analytics',
    status: 'available',
    customerVisible: true,
    surfaces: ['saas_app', 'bitrix24_embedded', 'edge_api', 'workflows_api'],
    packages: includedEverywhere(),
    docs: [
      {
        label: 'User guide - evaluation criteria (chat significance threshold)',
        path: 'apps/website/src/content/docs/en/docs/user-guide/evaluation-criteria.md',
      },
      {
        label: 'User guide - FAQ (short-conversation threshold)',
        path: 'apps/website/src/content/docs/en/docs/user-guide/faq.md',
      },
    ],
    code: [
      { label: 'Analysis routes', path: 'apps/edge-api/src/routes/analyses/index.ts' },
      uiDomainClient('Analyses domain client', 'analyses.api.ts'),
      { label: 'UI route', path: 'apps/ui-worker/src/routes/records.tsx' },
      {
        label: 'Analyze worker',
        path: 'apps/workflows-api/src/workers/analyze/worker.ts',
      },
      {
        label: 'Chat significance gate (isShortCall for chats)',
        path: 'apps/workflows-api/src/workflows/record-pipeline/steps/chat-significance.ts',
      },
      {
        label: 'Chat analysis size limit (MAX_ANALYZED_CHAT_BYTES)',
        path: 'apps/workflows-api/src/workflows/record-pipeline/steps/chat-analysis-limit.ts',
      },
      databaseRepository('Analysis repository', 'AnalysisRepository.ts'),
      databaseRepository('Analysis metadata repository', 'AnalysisMetadataRepository.ts'),
    ],
    notes: [
      "A chat with no operator reply, or with fewer real messages than the project's configured threshold (Min Chat Messages, default 4 — system/service messages don't count), is not analyzed. Same treatment as a short call: the transcript is still kept, but scoring, CRM delivery, series membership and the per-minute charge are skipped.",
      'A chat with more than 100 KB of text (UTF-8, MAX_ANALYZED_CHAT_BYTES) from any source is stored and visible in Records but not analyzed and not charged; the record closes as not_applicable with the reason chat_too_long. Stored chat text is capped at 1.9 MB (MAX_STORED_CHAT_TEXT_BYTES): upload and the public API answer 413 above it.',
    ],
  },
  {
    id: 'analyses.voice_metrics',
    title: 'Acoustic Voice Metrics',
    summary:
      'Objective voice-delivery signals for calls — speaking rate (wpm), pauses, pitch variation, and voice stability — computed by the acoustic service from the call audio and surfaced on the record analysis panel.',
    area: 'analytics',
    status: 'available',
    customerVisible: true,
    surfaces: ['workflows_api', 'edge_api', 'saas_app', 'bitrix24_embedded'],
    packages: includedEverywhere(),
    docs: [
      {
        label: 'Bitrix24 user guide',
        path: 'apps/website/src/content/docs/en/docs/user-guide/bitrix24.md',
      },
    ],
    code: [
      {
        label: 'Acoustic service client',
        path: 'apps/workflows-api/src/workers/analyze/acoustic.ts',
      },
      {
        label: 'Analyze worker (persists voiceMetrics + prosody)',
        path: 'apps/workflows-api/src/workers/analyze/worker.ts',
      },
      {
        label: 'Voice metrics card',
        path: 'apps/ui-worker/src/components/details/v2/analysis/VoiceMetricsSection.tsx',
      },
    ],
    notes: [
      "Call-only: requires call audio (channel === 'call' && audioUrl). Chats and audio-less records carry no voice metrics; the card simply does not render.",
      'Acoustic prosody numbers (f0, jitter, shimmer, HNR, speaking/articulation rates) are persisted raw in result_json.prosody for future analytics; the UI shows only the summarized voiceMetrics block.',
      'NO emotion inference is ever derived, stored, or displayed — the acoustic service has emotion recognition disabled — no emotion key in the response, and EMOTION_CLASSIFIER_ENABLED=true stops the service instead of enabling it (#2607) — and the acoustic client would drop the field at the wire boundary anyway (EU AI Act Art. 5(1)(f)). The block reports acoustic-signal facts, not inferred emotional state.',
    ],
  },
  {
    id: 'analyses.annotations_reviews',
    title: 'Analysis Annotations And Reviews',
    summary:
      'Human annotations and review status/feedback attached to individual analysis results.',
    area: 'review',
    status: 'available',
    customerVisible: true,
    surfaces: ['edge_api', 'saas_app'],
    packages: includedEverywhere(),
    code: [
      {
        label: 'Annotation routes',
        path: 'apps/edge-api/src/routes/analyses/annotations.ts',
      },
      { label: 'Review routes', path: 'apps/edge-api/src/routes/analyses/reviews.ts' },
      uiDomainClient('Analyses domain client', 'analyses.api.ts'),
      { label: 'Review tables', path: 'packages/database/src/schema/d1/state.ts' },
      databaseRepository(
        'Analysis annotation repository',
        'AnalysisAnnotationRepository.ts'
      ),
      databaseRepository('Analysis review repository', 'AnalysisReviewRepository.ts'),
    ],
  },
  {
    id: 'review.human_review_queue',
    title: 'Human Review Queue',
    summary:
      'Durable queue for CRM automation blocked by analysis verification, with audited approve/reject decisions.',
    area: 'review',
    status: 'beta',
    customerVisible: true,
    surfaces: ['saas_app', 'edge_api', 'workflows_api'],
    // Explicit tenant opt-in only. No package enables the beta implicitly.
    packages: unavailableEverywhere(),
    flag: {
      key: 'enableHumanReview',
      defaultValue: false,
      isImplemented: true,
      category: 'beta',
      note: 'Opt-in beta. The same explicit org flag gates the blocked-delivery writer, review API and UI. Approval enqueues a durable workflow; it never calls the CRM inline.',
    },
    docs: [
      {
        label: 'Analysis quality fixes',
        path: 'docs/plans/2026-08-12-analysis-quality-fixes.md',
      },
    ],
    code: [
      { label: 'Human review API', path: 'apps/edge-api/src/routes/human-review.ts' },
      uiDomainClient('Human review domain client', 'human-review.api.ts'),
      {
        label: 'Review queue UI',
        path: 'apps/ui-worker/src/routes/-review-queue.lazy.tsx',
      },
      {
        label: 'Review delivery workflow',
        path: 'apps/workflows-api/src/workflows/review-delivery-workflow.ts',
      },
      databaseRepository('Human review repository', 'HumanReviewRepository.ts'),
    ],
  },
  {
    id: 'sources.direct_telephony',
    title: 'Direct Telephony Sources',
    summary:
      'Direct source setup and ingestion for Streamtelecom and Binotel; Unitalk and Ringostat are in beta.',
    area: 'integrations',
    status: 'available',
    customerVisible: true,
    surfaces: ['edge_api', 'workflows_api', 'saas_app'],
    packages: includedEverywhere(),
    docs: [
      {
        label: 'Multi-source architecture',
        path: 'docs/architecture/MULTI_SOURCE_AND_VENDOR_MODEL.md',
      },
      {
        label: 'Current implementation plan',
        path: 'docs/plans/2026-06-04-telephony-connectors.md',
      },
    ],
    code: [
      databaseRepository(
        'Telephony agent directory repository',
        'TelephonyAgentDirectoryRepository.ts'
      ),
    ],
  },
  {
    id: 'sources.data_source_management',
    title: 'Data Source Management',
    summary:
      'Settings wizard and source-management API for available sources, active source, setup, connection test, and disconnect flows.',
    area: 'integrations',
    status: 'available',
    customerVisible: true,
    surfaces: ['saas_app', 'edge_api'],
    packages: includedEverywhere(),
    docs: [{ label: 'User guide', path: 'docs/guides/CRM_DATA_LOADER_USER_GUIDE.md' }],
    code: [
      { label: 'Source API', path: 'apps/edge-api/src/routes/sources/index.ts' },
      uiDomainClient('Settings domain client', 'settings.api.ts'),
      {
        label: 'Source wizard',
        path: 'apps/ui-worker/src/features/source-wizard/SourceWizard.tsx',
      },
      {
        label: 'Settings section',
        path: 'apps/ui-worker/src/routes/settings/components/DataSourcesSection.tsx',
      },
    ],
  },
  {
    id: 'sources.reconnect',
    title: 'CRM Source Reconnect',
    summary:
      'One-click rebind of a disconnected Bitrix24 or Uspacy source that is still installed in the portal, restoring the binding without a marketplace reinstall.',
    area: 'integrations',
    status: 'available',
    customerVisible: true,
    surfaces: ['saas_app', 'edge_api'],
    packages: includedEverywhere(),
    code: [
      { label: 'Reconnect route', path: 'apps/edge-api/src/routes/sources/index.ts' },
      uiDomainClient('Settings domain client', 'settings.api.ts'),
      {
        label: 'Connection settings UI',
        path: 'apps/ui-worker/src/routes/settings/components/ConnectionTab.tsx',
      },
      databaseRepository('Embedded install repository', 'EmbeddedInstallRepository.ts'),
    ],
    notes: [
      'Disconnect leaves the app installed and the OAuth material in KV, so the way back is a rebind — a marketplace reinstall is not even available when the customer never removed the app.',
      'Bitrix24 and Uspacy only; any other CRM vendor answers 501 rather than being handed to the wrong rebinder.',
      'A Uspacy portal whose processing was paused by the disconnect cannot be reactivated from this button: only the Customer client/admin may reauthorize from Aelo settings against the current legal documents (POST /organizations/:orgId/uspacy/processing). See .claude/rules — the Uspacy organization-processing boundary.',
    ],
  },
  {
    id: 'sources.hybrid_media_source',
    title: 'Hybrid Media Source',
    summary:
      'When a CRM is the record source but its call activity carries no recording file — only a text reference into the recording archive of the telephony operator — a telephony installation can be connected in an audio-only (media_only) role to supply it. A call id read directly out of the CRM activity is tried first; if none resolves, a telephony call index is matched by phone number, call time and duration. The index is fed by the live webhook for every call the operator reports as finished, and by a historical CDR pull for whatever the webhook never saw. An outcome of "nobody picked up" or "line busy" is what lets Aelo stop waiting and close the record with a named reason; so does a match that stays ambiguous after narrowing. Any other unanswered outcome cannot be told apart from one Aelo does not recognize yet, so the recording is still requested until the 24-hour ceiling.',
    area: 'integrations',
    status: 'partial',
    customerVisible: true,
    surfaces: ['edge_api', 'workflows_api', 'saas_app', 'bitrix24_embedded'],
    packages: includedEverywhere(),
    docs: [
      {
        label: 'Design spec',
        path: 'docs/superpowers/specs/2026-08-21-hybrid-media-source-design.md',
      },
      {
        label: 'Telephony integration guide',
        path: 'apps/website/src/content/docs/en/docs/integrations/telephony.md',
      },
    ],
    code: [
      {
        label: 'External audio resolver',
        path: 'apps/workflows-api/src/workflows/record-pipeline/helpers/external-audio.ts',
      },
      {
        label: 'Wait-for-media give-up rule',
        path: 'apps/workflows-api/src/workflows/record-pipeline/steps/wait-for-media-step.ts',
      },
      {
        label: 'Telephony installation role',
        path: 'packages/connectors/src/telephony/config.ts',
      },
      {
        label: 'Phone number comparison',
        path: 'packages/connectors/src/telephony/phone-number.ts',
      },
      {
        label: 'Direct call-id extractor registry',
        path: 'packages/connectors/src/correlation/registry.ts',
      },
      {
        label: 'Binotel × Bitrix24 call-id extractor',
        path: 'packages/connectors/src/correlation/extractors/binotel-bitrix24.ts',
      },
      databaseRepository(
        'Telephony call index repository',
        'TelephonyCallIndexRepository.ts'
      ),
      {
        label: 'media_only webhook intake',
        path: 'apps/workflows-api/src/queue-handlers/intake/enrich-telephony.ts',
      },
      {
        label: 'Call index CDR prefill',
        path: 'apps/workflows-api/src/queue-handlers/telephony-index-prefill.ts',
      },
      {
        label: 'Durable call-index prefill workflow',
        path: 'apps/workflows-api/src/workflows/telephony-index-prefill-workflow.ts',
      },
      {
        label: 'Source setup role field',
        path: 'apps/edge-api/src/routes/sources/index.ts',
      },
      {
        label: 'Vendor audio-source capability (both halves of the pair)',
        path: 'packages/core/src/integrations.ts',
      },
      {
        label: 'Audio-source card and role gate',
        path: 'apps/ui-worker/src/routes/settings/components/ConnectionTab.tsx',
      },
      {
        label: 'Role choice in the connection wizard',
        path: 'apps/ui-worker/src/features/source-wizard/SourceWizard.tsx',
      },
      {
        label: 'Halted-record reason display',
        path: 'apps/ui-worker/src/components/records/RecordHaltedNotice.tsx',
      },
    ],
    notes: [
      'Only Binotel can serve as the audio-only source today: its adapter is the only one that can mint a recording URL for a call id after the fact (capabilities.externalAudioSource in integrations.ts). Stream Telecom, Unitalk and Ringostat only ever hand their recording inside their own webhook and cannot mint one for a call they did not receive directly.',
      'The role is chosen in the connection wizard, on the credentials step, and only where it means something: the vendor must be able to mint a recording URL by call id (capabilities.externalAudioSource), and the record source must be one Aelo fetches recordings FROM (crm.mediaProvider — Bitrix24 today). Neither half is optional: a media_only install attached to a public-API, direct-telephony or link-carrying-CRM record source can never serve a single record, because the external-audio resolver is only reached from inside the branch that has already built a CRM media provider. Both halves are enforced at POST /sources/setup too, not only in the wizard — the route has callers the screen does not. The API check counts installs INCLUDING deactivated ones on purpose: a disconnected or reauth-required CRM is a temporary state, and refusing there would block a telephony credential rotation at exactly the moment the customer is repairing the neighbouring failure. An audio-only installation skips the bulk-import step — it imports no records — and appears in Settings → Data Sources → Connection as its own card, badged as the additional source, with its webhook URL, its own Change and its own Disconnect. With a CRM connected and no audio source yet, the CRM card offers «Add telephony as an audio source»: the wizard opens with the role fixed and lists only vendors that can mint a recording and are not bringing records already (#1099). A vendor that is bringing records is never switched to media_only in place — POST /sources/setup answers 409 record_source_conflict, checked against the row inside the write itself — so the Connection tab lists every record source beside the CRM with its own Disconnect.',
      "Every telephony installation not authenticated by sender address carries a webhook secret minted at setup (#1403): Unitalk and Ringostat send it in the `Authorization` header (`Bearer <secret>`), Stream Telecom — whose support registers a URL and which sends no custom headers — carries it as `?token=` in that URL. The installation id in the webhook path is an identifier, not the key: verify() denies by default, so a call without the secret, or with a wrong one, is dropped, and the per-installation rate limit is charged only after verification, while a per-(installation, sender) limit bounds the decrypt work before it. Binotel stays authenticated by its sender-IP allowlist and has no secret. The secret survives a re-setup and is recoverable afterwards: its second copy lives at the vendor, so regenerating our half rejected every call until the customer pasted the new one, and the two reasons to call /setup again (rotate the API key, reconnect) both did it silently. It is served by GET /sources/:vendor/webhook-secret on demand for all three vendors — never in GET /sources/active, which every org member can read — gated by checkPermission(organization.update) and answered with Cache-Control: no-store; the wizard shows Stream Telecom's address only with the token in it. The auth mode is declared once, in the integrations catalog (capabilities.webhookAuth), and enforced centrally before the vendor's own checks. Our own log channels drop the query string; Cloudflare's platform observability (Workers Logs request.url, traces url.full/url.query) keeps it for sampled calls — the trust boundary of a URL secret, accepted by the owner on 2026-10-03 (spec §4.3); making Stream Telecom's sender-IP allowlist mandatory once the vendor names its addresses is tracked in #1534. Deliberate rotation now has no path; adding one (with a dual-acceptance window) is tracked in #1487.",
      'Roles do not collide: one active telephony install PER ROLE, not per organization. Connecting an audio-only source leaves an ingest telephony serving, and vice versa (POST /sources/setup scopes its deactivation loop by parseTelephonyRole). Rotating credentials keeps the role: the API preserves it when no role is sent, and the wizard opens the choice on the current role of the installation so re-running it on an existing audio source does not silently promote it to a record source.',
      'The phone-bearing call index is capped by the shorter of 30 days and the organization’s effective transcript retention, is purged with the organization, and its historical CDR prefill runs as a durable Workflow.',
      'Bitrix24 is the record source, and not merely the verified one: createCrmMediaProvider builds a provider for CRMs whose catalog entry says crm.mediaProvider, that is Bitrix24 alone, and the external-audio resolver runs only inside the branch that provider feeds. This is why acceptsExternalAudioSource gates the wizard on the same catalog fact rather than on "some source is connected".',
    ],
  },
  {
    id: 'compliance.data_retention_window',
    title: 'Customer-Configurable Data Retention',
    summary:
      'Organization owner may shorten the retention window to any value from 1 to 365 days, previewed against what it would delete — records, coaching notes, coaching sessions, AI assistant questions and deal states — before it takes effect; absent means the platform level applies.',
    area: 'compliance',
    status: 'available',
    customerVisible: true,
    surfaces: ['saas_app', 'edge_api', 'workflows_api'],
    packages: includedEverywhere(),
    docs: [
      {
        label: 'Customer-configurable retention spec',
        path: 'docs/plans/2026-08-11-customer-configurable-retention.md',
      },
      { label: 'Security policy', path: 'SECURITY.md' },
    ],
    code: [
      {
        label: 'Retention bounds and resolution',
        path: 'packages/core/src/utils/retention-settings.ts',
      },
      {
        label: 'Retention override + impact preview API',
        path: 'apps/edge-api/src/routes/settings.ts',
      },
      uiDomainClient('Settings domain client', 'settings.api.ts'),
      {
        label: 'Retention settings UI',
        path: 'apps/ui-worker/src/routes/settings/components/DataRetentionSection.tsx',
      },
      {
        label: 'Retention cleanup cron',
        path: 'apps/workflows-api/src/scheduled/index.ts',
      },
    ],
    notes: [
      'The ceiling equals PLATFORM_RETENTION_DAYS (365) on purpose: a Controller may shorten the window but never hold data longer than the year every customer-facing surface promises.',
      "Unlimited (0) is deliberately unavailable — switching deletion off entirely is not the Controller's to choose.",
      'Out-of-range values are REJECTED at the API and IGNORED at resolution time, never clamped into effect (2026-07-18 incident: a stale low value deleted ~42k records).',
      'Organization owner only: the same role that signs the DPA, not any admin.',
    ],
  },
  {
    id: 'integrations.bitrix24',
    title: 'Bitrix24 Integration',
    summary:
      "Embedded Bitrix24 app, an Aelo tab in the deal, lead, contact and company cards, CRM data sync, and delivery paths back into Bitrix24. The tab's «Refresh» button rereads the card's conversations and, in a deal or lead card where the deal state is available, the deal state, without reloading the card.",
    area: 'integrations',
    status: 'available',
    customerVisible: true,
    surfaces: ['bitrix24_embedded', 'edge_api', 'workflows_api', 'saas_app'],
    packages: includedEverywhere(),
    // No feature flag: B24 availability is governed by embedded_installs rows,
    // not a toggle. Package availability above reflects the real entitlement.
    docs: [
      {
        label: 'Multi-source architecture',
        path: 'docs/architecture/MULTI_SOURCE_AND_VENDOR_MODEL.md',
      },
      {
        label: 'Aelo tab in CRM cards spec',
        path: 'docs/plans/2026-10-17-bitrix24-deal-tab.md',
      },
      {
        label: 'Aelo tab in CRM cards plan',
        path: 'docs/plans/2026-10-17-bitrix24-deal-tab-plan.md',
      },
    ],
    code: [
      {
        // Клиента `embedded.api.ts` больше нет: он не вызывался ни разу и
        // удалён (ecef6e676). Embedded-режим ходит прямым fetch через этот
        // хук — он и есть точка, где живёт его авторизация.
        label: 'Embedded auth fetch',
        path: 'apps/ui-worker/src/hooks/useEmbeddedAuthFetch.ts',
      },
      databaseRepository('Embedded install repository', 'EmbeddedInstallRepository.ts'),
      {
        label: 'CRM card tab entry (placement parsing)',
        path: 'apps/edge-api/src/routes/bitrix24/helpers/crm-tab-placement.ts',
      },
      {
        label: 'CRM card tab screen',
        path: 'apps/ui-worker/src/routes/embedded/crm-tab/-CrmTabScreen.tsx',
      },
    ],
  },
  {
    id: 'integrations.bitrix24_smart_process',
    title: 'Bitrix24 Smart Process Setup',
    summary:
      'Embedded and organization-level Smart Process setup for storing and delivering analysis results in Bitrix24.',
    area: 'integrations',
    status: 'available',
    customerVisible: true,
    surfaces: ['bitrix24_embedded', 'edge_api', 'saas_app'],
    packages: includedEverywhere(),
    code: [
      {
        label: 'Embedded setup API',
        path: 'apps/edge-api/src/routes/embedded/smart-process.ts',
      },
      {
        // Клиента `embedded.api.ts` больше нет: он не вызывался ни разу и
        // удалён (ecef6e676). Embedded-режим ходит прямым fetch через этот
        // хук — он и есть точка, где живёт его авторизация.
        label: 'Embedded auth fetch',
        path: 'apps/ui-worker/src/hooks/useEmbeddedAuthFetch.ts',
      },
      uiDomainClient('Settings domain client', 'settings.api.ts'),
      {
        label: 'Organization setup API',
        path: 'apps/edge-api/src/routes/organizations/smart-process.ts',
      },
    ],
  },
  {
    id: 'integrations.uspacy',
    title: 'Uspacy Integration',
    summary:
      "Uspacy Calls Beta: the portal is connected by installing Aelo from the Uspacy Marketplace, calls arrive through the signed placement flow with their recording link, and the finished analysis comes back into Uspacy as a call note plus one comment on the linked entity. Aelo also reads the portal's deal funnels with their stages and a deal's current stage, so the Deal Stages tab, the sales methodology of each funnel, the deal state, stage capture and, with stage-based evaluation, deal outcomes and the per-call progression verdict work for Uspacy deals; for the deal state it also reads a deal's linked leads, to continue the state of the lead the deal was converted from. The app's page in Uspacy shows the connection status and, once the portal has an Aelo organization, a menu of large buttons — Records, Overview, Deals, Analysis Setup, AI assistant, Settings, Billing — that open those sections of the Aelo web app in a new tab, for that organization, behind the ordinary Aelo sign-in; Uspacy has no embedded Aelo app and no Aelo tab in its cards yet.",
    area: 'integrations',
    status: 'beta',
    customerVisible: true,
    surfaces: ['edge_api', 'workflows_api', 'saas_app'],
    // No tier gate exists in code: the beta is reached by installing from the
    // Uspacy Marketplace, and USPACY_CALLS_ENABLED is a platform kill switch
    // (true in every environment), not a per-tenant entitlement. Saying
    // anything narrower here would publish a restriction the product does not
    // enforce.
    packages: includedEverywhere(),
    docs: [
      {
        label: 'Uspacy connector plan',
        path: 'docs/plans/2026-07-30-uspacy-connector.md',
      },
      {
        label: 'Uspacy Terms',
        path: 'apps/website/src/pages/[lang]/legal/uspacy-terms.astro',
      },
      {
        label: 'Uspacy Privacy Notice',
        path: 'apps/website/src/pages/[lang]/legal/uspacy-privacy.astro',
      },
      {
        label: 'Uspacy processing instruction',
        path: 'apps/website/src/pages/[lang]/legal/uspacy-processing-instruction.astro',
      },
      {
        label: 'Sub-processors register',
        path: 'apps/website/src/pages/[lang]/legal/sub-processors.astro',
      },
    ],
    code: [
      {
        label: 'Uspacy install, provisioning and subscription routes',
        path: 'apps/edge-api/src/routes/uspacy/index.ts',
      },
      {
        label: 'Organization-level Uspacy settings',
        path: 'apps/edge-api/src/routes/organizations/uspacy.ts',
      },
      {
        label: 'Uspacy CRM provider',
        path: 'packages/connectors/src/crm/uspacy-provider.ts',
      },
      {
        label: 'Uspacy token manager',
        path: 'packages/connectors/src/crm/uspacy-token-manager.ts',
      },
      {
        label: 'Marketplace micro-frontend',
        path: 'apps/uspacy-extension/src/index.ts',
      },
      {
        label: 'Uspacy deal funnels and stages reader',
        path: 'packages/connectors/src/crm/uspacy/funnels.ts',
      },
      {
        label: 'Uspacy field service (funnels, deal stage, deal outcome)',
        path: 'packages/connectors/src/crm/field-service/uspacy.ts',
      },
      databaseRepository(
        'Integration processing repository',
        'IntegrationProcessingRepository.ts'
      ),
    ],
    notes: [
      "Beta scope: calls, plus the deal funnels (#1603): the portal's deal funnels with their stages and the deal's current stage (`kanban_stage_id`) are read for the Deal Stages tab, the sales methodology of each funnel, the deal state and stage capture, and only while the install's processing instruction is active. A funnel's closing success/fail stage gives a stage its phase; any other stage takes the phase the customer chose, else the one AI guesses from the funnel and stage names (only the names are sent to the AI model, only with stage-based selection on); leads follow the default methodology, and so does a deal when the portal cannot be read. With stage-based evaluation the outcome sync re-reads each linked deal's stage on a schedule (#2086): a funnel's SUCCESS stage is won, its FAIL stage lost, any other open; the progression verdict, with no stage history in Uspacy, compares the stage at the call with the stage Aelo saw the deal in when the 7-day window ended, from the stage and funnel identifiers it stores at each sync (with the times it first and last saw the deal there) for 90 days after the deal was last seen there — a call whose window ends at a time Aelo had not seen the deal at (before the feature was switched on, or in a gap between two syncs) gets none. With the deal state, a deal's linked leads (`related/leads`) are read to find the one Uspacy marks as converted, so the deal continues that lead's state as in Bitrix24 (#1931; only `id` and `converted` are used). The deal snapshot's name and amount stay Bitrix24-only; of a Uspacy deal or lead only its owner (`owner`, the responsible employee) is read, with the deal state and only on processing instruction 1.17 or later, and kept with the state (#2454); the CRM task is written as a planned activity (`POST /crm/v1/static/tasks`) in the deal or lead, with `responsible_id` set to that owner when the latest state knows one and none otherwise, only while the install's processing is active (#2212, #2454). Structured field mapping, Smart Objects and org-structure import are later phases, and there is no source-wizard card — a Uspacy portal cannot be connected from the app, only from the Marketplace.",
      'Processing is gated by the acceptance state recorded per install, not by generic consent events: activation from the placement requires an exact install-time installer match, a portal already linked to an Aelo organization is reauthorized only from Aelo settings, and a paused or withdrawn install fails closed at ingest.',
      'Delivery writes a note on the call and one comment; a redelivery would append the report twice, so delivery receipts are on and entity ids are treated as local to the portal.',
      "The section menu on the app's page (`./settings`, #746, owner decision 2026-10-28) is links only: no frame, no session minted from the Uspacy token and no identity of the portal employee — the Aelo web app decides what the signed-in person may open. Deals and AI assistant carry a «Pro plan and up» mark, as in their own catalog entries. The card tab (`./app`) still shows the bare status; the tab for deals and leads is #746 PR 2.",
    ],
  },
  {
    id: 'integrations.uspacy_card_tab',
    title: 'Uspacy Card Tab',
    summary:
      "The Aelo tab in a Uspacy deal or lead card (#746): the conversations of that card the employee may see by their Aelo role, and the analysis in place. Only a portal employee linked to a member of the organization gets it — the link is made by the employee themselves, from the tab, through the ordinary Aelo sign-in; an unlinked employee is told to ask the Aelo administrator. The session is minted from the employee's Uspacy token, lives only in the frame's memory (no cookie) and is re-checked on every request and every 30 minutes against the installation, its processing state and instruction, the pilot and this flag, and the employee's link and membership.",
    area: 'integrations',
    status: 'prototype',
    // Hidden until the tab ships to customers (#746 PR 2b): server side only.
    customerVisible: false,
    surfaces: ['edge_api'],
    // unavailableEverywhere: tier defaults derive from packages, so 'included'
    // would silently enable the tab in every tier; it is enabled per organization.
    packages: unavailableEverywhere(),
    flag: {
      key: 'embeddedUspacy',
      defaultValue: false,
      isImplemented: true,
      category: 'beta',
      note: 'Per-organization switch for the Uspacy card tab. Off: POST /uspacy/placement/embedded-session and /employee-link answer state unavailable, and an open tab is revoked on its next request (401 session_revoked). Customers get it only after #2709 (manual start of a paused or withdrawn installation is refused) and after the partner attaches ./app — spec docs/plans/2026-10-24-uspacy-embedded-tab.md §3.2.',
    },
    docs: [
      {
        label: 'Card tab session spec',
        path: 'docs/plans/2026-10-29-uspacy-card-tab-session.md',
      },
    ],
    code: [
      {
        label: 'Card tab session and employee link routes',
        path: 'apps/edge-api/src/routes/uspacy/embedded-session.ts',
      },
      {
        label: 'Embedded session middleware',
        path: 'apps/edge-api/src/middleware/embedded-session.ts',
      },
    ],
  },
  {
    id: 'integrations.support_chat_identity',
    title: 'Support Chat Identity',
    summary:
      "Identifies a logged-in user to the operator side of the Bitrix24 support-chat widget embedded in the app, instead of the operator seeing an anonymous guest. Edge-api derives a salted hash (SUPPORT_CHAT_SALT) that the widget treats as external authentication, plus the user's name, email, avatar, and role, and a custom-data block with organization name, subscription tier and status, organization id, and which surface (standalone app vs. Bitrix24 domain) the identity came from. One handler is mounted for both the Clerk-authenticated app and the Bitrix24 embedded session, so a user keeps one support history regardless of which surface they write from.",
    area: 'integrations',
    status: 'available',
    customerVisible: true,
    surfaces: ['saas_app', 'bitrix24_embedded', 'edge_api'],
    packages: includedEverywhere(),
    // No feature flag and no admin toggle: always on wherever the widget
    // loads. Failure is silent by design (falls back to guest chat) rather
    // than surfaced as an error — see useSupportChatIdentity.ts.
    code: [
      {
        label: 'Support chat identity route (mounted for app + embedded)',
        path: 'apps/edge-api/src/routes/support-chat/index.ts',
      },
      {
        label: 'Support chat identity helpers (salted hash, custom-data block)',
        path: 'apps/edge-api/src/routes/support-chat/identity.ts',
      },
      uiDomainClient('Support domain client', 'support.api.ts'),
      {
        label: 'Support chat identity hook (publishes identity to widget)',
        path: 'apps/ui-worker/src/hooks/useSupportChatIdentity.ts',
      },
      {
        label: 'Support chat widget bridge (typed access to window.__aeloChat)',
        path: 'apps/ui-worker/src/lib/support-chat.ts',
      },
    ],
  },
  {
    id: 'integrations.crm_import',
    title: 'CRM Import',
    summary:
      'Bulk CRM import initiation, status, cancellation, history, and workflow trigger support.',
    area: 'integrations',
    status: 'available',
    customerVisible: true,
    surfaces: ['saas_app', 'edge_api', 'workflows_api'],
    packages: includedEverywhere(),
    docs: [
      {
        label: 'CRM data loader guide',
        path: 'docs/guides/CRM_DATA_LOADER_USER_GUIDE.md',
      },
    ],
    code: [
      { label: 'Edge API', path: 'apps/edge-api/src/routes/crm-import.ts' },
      uiDomainClient('CRM import domain client', 'crm.api.ts'),
      {
        label: 'Workflow route',
        path: 'apps/workflows-api/src/routes/crm-import-trigger.ts',
      },
      { label: 'Workflow', path: 'apps/workflows-api/src/workflows/crm-import.ts' },
      databaseRepository('Import job repository', 'ImportJobRepository.ts'),
    ],
  },
  {
    id: 'integrations.crm_fields_metadata',
    title: 'CRM Fields Metadata Cache',
    summary:
      'CRM field metadata cache that powers provider-aware field selection in the inbound routing rule builder.',
    area: 'integrations',
    status: 'available',
    customerVisible: true,
    surfaces: ['saas_app', 'edge_api'],
    packages: includedEverywhere(),
    code: [
      { label: 'CRM fields API', path: 'apps/edge-api/src/routes/crm-fields.ts' },
      uiDomainClient('CRM fields domain client', 'crm-fields.api.ts'),
      { label: 'D1 lookup schema', path: 'packages/database/src/schema/d1/lookups.ts' },
      {
        label: 'Routing feature',
        path: 'apps/ui-worker/src/features/routing/components/RoutingRuleBuilder.tsx',
      },
      // `ProjectCRMFieldsRepository.ts` was listed here too and has been deleted:
      // neither it nor its only consumer (`Bitrix24FieldsService`) was
      // constructed anywhere outside their own tests. The feature itself is
      // real and unaffected — it is delivered by the four references above plus
      // the cache repository below.
      databaseRepository('CRM fields cache repository', 'CrmFieldsCacheRepository.ts'),
    ],
  },
  {
    id: 'integrations.public_api',
    title: 'Public Upload API',
    summary: 'Programmatic call upload and result retrieval via org-scoped API keys.',
    area: 'integrations',
    status: 'available',
    customerVisible: true,
    surfaces: ['saas_app', 'edge_api'],
    packages: paidOnly(),
    flag: {
      key: 'enablePublicApi',
      defaultValue: true,
      isImplemented: true,
      category: 'business',
    },
    docs: [
      {
        label: 'Public API reference',
        path: 'apps/website/src/content/docs/en/docs/api/index.md',
      },
      {
        label: 'Upload guide',
        path: 'apps/website/src/content/docs/en/docs/api/upload.md',
      },
    ],
    code: [
      { label: 'Public API routes', path: 'apps/edge-api/src/routes/public/records.ts' },
      { label: 'API key auth', path: 'apps/edge-api/src/middleware/api-key-auth.ts' },
      {
        label: 'API key management routes',
        path: 'apps/edge-api/src/routes/settings/api-keys.ts',
      },
      {
        label: 'Public API schemas',
        path: 'packages/api-types/src/schemas/public-api.ts',
      },
      {
        label: 'Settings section',
        path: 'apps/ui-worker/src/routes/settings/components/ApiKeysSection.tsx',
      },
      databaseRepository('API key repository', 'ApiKeyRepository.ts'),
    ],
    notes: [
      "Paid-tier gate is enforced twice: at key-creation time in routes/settings/api-keys.ts (getTierRank(tier) <= 0 -> 403 with an upgrade prompt, in that route's own error envelope) and again at key-use time in api-key-auth.ts (getTierRank(tier) <= 0 -> 403 tier_not_eligible). Both checks read the lock tier (gateTier, billing.pro_trial): a free-tier org can neither mint nor use a key, except while its Pro trial is active.",
    ],
  },
  {
    id: 'routing.inbound_rules',
    title: 'Inbound Routing Rules',
    summary:
      'Organization-level JSONLogic rules and fallback settings that route incoming records to the right project.',
    area: 'automation',
    status: 'available',
    customerVisible: true,
    surfaces: ['saas_app', 'edge_api', 'workflows_api'],
    packages: includedEverywhere(),
    limitKeys: ['max_routing_rules'],
    code: [
      {
        label: 'Inbound routing API',
        path: 'apps/edge-api/src/routes/inbound-routing.ts',
      },
      uiDomainClient('Inbound routing domain client', 'inbound-routing.api.ts'),
      {
        label: 'Routing step',
        path: 'apps/workflows-api/src/workflows/record-pipeline/steps/routing-step.ts',
      },
      {
        label: 'Settings UI',
        path: 'apps/ui-worker/src/routes/settings/components/RoutingSection.tsx',
      },
      databaseRepository('Inbound routing repository', 'InboundRoutingRepository.ts'),
    ],
  },
  {
    id: 'delivery.crm_delivery_rules',
    title: 'CRM Delivery Rules',
    summary:
      'Post-analysis rules that choose which CRM fields to update after analysis. The rule engine works, but the project settings UI never reached parity: it edits conditions only, so every rule it writes carries an empty fieldsToUpdate and is a no-op at delivery time. Which analysis fields reach the CRM is configured per data source instead (delivery target fieldMappings), which makes this surface a redundant second place to answer the same question. Hidden behind a flag rather than deleted, and since #814 the flag is enforced rather than merely hiding the settings section: the routing step refuses before it reads a rule, and creating, editing, toggling or reordering one is refused by the API. Reading and deleting stay open so an organization can still see and clear what it already has; the repository and the routing code stay in place for the rebuild.',
    area: 'automation',
    status: 'prototype',
    customerVisible: false,
    surfaces: ['saas_app', 'edge_api', 'workflows_api'],
    // unavailableEverywhere: tier defaults derive from packages, so 'included'
    // would silently re-enable the flag in every tier.
    packages: unavailableEverywhere(),
    flag: {
      key: 'enableDeliveryRules',
      defaultValue: false,
      isImplemented: false,
      category: 'page',
      note: 'Hides the project settings "CRM Delivery" section. isImplemented:false is deliberate: the editor cannot set fieldsToUpdate, so a rule created here never writes anything, and targetEntityType is stored but never acted on — CRM delivery writes the rule fields to the record own CRM entity, and the branch that claimed to honour the rule target was removed as unreachable (#770). Forcing the flag false keeps an operator from switching on a surface that produces empty rules. Until #814 the force-off hid the settings section and nothing else: /api/v1/delivery-rules accepted a non-empty fieldsToUpdate and routingStep executed what it stored, so a rule created straight through the API did write CRM fields — from a surface this entry calls unavailable. Both ends enforce it now. Creating, editing, toggling and reordering are refused; reading and deleting stay open so an organization can still see and clear what it already has, and the routing step refuses before it reads the rules, which is what stops a rule already in the table. Re-enabling requires building the action editor and building entity targeting first — see BACKLOG.',
    },
    code: [
      { label: 'Delivery rule API', path: 'apps/edge-api/src/routes/delivery-rules.ts' },
      uiDomainClient('Delivery rules domain client', 'routing-rules.api.ts'),
      {
        label: 'Delivery worker',
        path: 'apps/workflows-api/src/workers/deliver/index.ts',
      },
      {
        label: 'Project settings UI',
        path: 'apps/ui-worker/src/routes/projects.$projectId.settings/components/DeliveryRulesTab.tsx',
      },
      databaseRepository('Delivery repository', 'DeliveryRepository.ts'),
      databaseRepository('Delivery rules repository', 'DeliveryRulesRepository.ts'),
    ],
  },
  {
    id: 'telephony.agent_directory',
    title: 'Telephony Agent Directory',
    summary:
      'Telephony agent directory refresh and lookup for matching provider users to AICA agents.',
    area: 'integrations',
    status: 'available',
    customerVisible: false,
    surfaces: ['edge_api', 'workflows_api', 'saas_app'],
    packages: includedEverywhere(),
    code: [
      {
        label: 'Agent directory API',
        path: 'apps/edge-api/src/routes/telephony/agent-directory.ts',
      },
      {
        label: 'Scheduled refresh',
        path: 'apps/workflows-api/src/scheduled/agent-directory-refresh.ts',
      },
      { label: 'D1 lookup schema', path: 'packages/database/src/schema/d1/lookups.ts' },
      databaseRepository(
        'Telephony agent directory repository',
        'TelephonyAgentDirectoryRepository.ts'
      ),
      databaseRepository('User external ID repository', 'UserExternalIdRepository.ts'),
    ],
  },
  {
    id: 'platform.command_palette',
    title: 'Command Palette',
    summary: 'Keyboard-driven navigation entrypoint in the application sidebar.',
    area: 'ui',
    status: 'available',
    customerVisible: true,
    surfaces: ['saas_app', 'bitrix24_embedded'],
    packages: includedEverywhere(),
    flag: {
      key: 'enableCommandPalette',
      defaultValue: true,
      isImplemented: true,
      category: 'ui',
      note: 'Controls CMD-K button visibility in sidebar',
    },
  },
  {
    id: 'platform.dark_mode',
    title: 'Dark Mode',
    summary: 'Application theme mode support.',
    area: 'ui',
    status: 'available',
    customerVisible: true,
    surfaces: ['saas_app', 'bitrix24_embedded'],
    packages: includedEverywhere(),
    // No feature flag: theme is managed by ThemeContext, always available.
  },
  {
    id: 'platform.whats_new_popup',
    title: "What's New Release Pop-up",
    summary:
      'One-time release notice shown at sign-in after a new version ships, in the app and inside the CRM placement.',
    area: 'ui',
    status: 'available',
    customerVisible: true,
    surfaces: ['saas_app', 'bitrix24_embedded'],
    packages: includedEverywhere(),
    // No feature flag: the pop-up is the release channel itself, not an
    // optional surface. A release with nothing to show declares `skip` in its
    // artefact instead — see `scripts/check-whats-new.mjs`.
    code: [
      {
        label: 'Release copy artefacts',
        path: 'docs/releases/whats-new/',
      },
      {
        label: 'Pop-up feature',
        path: 'apps/ui-worker/src/features/whats-new/WhatsNewGate.tsx',
      },
      {
        label: 'Seen-state (embedded)',
        path: 'apps/edge-api/src/routes/embedded/whats-new.ts',
      },
    ],
    notes: [
      'A tier-gated item is shown with a "available on Pro and up" note, never hidden: the point of the channel is that someone learns about a capability they do not have.',
      'Seen-state lives on the user (users.metadata.lastSeenRelease), so one person sees a release once across the app, the CRM placement and every device.',
    ],
  },
  {
    id: 'platform.animations',
    title: 'UI Animations',
    summary: 'Motion and transition layer for the application shell.',
    area: 'ui',
    status: 'available',
    customerVisible: true,
    surfaces: ['saas_app', 'bitrix24_embedded'],
    packages: includedEverywhere(),
    // No feature flag: motion layer is always on, not toggled.
  },
  {
    id: 'notifications.in_app',
    title: 'In-App Notifications',
    summary: 'Notification bell and dropdown for user-visible events.',
    area: 'notifications',
    status: 'available',
    customerVisible: true,
    surfaces: ['saas_app', 'bitrix24_embedded'],
    packages: includedEverywhere(),
    flag: {
      key: 'enableNotifications',
      defaultValue: true,
      isImplemented: true,
      category: 'ui',
      note: 'Controls notifications bell + dropdown in sidebar',
    },
    code: [
      uiDomainClient('Notifications domain client', 'notifications.api.ts'),
      databaseRepository('Notification repository', 'NotificationRepository.ts'),
    ],
  },
  {
    id: 'notifications.channel_preferences',
    title: 'Notification Channel Preferences',
    summary:
      "Per-user notification channel settings (email, Telegram, webhook) and per-category subscriptions for external delivery. A personal subscription overrides the org's per-role default; the org's RBAC matrix still gates which categories a role may see at all. Telegram is connected with a one-click, one-time link to Aelo's own platform bot — no chat ID or bot token entry. Org admins additionally set per-role category defaults in a role × category matrix.",
    area: 'notifications',
    status: 'available',
    customerVisible: true,
    surfaces: ['saas_app', 'edge_api'],
    packages: includedEverywhere(),
    code: [
      {
        label: 'Notification channels API',
        path: 'apps/edge-api/src/routes/settings/notification-channels.ts',
      },
      uiDomainClient('Settings domain client', 'settings.api.ts'),
      {
        label: 'Settings UI',
        path: 'apps/ui-worker/src/components/settings/NotificationChannelsSection.tsx',
      },
      {
        label: 'D1 notification schema',
        path: 'packages/database/src/schema/d1/notifications.ts',
      },
      databaseRepository(
        'User notification channels repository',
        'UserNotificationChannelsRepository.ts'
      ),
      {
        label: 'Category → RBAC permission mapping',
        path: 'packages/core/src/notifications/category-permissions.ts',
      },
      {
        label: 'Telegram one-time link generation + status',
        path: 'apps/edge-api/src/routes/integrations/telegram-link.ts',
      },
      {
        label: 'Telegram webhook (/start binding)',
        path: 'apps/edge-api/src/routes/webhooks/telegram.ts',
      },
      {
        label: 'Telegram connect card (profile UI)',
        path: 'apps/ui-worker/src/features/notifications/TelegramConnectCard.tsx',
      },
      {
        label: 'Category subscription checkboxes',
        path: 'apps/ui-worker/src/features/notifications/CategorySubscriptions.tsx',
      },
      {
        label: 'Org role-defaults matrix (admin settings)',
        path: 'apps/ui-worker/src/features/notifications/RoleDefaultsMatrix.tsx',
      },
    ],
  },
  {
    id: 'notifications.email_digests',
    title: 'Email Digests',
    summary:
      "Scheduled organization owner/admin/supervisor/agent digest emails backed by queues, templates, and unsubscribe tokens. Includes an alerts section — an aggregate rule/severity/count table for alerts below the instant-delivery severity threshold — shown only to roles whose RBAC-allowed categories include quality_alerts; the table has no per-record detail, so it never carries a caller's name or phone number. The daily digest covers the 24 hours before it is sent; the weekly digest goes out at 08:00 in the organization's time zone on its first day of the week (settings.organization_week_start, Monday by default) and covers the calendar week that has just ended, trending against the week before — the same week the dashboard's Week period shows. Delivery resolves per person: an organization-wide daily/weekly default (settable by a role holding organization.update; on when never configured) that each member may override for themselves, plus the unsubscribe link every digest carries. The daily digest of an organization owner (the whole organization) and a supervisor (their own projects) lists up to five objection reasons the team left unhandled the previous local day — reason names and calls unhandled out of calls where it came up, counted by the Objections screen's code, never a customer quote or wording — with an org-tagged link to that screen on that day; it also gives the share of objections that could not be recognized, as the screen does, so it is absent only when nothing was left unhandled or the count could not be made. A member who holds an active narrow access grant (Settings → Team → Access) receives no digest while it is active, because the digest letter is picked by role and would carry what the grant withholds; revoking the grant resumes the digests.",
    area: 'reporting',
    status: 'available',
    customerVisible: true,
    surfaces: ['workflows_api', 'edge_api', 'saas_app'],
    packages: includedEverywhere(),
    docs: [
      { label: 'Digest plan', path: 'docs/plans/2026-06-02-email-digests.md' },
      {
        label: 'Digest implementation',
        path: 'docs/plans/2026-06-02-email-digests-implementation.md',
      },
    ],
    code: [
      { label: 'Digest builder', path: 'apps/workflows-api/src/digests/builder.ts' },
      { label: 'Digest queue', path: 'apps/workflows-api/src/queue-handlers/digest.ts' },
      {
        label: 'Unsubscribe API',
        path: 'apps/edge-api/src/routes/digests/unsubscribe.ts',
      },
      {
        label: 'Org digest defaults (settings API)',
        path: 'apps/edge-api/src/routes/settings.ts',
      },
      {
        label: 'Org digest defaults (UI)',
        path: 'apps/ui-worker/src/features/notifications/RoleDefaultsMatrix.tsx',
      },
      databaseRepository('Notification repository', 'NotificationRepository.ts'),
    ],
  },
  {
    id: 'analytics.overview',
    title: 'Analytics Dashboards',
    summary:
      'Dashboard and analytics pages for call volume, quality, and performance views.',
    area: 'analytics',
    status: 'available',
    customerVisible: true,
    surfaces: ['saas_app', 'bitrix24_embedded'],
    packages: includedEverywhere(),
    flag: {
      key: 'enableAnalytics',
      defaultValue: true,
      isImplemented: true,
      category: 'page',
      note: 'Controls analytics sidebar items',
    },
    docs: [{ label: 'Analytics docs', path: 'docs/analytics/README.md' }],
    code: [
      uiDomainClient('Settings domain client', 'settings.api.ts'),
      databaseRepository('Analytics repository', 'AnalyticsRepository.ts'),
    ],
  },
  {
    id: 'analytics.call_statistics',
    title: 'Call Statistics',
    summary:
      'Call statistics page and API for call volume, trends, durations, and operational call metrics. The page counts calls only — chats are not in its total — unless it is opened from a dashboard card (its report link) whose channel filter is Chats or All channels: then it counts that channel, so its numbers are those of the card. Filters: period, projects. The missed share is missed inbound calls of all inbound calls, answered and missed; missed calls whose direction is unknown are shown apart and are not in the share.',
    area: 'analytics',
    status: 'available',
    customerVisible: true,
    surfaces: ['saas_app', 'edge_api'],
    packages: includedEverywhere(),
    docs: [
      {
        label: 'Call statistics design',
        path: 'docs/analytics/CALL_STATISTICS_DESIGN.md',
      },
    ],
    code: [
      { label: 'UI route', path: 'apps/ui-worker/src/routes/analytics/call-stats.tsx' },
      uiDomainClient('Call stats domain client', 'call-stats.api.ts'),
      { label: 'Analytics API', path: 'apps/edge-api/src/routes/analytics.ts' },
      databaseRepository('Analytics repository', 'AnalyticsRepository.ts'),
      databaseRepository(
        'Record metadata analytics repository',
        'RecordMetadataAnalyticsRepository.ts'
      ),
      databaseRepository('Postgres analytics reader', 'PgRecordAnalyticsReader.ts'),
      databaseRepository('Missed-call definitions', 'missed-call-counts.ts'),
    ],
  },
  {
    id: 'analytics.missed_calls',
    title: 'Missed Calls Analytics',
    summary:
      'Missed-call analytics view for call outcomes, follow-up gaps, and operational monitoring: missed inbound calls, outbound calls the client did not answer, missed calls of unknown direction (apart, not in the share), the missed share of inbound calls and a table by employee. Counted on the server over every call of the period, within the records the viewer may open — not over the first 200 rows of a list. In Records the same split is on the badge of a call, in its card and in the filter: “Missed” (an inbound call nobody picked up), “Client No-Answer” (an outbound call the client did not answer) and “Missed, direction unknown”; choose the “Missed” category together with a Direction (Incoming, Outgoing or Direction unknown) to list each of them on its own. In Bitrix24, an outbound call under 15 seconds that Bitrix24 does not mark as missed is counted as “Short”, not as “Client No-Answer”. Filters: period, projects (the API takes projectIds); opened from a dashboard card, the page shows the window, projects and demo rule of that card.',
    area: 'analytics',
    status: 'available',
    customerVisible: true,
    surfaces: ['saas_app', 'edge_api'],
    packages: includedEverywhere(),
    code: [
      { label: 'UI route', path: 'apps/ui-worker/src/routes/analytics/missed-calls.tsx' },
      uiDomainClient('Call stats domain client', 'call-stats.api.ts'),
      {
        label: 'Analytics API (GET /api/v1/analytics/missed-calls)',
        path: 'apps/edge-api/src/routes/analytics.ts',
      },
      databaseRepository('Record metadata repository', 'RecordMetadataBaseRepository.ts'),
      databaseRepository('Missed-call definitions', 'missed-call-counts.ts'),
    ],
  },
  {
    id: 'analytics.customer_questions',
    title: 'Customer Questions and Decline Reasons',
    summary:
      'Part of the Voice of Customer section: what customers ask and why they decline, from the conversations analysed since collection started — there is no earlier history. Sales tab: frequent questions by subject ("in K of N conversations", the change against the previous period and the three latest wordings), questions left without a full answer, and why customers decline ("in K of N declines") with the declines broken down by the call number in the series, by employee (an employee gets a row only with at least 3 sales conversations in the period) and, with stage-based scorecard selection, by funnel stage. Site and content tab: frequent questions and first-minute questions — on a call, asked within the first 60 seconds; in a chat, asked before the employee\'s first message — with an export of the tab and a draft FAQ for your website built on request (not stored, up to 10 drafts a day per organization and 5 per person). Every number opens the conversations behind it. Neither collecting the questions nor building the draft charges minutes.',
    area: 'analytics',
    status: 'beta',
    customerVisible: true,
    surfaces: ['saas_app', 'bitrix24_embedded', 'edge_api', 'workflows_api'],
    packages: proPlus(),
    flag: {
      key: 'enableCustomerQuestions',
      defaultValue: false,
      isImplemented: true,
      category: 'beta',
      note: 'Pro and up, inside Voice of Customer (spec 2026-10-02-customer-questions-and-decline-reasons.md §8 p. 9). Read server-side by GET /analytics/voice (the customerQuestions blocks), POST /analytics/voice/faq-draft and the lost-deal reason writers; the monthly email adds its questions and declines lines only with it. Beta until the quality measurement passes (docs/eval/2026-10-03-voice-extraction-measurement.md; for the decline outcome and reason, since v5 — docs/eval/2026-10-15-voice-extraction-v5-release-decisions.md, since v6 — docs/eval/2026-10-25-voice-extraction-stated-and-disputed.md, since v7 — docs/eval/2026-10-25-voice-extraction-reason-boundary.md, since v8 — docs/eval/2026-11-03-voice-extraction-declined-step-v8.md); the blocks "questions before a decision" (phase) and "share named by the customer" (stated) did not pass and are not shown. The step runs prompt revision voice-extraction-v8: v5 (#2274) — a no for now is a decline (budget / timing), a non-decision-maker with no next step is a decline (not_decision_maker); v6 (#2311) — "named by the customer" gets positive examples; v7 (#2264) — a reason named together with a later time wins over the time, "we do not need it now" is no_current_need; v8 (#2753) — a no for now outweighs any step accepted with it (a visit, a meeting, a quote), not only an agreed call. The v6 measurement brought "named by the customer" to its threshold by point estimate (0.835, interval reaching under 0.8); the v7 measurement raises reason recall (+0.065) and reason precision (+0.075) but leaves reason recall under its threshold (0.692 < 0.7; accepted by the owner on 26.10); the v8 measurement moves no measure beyond noise and keeps reason recall at 0.692 (accepted by the owner on 03.11), so the feature stays beta and the stated block stays hidden.',
    },
    docs: [
      {
        label: 'Customer questions and decline reasons spec',
        path: 'docs/plans/2026-10-02-customer-questions-and-decline-reasons.md',
      },
      {
        label: 'Release quality measurement',
        path: 'docs/eval/2026-10-03-voice-extraction-measurement.md',
      },
      {
        label: 'Decline outcome measurement of revision v5',
        path: 'docs/eval/2026-10-15-voice-extraction-v5-release-decisions.md',
      },
      {
        label: 'Decline outcome and "named by the customer" measurement of revision v6',
        path: 'docs/eval/2026-10-25-voice-extraction-stated-and-disputed.md',
      },
      {
        label: 'Decline reason code measurement of revision v7',
        path: 'docs/eval/2026-10-25-voice-extraction-reason-boundary.md',
      },
      {
        label: 'Decline outcome measurement of revision v8 (governs the beta)',
        path: 'docs/eval/2026-11-03-voice-extraction-declined-step-v8.md',
      },
      {
        label: 'Voice of Customer guide',
        path: 'apps/website/src/content/docs/en/docs/user-guide/voice-of-customer.md',
      },
    ],
    code: [
      {
        label: 'Extraction step',
        path: 'apps/workflows-api/src/workflows/record-pipeline/steps/voice-extraction-step.ts',
      },
      {
        label: 'Extraction contract and code sets',
        path: 'packages/api-types/src/schemas/voice-extraction.ts',
      },
      databaseRepository('Extraction results', 'VoiceExtractionRepository.ts'),
      {
        label: 'Blocks gate (edge-api)',
        path: 'apps/edge-api/src/utils/customer-questions-gate.ts',
      },
      {
        label: 'Blocks gate (workflows-api: FAQ draft, lost-deal reason, email)',
        path: 'apps/workflows-api/src/utils/customer-questions-gate.ts',
      },
      {
        label: 'Sales tab: Questions section',
        path: 'apps/ui-worker/src/components/analytics/voice/VoiceSalesQuestions.tsx',
      },
      {
        label: 'Sales tab: Declines section',
        path: 'apps/ui-worker/src/components/analytics/voice/VoiceSalesDeclines.tsx',
      },
      {
        label: 'Site and content tab blocks',
        path: 'apps/ui-worker/src/components/analytics/voice/VoiceSiteQuestions.tsx',
      },
      { label: 'FAQ draft API', path: 'apps/edge-api/src/routes/voice-faq-draft.ts' },
      {
        label: 'FAQ draft generation',
        path: 'apps/workflows-api/src/workers/voice-faq/draft.ts',
      },
      {
        label: 'Old route, redirects to Voice of Customer',
        path: 'apps/ui-worker/src/routes/analytics/customer-questions.tsx',
      },
    ],
  },
  {
    id: 'dashboards.role_based',
    title: 'Role-Based Dashboards',
    summary:
      'Organization owner, supervisor, and agent dashboards with role-specific KPIs and navigation.',
    area: 'analytics',
    status: 'available',
    customerVisible: true,
    surfaces: ['saas_app', 'bitrix24_embedded', 'edge_api'],
    packages: includedEverywhere(),
    code: [
      { label: 'Dashboard route', path: 'apps/ui-worker/src/routes/dashboard.tsx' },
      uiDomainClient('Settings domain client', 'settings.api.ts'),
      uiDomainClient('Organizations domain client', 'organizations.api.ts'),
      { label: 'Dashboard API', path: 'apps/edge-api/src/routes/dashboard.ts' },
      { label: 'Route permissions', path: 'apps/ui-worker/src/lib/route-permissions.ts' },
      databaseRepository('Analytics repository', 'AnalyticsRepository.ts'),
    ],
  },
  {
    id: 'dashboards.highlights',
    title: 'Dashboard Highlights',
    summary:
      "A Highlights tab on the organization owner and supervisor dashboards that sums up the selected period in plain words: conversations from received to analyzed, the three most frequent issues by scoring metric, conversations without a high or critical attention flag with the flagged list, the average score with the best and lagging agent, conversation topics, serious product complaints and competitor mentions. Competitor names are merged through the organization's glossary, so the spellings of one brand are counted as one row under the glossary name; a name the glossary knows as something other than a competitor — the customer's own brand, the platform they service, a supplier, a partner, a colleague, a piece of jargon — is not listed as a competitor at all. A name the glossary does not know keeps its own spelling, is marked as not merged, and under the list the tab shows what share of the names heard that is — a name counts once per conversation — naming up to three of them, including ones too rare to make the list. An organization whose glossary lists no competitor sees neither the mark nor the share: there is nothing to fill in, and names are merged by spelling alone. Supervisors see only their own projects.",
    area: 'reporting',
    status: 'available',
    customerVisible: true,
    surfaces: ['saas_app', 'bitrix24_embedded', 'edge_api'],
    packages: includedEverywhere(),
    docs: [
      { label: 'Highlights tab spec', path: 'docs/plans/2026-09-17-highlights-tab.md' },
      {
        label: 'Dashboard guide',
        path: 'apps/website/src/content/docs/en/docs/user-guide/dashboard.md',
      },
    ],
    code: [
      {
        label: 'Highlights tab',
        path: 'apps/ui-worker/src/components/dashboards/shared/highlights/HighlightsTab.tsx',
      },
      uiDomainClient('Reports domain client', 'reports.api.ts'),
      { label: 'Report summary API', path: 'apps/edge-api/src/routes/reports.ts' },
      databaseRepository('Report repository', 'ReportRepository.ts'),
      databaseRepository('Report summary rules', 'report-summary.ts'),
      { label: 'Criterion keys', path: 'packages/core/src/criterion-keys.ts' },
      { label: 'Report contract', path: 'packages/api-types/src/schemas/reports.ts' },
      { label: 'Competitor merging', path: 'packages/core/src/voc/rank-competitors.ts' },
      {
        label: 'Glossary reader',
        path: 'apps/edge-api/src/utils/competitor-glossary.ts',
      },
    ],
  },
  {
    id: 'dashboards.quality_slice',
    title: 'Dashboard Quality Block',
    summary:
      'The first block of the unified dashboard: the average quality score over the scored conversations of the period, how many were scored and how many were not, the change against the previous period of the same length, and the score day by day. Every number opens what it rests on and, wherever the Records list can take its scope, the very conversations it was computed from — a supervisor with no project selected and more than 40 projects is told why instead. Supervisors see their own projects, agents their own conversations.',
    area: 'reporting',
    status: 'beta',
    customerVisible: true,
    surfaces: ['saas_app', 'bitrix24_embedded', 'edge_api'],
    // Closed preview of the unified dashboard (#1130): an explicit org opt-in, no package.
    packages: unavailableEverywhere(),
    flag: {
      key: 'enableDashboardQualitySlice',
      defaultValue: false,
      isImplemented: true,
      category: 'beta',
      note: "Closed preview. GET /api/v1/dashboard/quality answers 404 while the flag is off; the dashboard asks only where the flag map says it is on — on the web from /feature-flags, in embedded Bitrix24 from the session's flags subset (#1577).",
    },
    docs: [
      {
        label: 'Quality block spec',
        path: 'docs/plans/2026-09-29-dashboard-quality-e1.md',
      },
      {
        label: 'Dashboard guide',
        path: 'apps/website/src/content/docs/en/docs/user-guide/dashboard.md',
      },
    ],
    code: [
      {
        label: 'Quality block',
        path: 'apps/ui-worker/src/components/dashboards/shared/quality/QualitySliceBlock.tsx',
      },
      {
        label: 'Quality slice API',
        path: 'apps/edge-api/src/routes/dashboard-quality.ts',
      },
      {
        label: 'Quality slice contract',
        path: 'packages/api-types/src/schemas/dashboard-quality.ts',
      },
      databaseRepository('Analytics repository', 'AnalyticsRepository.ts'),
    ],
  },
  {
    id: 'dashboards.unified',
    title: 'Unified Dashboard',
    // #1337 (E2 of #1130): the shell of the new dashboard. Not customer-visible
    // while the preview is closed — no promise to describe before E5.
    summary:
      'The new dashboard of six tabs — Summary, Conversations, Sales, Quality, Team and Voice of Customer — with one filter panel (period, comparison with the previous period of the same length, projects, channel, demo data) kept in the address; a group lead sees which departments its view covers, as a scope, not a filter. Every block is a card with its number, its base, its period, the time the data was read and the change against the previous period, and states of its own; a block or tab the viewer has no capability for is not shown. Conversations: how many came in and where each is in its life cycle (the Highlights funnel, missed calls a group of their own), coverage of what could be analysed, the missed-call share of inbound calls with an unknown direction counted apart, by day, structure by category, channel and direction and project, and the load by hour and weekday × hour in the viewer’s zone; each number explains what it rests on, and a life-cycle group opens its records for a viewer who may read them. Voice of Customer (Pro and up, as the Voice of Customer section): competitors, serious feedback, purchase barriers, market signals, conversation themes, needs, customer question subjects and stated decline reasons — labels and counts of the viewer’s scope, each with its own base, the change in percentage points, «few data» under five conversations, at most five rows and how many more, and a link to the section’s report; no client’s words; an export of the tab as a table of those labels and counts, read anew on the click, with the period, the scope and the demo setting (the customers’ wordings stay in the report’s export). Quality’s company criteria card lists every criterion — flag, score and choice — with its value, what a flag looks for, its calls as evaluated, unsure and not applicable, not evaluated as an upper bound, paused, archived and a wording changed in the period, and opens the criteria report on its own screen (/analytics/criteria) at that criterion. In Team, a name in the Employees table opens that person’s three most frequent dips for the period — the Highlights top issues narrowed to the person, «in N of M» of their analysed conversations, with examples that open the conversations; periods up to 93 days. The same name opens «After feedback»: the person’s score per criterion over the 10 scored conversations before the supervisor’s latest feedback (a coaching note, an annotation other than a correction, a review comment) and the 10 from it onward, n on both sides, the change shown where both sides have at least 3, and in words when there has been no feedback, too few conversations since, or too few before it. The channel filter applies to Conversations; Quality counts every channel and says so. Organization owners, administrators, supervisors and access-grant holders get it on /dashboard and in Bitrix24, where the six tabs replace the Overview. An agent gets its own view in the same shell, one tab «Mine»: its quality score with the change against the previous period, the skills to work on, its place among the colleagues with at least five scored calls (shown once it has five itself), its skills against the team, its frequent dips (the same block, counted the same way), its score after its latest feedback (the same block; demo notes are not its feedback), its personal bests and its supervisor’s notes. The platform owner keeps its dashboard, and so does an owner looking through an agent’s eyes.',
    area: 'reporting',
    status: 'beta',
    customerVisible: false,
    surfaces: ['saas_app', 'bitrix24_embedded'],
    // Closed preview: an explicit org opt-in by the platform owner, no package.
    packages: unavailableEverywhere(),
    flag: {
      key: 'enableUnifiedDashboard',
      defaultValue: false,
      isImplemented: true,
      category: 'beta',
      // The customer must not switch it on: the shell shows «скоро» cards in
      // place of the domains still being built (#1337–#1340).
      platformOwnerOnly: true,
      note: "Closed preview of the unified dashboard (#1337, spec docs/plans/2026-10-06-dashboard-e2.md). On: /dashboard renders the six-tab shell for client, admin, supervisor and grant holders instead of their role dashboard; in embedded Bitrix24 (the session's flags subset) the six tabs replace the Overview tab. Agents get the personal view of the same shell (#1338 PR C, one tab «mine») instead of AgentDashboard; the platform owner keeps OwnerDashboard, and an owner's View As agent keeps AgentDashboard. The personal view reads the E1 quality slice for its score, GET /api/v1/agents/me/performance (optional projectIds, checked like every slice, narrowing only the own reads — the team is never narrowed; scoredCount of both windows; ratedRanking ranks only people with at least 5 scored conversations) for focus, rank, skills and bests, and GET /api/v1/me/coaching/notes and GET /api/v1/agents/me/coaching/sessions (#2053, spec docs/plans/2026-10-31-personal-coaching-sessions.md: the employee's own coaching meetings in the dashboard window [start, end), start included and end excluded, with n = total and limit/offset pagination; own meetings only, never ones the person leads, demo left out; team.scores for a grant, the meeting's text only with evidence.text; 404 while enableCoaching is off) behind enableCoaching. Off: the role dashboards, unchanged. The domain routes it reads come with their PRs and are gated by this flag (404 while off): GET /api/v1/dashboard/flow (Conversations, PR 2, capability aggregates.flow) and GET /api/v1/dashboard/team (Team, #1338, capability aggregates.team; names and rows need team.scores, small counts withheld from aggregates-only viewers) and GET /api/v1/dashboard/quality/sections (Quality, #1338 PR B, capability aggregates.quality; objections also need aggregates.voc, else noAccess; aggregates only, no quotes or names) and GET /api/v1/dashboard/sales (Sales, #1339, capability aggregates.sales, Pro and up through enableDashboardSales; aggregates only) and GET /api/v1/dashboard/voice (Voice of Customer, #1340, capability aggregates.voc, Pro and up through enableVoiceOfCustomer as the section; themes and customer questions behind their own flags; labels and counts only, no quotes, records or employees, so an analyst grant sees question subjects and decline reasons as counts) so far; below Pro, an org in its demo window (demo records and no real record yet) gets demo rows only (demoOnly true) from these two routes instead of 403 tier_not_eligible (#2816, see the enableDashboardSales and enableVoiceOfCustomer entries). An employee's frequent dips (#767): GET /api/v1/reports/employee-issues (employee = the Team row key; capabilities team.scores and aggregates.quality; the Highlights scope — a supervisor's own projects) and GET /api/v1/agents/me/issues (own records, team.scores for a grant; projects only narrow) — both the Highlights top issues over that person's records, examples only with evidence.text and records.read; both gated by this flag (404 while off). GET /agents/me/issues narrows by projectId or projectIds. «After feedback» (#2609, spec docs/plans/2026-10-25-after-feedback-block.md): GET /api/v1/reports/employee-feedback-effect (employee = the Team row key; team.scores and aggregates.quality; the same scope) and GET /api/v1/agents/me/feedback-effect (own records) — the latest feedback date from D1 (coaching_notes, analysis_annotations not of type correction, analysis_reviews with overall_feedback at analysis_reviews.feedback_at, the moment the text appeared; never the employee's own nor the platform owner's; a review's author is analysis_reviews.feedback_by_user_id, who wrote the text; within the caller's scope — feedback on the person's in-scope records, a note tied to a record only when that record is in scope, one tied to none only when the person has an in-scope record), then per criterion the six analyses.metrics sub-scores over the 10 nearest scored records before and from it, by records.created_at, ignoring the dashboard period, reading at most 100 nearest analysed records per side; both gated by this flag (404 while off). No route takes a department filter: a group lead's departments bound its scope server-side.",
    },
    docs: [
      { label: 'E2 spec', path: 'docs/plans/2026-10-06-dashboard-e2.md' },
      { label: 'E4 spec', path: 'docs/plans/2026-10-18-dashboard-e4.md' },
      { label: 'Employee dips spec', path: 'docs/plans/2026-10-18-employee-top-dips.md' },
      {
        label: 'Personal coaching meetings spec',
        path: 'docs/plans/2026-10-31-personal-coaching-sessions.md',
      },
      {
        label: 'After feedback spec',
        path: 'docs/plans/2026-10-25-after-feedback-block.md',
      },
      {
        label: 'Revision 5 layout',
        path: 'docs/plans/2026-10-02-dashboard-prototype-rev5.md',
      },
    ],
    code: [
      {
        label: 'Unified dashboard shell',
        path: 'apps/ui-worker/src/components/dashboards/unified/UnifiedDashboard.tsx',
      },
      {
        label: 'Dashboard route',
        path: 'apps/ui-worker/src/routes/dashboard.tsx',
      },
      {
        label: 'Embedded dashboard tab',
        path: 'apps/ui-worker/src/routes/embedded/dashboard/components/DashboardTab.tsx',
      },
      {
        label: 'Conversations (flow) route',
        path: 'apps/edge-api/src/routes/dashboard-flow.ts',
      },
      {
        label: 'Team route',
        path: 'apps/edge-api/src/routes/dashboard-team.ts',
      },
      {
        label: 'Quality sections route',
        path: 'apps/edge-api/src/routes/dashboard-quality-sections.ts',
      },
      {
        label: 'Personal view cards',
        path: 'apps/ui-worker/src/components/dashboards/unified/PersonalCard.tsx',
      },
      {
        label: 'Criteria report screen',
        path: 'apps/ui-worker/src/routes/analytics/criteria.tsx',
      },
      {
        label: 'Voice of Customer tab export',
        path: 'apps/ui-worker/src/components/dashboards/unified/voice-export.ts',
      },
      {
        label: 'Own performance route',
        path: 'apps/edge-api/src/routes/agents/performance/handler-me.ts',
      },
    ],
  },
  // «Продажи» нового дашборда (#1339): Pro и выше — решение владельца 12.10.
  // Вкладка живёт внутри закрытого превью `dashboards.unified`, поэтому
  // клиентских обещаний до выпуска (E5) нет.
  {
    id: 'dashboards.sales',
    title: 'Unified Dashboard — Sales',
    summary:
      "The Sales tab of the unified dashboard: the deals with a conversation in the period and how they end — won, lost, open, or outcome unknown (not synced from the CRM yet) — as the deal stands in the CRM now, not closed within the period; the average score of the period's conversations per outcome; the share of conversations after which the deal moved to a later stage within 7 days, by the stage it was at during the conversation; how many contacts the deals took, counted over the deal's whole life; and how fresh and complete the CRM data is: the oldest and newest outcome sync among the period's deals, the deals not synced, and how many calls are tied to a deal, a lead or nothing. No change against a previous period (an outcome is a current CRM state), no amounts, and no claim that a conversation caused an outcome. Outcomes and stage movement need stage-aware scoring on and a CRM that reports outcomes (Bitrix24). Exception: while every deal of the period is a demo deal (sample data, #1499), the tab shows them whole without a CRM and without stage-aware scoring; the demo is cleared with the first real calls.",
    area: 'reporting',
    status: 'beta',
    customerVisible: false,
    surfaces: ['saas_app', 'bitrix24_embedded', 'edge_api'],
    packages: proPlus(),
    flag: {
      key: 'enableDashboardSales',
      defaultValue: false,
      isImplemented: true,
      category: 'page',
      note: 'Pro and up (owner decision 12.10): the server refuses GET /api/v1/dashboard/sales below Pro (403 tier_not_eligible) and with the flag off (feature_disabled); below Pro the tab is shown locked with the plan screen. Exception (#2816): below Pro, an org in its demo window (demo records and no real record yet) is answered 200 with demoOnly true and demo rows only, unless the request explicitly asks includeDemo=no (a flag switched off by an override never opens the demo: the refusal stays 403 tier_not_eligible) and the tab shows the demo under a «This is demo data» banner; without demo records the 403 and the locked tab stay. Inside the closed preview of enableUnifiedDashboard (404 while that is off).',
    },
    docs: [
      { label: 'E2 spec — Sales', path: 'docs/plans/2026-10-06-dashboard-e2.md' },
      {
        label: 'Demo deals spec',
        path: 'docs/plans/2026-10-14-demo-crm-deals-series.md',
      },
    ],
    code: [
      {
        label: 'Sales route',
        path: 'apps/edge-api/src/routes/dashboard-sales.ts',
      },
      {
        label: 'Sales slice contract',
        path: 'packages/api-types/src/schemas/dashboard-sales.ts',
      },
      {
        label: 'Sales tab cards',
        path: 'apps/ui-worker/src/components/dashboards/unified/SalesCard.tsx',
      },
      databaseRepository('Analytics repository', 'AnalyticsRepository.ts'),
    ],
  },
  {
    id: 'analytics.objection_reasons',
    title: 'Objections by Reason',
    summary:
      'The supervisor Quality tab ranks the objection reasons the team most often leaves unhandled. Every objection is sorted into one reason from a closed list of 19 (price, not now, delivery and availability, needs approval, other, and so on), so one reason is counted together however customers phrase it. Counting unit is the conversation. Objections analyzed before the list existed are sorted through a stored mapping of past wordings; the share that could not be recognized is shown on the card, never hidden. Each reason shows how customers put it and a quote of an unhandled one; a reason the team handled in every conversation stays on the list with 0 % unhandled. From the card, the Objections screen shows every objection of a period up to a quarter in two views: market (conversations and share per reason among conversations with a recognized objection, change against the previous period of the same length, customer wordings and quotes; deal stage at the time of the call and deal movement after it only where the CRM data exists for those conversations) and team (unhandled share, average handling score, operators who drop the reason most, from 3 conversations). Each reason shows up to three best-handled conversations with the manager replies that followed, taken from the transcript, and a quote opens its conversation with the quote highlighted. Filters: period, projects, call category.',
    area: 'analytics',
    status: 'available',
    customerVisible: true,
    surfaces: ['saas_app', 'bitrix24_embedded', 'edge_api'],
    packages: includedEverywhere(),
    docs: [
      {
        label: 'Canonical labels spec',
        path: 'docs/plans/2026-09-18-voc-canonical-labels.md',
      },
      {
        label: 'Dashboard guide',
        path: 'apps/website/src/content/docs/en/docs/user-guide/dashboard.md',
      },
      {
        label: 'Objections market signal spec',
        path: 'docs/plans/2026-09-27-objections-market-signal.md',
      },
      {
        label: 'Objections guide',
        path: 'apps/website/src/content/docs/en/docs/user-guide/objections.md',
      },
    ],
    code: [
      {
        label: 'Quality tab',
        path: 'apps/ui-worker/src/components/dashboards/supervisor/QualityTab.tsx',
      },
      {
        label: 'Objections screen',
        path: 'apps/ui-worker/src/components/analytics/objections/ObjectionsView.tsx',
      },
      { label: 'Objections API', path: 'apps/edge-api/src/routes/agents/objections.ts' },
      { label: 'Objections contract', path: 'packages/api-types/src/schemas/agents.ts' },
      databaseRepository('Objections aggregate', 'ObjectionsRepository.ts'),
      databaseRepository('Objection stream', 'objection-stream.ts'),
      databaseRepository('Label map reader', 'VocLabelMapRepository.ts'),
      { label: 'Objection codes', path: 'packages/core/src/voc/objection-codes.ts' },
      { label: 'Signal ranking', path: 'packages/core/src/voc/rank-signals.ts' },
      { label: 'Answer excerpt', path: 'packages/core/src/voc/objection-excerpt.ts' },
    ],
  },
  {
    id: 'analytics.script_steps',
    title: 'Script Steps Report',
    summary:
      'An Analytics screen showing which steps of the sales script the team does not complete and which criteria they miss. It opens from the menu or from the Skipped Steps widget on the Quality tab, with the widget\'s period and projects. For each script it lists the steps, the most often not completed first; every step shows the share of conversations where it was not completed next to the number of conversations that owed it ("143 of 150"), how many of those were partly done and how many fell short of exactly one criterion, and how many conversations did not need the step (an optional step that did not come up). A step nobody missed stays on the list with 0. "Not completed" means not every required criterion matched, the same as the tick on the record. A row opens into a breakdown by employee (employees with few conversations are one footnote, conversations without an employee are their own line) and by criterion, with the criteria that are almost never met marked so the wording can be checked before blaming people. An opened step links to the Records list filtered to the conversations where that step was not completed, with the report\'s period and projects (no link for a step removed from the script, and none in the Bitrix24 app). The top of the page says how many of the analyzed records of the period were evaluated against a script, and marks a script whose rules changed inside the period. A permanent note explains that a step required in the script but conditional in meaning is counted as not completed when the condition did not occur, until steps can be marked as conditional. Supervisors see their own projects. Filters: period up to a quarter, projects.',
    area: 'analytics',
    status: 'available',
    customerVisible: true,
    surfaces: ['saas_app', 'edge_api'],
    packages: includedEverywhere(),
    docs: [
      {
        label: 'Script steps report spec',
        path: 'docs/plans/2026-10-05-script-steps-report.md',
      },
      {
        label: 'Scripts guide',
        path: 'apps/website/src/content/docs/en/docs/user-guide/scripts.md',
      },
    ],
    code: [
      {
        label: 'Script steps screen',
        path: 'apps/ui-worker/src/components/analytics/script-steps/ScriptStepsView.tsx',
      },
      {
        label: 'Script steps route',
        path: 'apps/ui-worker/src/routes/analytics/script-steps.tsx',
      },
      {
        label: 'Quality tab',
        path: 'apps/ui-worker/src/components/dashboards/supervisor/QualityTab.tsx',
      },
      uiDomainClient('Reports domain client', 'reports.api.ts'),
      {
        label: 'Script steps report API',
        path: 'apps/edge-api/src/routes/reports-script-steps.ts',
      },
      {
        label: 'Script steps report contract',
        path: 'packages/api-types/src/schemas/script-steps-report.ts',
      },
      databaseRepository('Script steps aggregate', 'ScriptStepsReportRepository.ts'),
    ],
    notes: [
      'No flag of its own: the menu item follows the scripts flag (`enableScripts`, coaching.scripts) and the API is the quality aggregate (`aggregates.quality`), so a narrow analytics grant that holds it sees the screen.',
      'Not completed = isCompleted false on a step the conversation owed (`countsTowardScore` is not false). Optional steps that did not come up are shown as "not needed", outside the share. Data is the evaluation of a conversation by ONE script (the latest), read from the Postgres script adherence rows; quotes from the conversation are never selected.',
      'The conversations behind a step are not linked yet: the Records list has no step filter (#879 PR 5).',
    ],
  },
  // «Голос клиента» (#848): Pro и выше — решение владельца 28.09. Рычаг —
  // флаг, и проверяет его сервер, а не только навигация. Экран «Возражения»
  // (`analytics.objection_reasons`) остаётся во всех пакетах по своему входу
  // из карточки Quality; вкладка «Продажи» раздела показывает его же.
  {
    id: 'analytics.voice_of_customer',
    title: 'Voice of Customer',
    summary:
      "One section with what customers say across the conversations of a period up to a quarter, split by who acts on it. Sales: summary cards naming the top objection, decline reason, unanswered question and theme (the row heard in the most conversations, at least 5), then one section at a time — Objections (the Objections screen), Declines, Questions and Themes — each list showing its first five rows with the rest on request. Marketing: competitors named in conversations (merged through the client glossary, the same counts as the Highlights tab), how they were spoken of and in what context; pricing, demand and trend signals; the needs customers named. Product and service: product feedback at every level from incident to praise, and supply and regulation signals. Site and content: summary cards naming the question asked most often, the top first-minute question and the theme whose conversations most often left a question without a full answer, then one section at a time — Frequent questions, First minute, Themes and FAQ draft (a draft FAQ for your website built on request), each list showing its first five rows. With no theme in the set, neither tab shows a themes section or card; the button next to the tabs reads \"Set themes\". Customer questions and decline reasons, and conversation themes from the organization's own set, are described in their own entries. On the Marketing and Product and service tabs every row reads \"in K of N conversations\" with the change against the previous period of the same length, opens to the most recent conversations behind it (up to 20), and says in words when fewer than 5 conversations stand behind it. Over time: every row of a block counted by a closed code or a merged name — objections, decline reasons, question subjects, themes, competitors, market signals and product feedback — carries a line of its share across the period, by day up to 31 days and by week beyond, with a gap where the period has no data rather than a zero; an open row shows the same as bars, where a bar narrows the row's conversations to that day or week, and switches to the last 12 months by month within the organization's retention, where a month offers to show the whole page for it. Objections have the line, and opened the same bars and last 12 months, in the Objections section of Sales, market view only — not on the separate Objections screen; there a bar narrows the objection's customer quotes. Questions without an answer, first-minute questions and needs have no line. A summary card shows its row's line once a block of the tab — its section or What changed — has loaded it for the period with the row on screen, loading none of its own (the team view of objections loads none), and drops it when its numbers come back changed while that block is closed, until the block is opened again; the unanswered-question, first-minute and theme-with-unanswered-questions cards have none. A What changed view — on Marketing and Product and service a switch above the blocks, on Sales always the last section, on Site and content while customer questions are on — lists the rows of all the tab's blocks that carry a mark, strongest shift first; objection reasons are among them, marked grew, fell or new by the same rule against the conversations with a recognized objection (the share their row shows) — new only when no earlier period with recognized objections held the reason and no earlier period, the previous one included, held an objection that could not be sorted into a reason; never gone, as only reasons heard in the period are listed; the reason's row in the Objections section of Sales is marked new by the same rule; a block whose count reached its limit is named instead of marked. Marks follow one rule on screen, in print and in the monthly email: grew or fell when both periods have at least 30 conversations, the larger at least 5 with the row, and the share moved by at least 3 percentage points and by at least half; new and gone only against earlier periods of the same length counted in full within retention. The lines come from a derived table of each analysis's codes and normalised keys (no conversation text, deleted with the record), filled for the whole retention of the organizations that had the section in October 2026 (for a later one it starts about a week before it gets the section) and checked nightly; a line that is still loading or failed never holds back the rows' numbers. Counting is by the closed fields of the analysis (feedback level, signal type, sentiment); free-text wordings are shown under each row, not grouped. Export, from one Export menu on every tab: a CSV of the tab, a printable report of the tab, and a list of customer wordings; on Sales these cover the Objections section — the objection reasons of the market view with their conversations, their share of the conversations with a recognized objection and the previous period, the customer quotes in the wordings, and in print the screen's lines on conversations with objections and the unrecognized share — and, while customer questions and decline reasons are on, the Declines and Questions sections (decline reasons, questions without an answer, frequent questions); on Sales and Site and content, while conversation themes are on, the themes block too (not under a theme filter); the team view of objections and the decline breakdowns, which name employees, are not exported. A monthly Voice of Customer email summarises the same numbers, with the top question subjects and decline reasons as counts only; a person who holds a narrow analytics access grant (`access.analytics_grants`) does not receive it, because the letter is chosen by role. Filters: period, projects, call category and, with conversation themes on, one conversation theme (see the conversation themes entry).",
    area: 'analytics',
    status: 'available',
    customerVisible: true,
    surfaces: ['saas_app', 'bitrix24_embedded', 'edge_api', 'workflows_api'],
    packages: proPlus(),
    flag: {
      key: 'enableVoiceOfCustomer',
      defaultValue: false,
      isImplemented: true,
      category: 'page',
      note: 'Pro and up (owner decision 28.09); the server refuses the API for an org without the flag, not only the navigation. Exception (#2816): below Pro, an org in its demo window (demo records and no real record yet) is answered 200 with demoOnly true and demo rows only on the read routes — GET /api/v1/analytics/voice (and its only= variants), /analytics/voice/series, the GET /analytics/voice/themes list, GET /api/v1/dashboard/voice, GET /api/v1/agents/objections/stream and the include=voice markers of GET /api/v1/analytics/config-events — and the screen opens with the demo under a «This is demo data» banner (a See plans link for a role with billing access, otherwise «ask the account owner»; in Bitrix24 always the latter). The objections stream is open to every plan, so there the opening applies to every call, with or without themeCode or marks=true (marks=true still needs the gate of the section): a locked org in its demo window gets demo rows alone and demoOnly true, any other org is answered as before. The config-events markers in such an answer belong to the demo alone: model and codes-version markers from the demo facts, none of the analysis-config, theme-set or profile edits (change journals of the customer, which the demo never writes). A flag switched off by an override never opens the demo, nor does an explicit includeDemo=no; outside the demo window it is the locked screen and 403 tier_not_eligible as before. Writes and paid calls stay 403 below Pro (theme set edits, the starter and weekly proposals, POST /analytics/voice/faq-draft), shown as «Available on {plan} and higher plans»; export is a read and stays.',
    },
    docs: [
      {
        label: 'Voice of Customer spec',
        path: 'docs/plans/2026-09-28-voice-of-customer-section.md',
      },
      {
        label: 'Voice of Customer over time spec',
        path: 'docs/plans/2026-10-02-voc-trends.md',
      },
      {
        label: 'Voice of Customer guide',
        path: 'apps/website/src/content/docs/en/docs/user-guide/voice-of-customer.md',
      },
    ],
    code: [
      {
        label: 'Voice of Customer contract',
        path: 'packages/api-types/src/schemas/voice-of-customer.ts',
      },
      {
        label: 'Voice of Customer facts contract',
        path: 'packages/api-types/src/schemas/voc-facts.ts',
      },
      {
        label: 'Voice of Customer aggregate',
        path: 'packages/core/src/voc/voice-of-customer.ts',
      },
      databaseRepository('Voice of Customer reads', 'VoiceOfCustomerRepository.ts'),
      databaseRepository('Voice of Customer facts writer', 'VocFactsRepository.ts'),
      databaseRepository(
        'Voice of Customer series and marks reads',
        'VocFactsReadRepository.ts'
      ),
      {
        label: 'Voice of Customer API',
        path: 'apps/edge-api/src/routes/voice-of-customer.ts',
      },
      {
        label: 'Voice of Customer series API',
        path: 'apps/edge-api/src/routes/voice-trends.ts',
      },
      {
        label: 'Nightly facts reconciliation',
        path: 'apps/workflows-api/src/scheduled/voc-facts-reconcile.ts',
      },
      {
        label: 'Tier and flag gate',
        path: 'apps/edge-api/src/utils/tier-feature-access.ts',
      },
      {
        label: 'Voice of Customer section',
        path: 'apps/ui-worker/src/components/analytics/voice/VoiceOfCustomerView.tsx',
      },
      {
        label: 'Voice of Customer route',
        path: 'apps/ui-worker/src/routes/analytics/voice.tsx',
      },
      {
        label: 'Row series: sparkline, bars and 12 months',
        path: 'apps/ui-worker/src/components/analytics/voice/VoiceRowTrend.tsx',
      },
      {
        label: 'Row series requests',
        path: 'apps/ui-worker/src/components/analytics/voice/useVoiceSeries.ts',
      },
      {
        label: 'Row series and What changed view model',
        path: 'apps/ui-worker/src/components/analytics/voice/voice-series-view-model.ts',
      },
      {
        label: 'What changed',
        path: 'apps/ui-worker/src/components/analytics/voice/VoiceChangesSection.tsx',
      },
      {
        label: 'Voice of Customer print report',
        path: 'apps/ui-worker/src/lib/print-report/voice.ts',
      },
      {
        label: 'Monthly Voice of Customer email (sweep job + send)',
        path: 'apps/workflows-api/src/digests/voice-digest.ts',
      },
      {
        label: 'Monthly Voice of Customer email renderer',
        path: 'apps/workflows-api/src/digests/voice-templates.ts',
      },
      {
        label: 'Monthly Voice of Customer email copy (en/ru/uk/es)',
        path: 'packages/core/src/i18n/voice-digest.ts',
      },
    ],
  },
  {
    id: 'analytics.conversation_themes',
    title: 'Conversation Themes',
    // #845, released in PR 7 (spec §8 p. 7): Pro and up, like the section.
    // Beta: on the release measurement the main theme and the multi-label
    // precision sit at the threshold (docs/eval/2026-10-10-conversation-themes-measurement.md).
    // Themes come only with customer questions — the same extraction step
    // assigns them (owner decision 10.10: the step is not decoupled).
    summary:
      "The organization's own set of conversation themes inside Voice of Customer — what a conversation is about (a model, leasing, delivery to regions), up to 30 in use, archived instead of deleted, separate from the call category and the subject of a question. New conversations get 1–3 themes from the set (or uncategorized) and an optional suggested new theme in the Voice of Customer step — the step that finds customer questions, so themes need customer questions and decline reasons switched on, and a conversation in which the step cannot tell which side is the customer gets no themes and is not counted; conversations analysed before the set had its first theme are not labelled afterwards. The Sales and Site and content tabs count them: each theme in K of N conversations (N — the conversations with themes assigned since the theme entered the set, returned from the archive or had its definition changed), the change against the previous period where the two are comparable, the share of conversations no theme fits, theme by call category, and per theme the average quality score and the declines among sales conversations (Sales) or the conversations with a question left without a full answer (Site and content); the themes block leaves in the tab's CSV and print export, except under a theme filter, which does not narrow it; on Site and content without customer questions the export is the whole themes block, unlabelled by a theme. A theme picker next to the Themes button narrows the whole Voice of Customer page to the conversations of one theme: every block of every tab (Sales, Site and content, Marketing, Product and service), in the portal and in Bitrix24, counts only that theme's conversations, the FAQ draft is built from their questions (the daily draft limit is shared, not split), the themes block itself keeps showing the whole set with the chosen theme highlighted, a line above the tab's summary says «K of N conversations in the period» with a Clear button, and a theme set inside the previous period has no comparison with it; an export under a theme says so in its file name and printed heading. An empty set can start from a proposal: one model call over the organization's last-90-days conversation topics and call categories (re-redacted; no minutes are charged) returns up to 25 themes (10–25 asked for, fewer kept after the checks) with conversation shares, which the owner or an administrator edits and approves. Once a week the suggested new themes of the last 30 days (re-redacted, at most 300 distinct phrases, one model call per organization, no minutes charged) are grouped into at most five candidates; a candidate reaching 10 conversations and 3 % of the period's conversations, not repeating a call category or a question subject and not already in the set is shown to the owner and administrators with its counts and up to three example conversations, to accept into the set or reject (silent for 90 days).",
    area: 'analytics',
    status: 'beta',
    customerVisible: true,
    surfaces: ['saas_app', 'bitrix24_embedded', 'edge_api', 'workflows_api'],
    packages: proPlus(),
    flag: {
      key: 'enableConversationThemes',
      defaultValue: false,
      isImplemented: true,
      category: 'beta',
      note: "Pro and up, inside Voice of Customer (spec 2026-10-02-conversation-themes.md §8 p. 7). Beta until the quality measurement passes with a margin (docs/eval/2026-10-10-conversation-themes-measurement.md: main theme below the 0.8 threshold in both runs, multi-label precision below it in one and on it in the other). An organization with the flag on and no active theme costs nothing: the step runs v1 and the weekly summary skips it. Themes are assigned only when ALL hold: the Voice of Customer extraction step itself runs for the record (its own gate: Voice of Customer and customer questions open, Pro and up), this flag is on for the organization, the organization has at least one active theme, and, for a record from a Uspacy installation, that installation's processing instruction is active or paused at 1.10 or later, the first to name themes — decided per installation, not per organization: a record from no installation or from another CRM is not gated by Uspacy, and an installation with no processing-state record covers nothing (workflows-api uspacy-themes-gate.ts themesAllowedForRecord over database uspacy-instruction-gate.ts, #1608; an active installation on an older instruction is offered reauthorization in Settings → Consent Management). Then the step asks for voice_extraction_v2 (theme_codes, theme_definition_versions, theme_status, suggested_theme) and stores theme_set_revision, the latest set change read in one D1 snapshot with the set (#2012): a theme row's N and K, and the theme filter, take a conversation only if that revision is not before the theme's base (a row without it — written before #2012 — by the moment it was written), so a conversation whose step read the set before an edit and was written after it is in neither; it also stores themes_prompt_revision, the revision of the theme part of the prompt (#2952): a row of the customer's set and «no theme» show previous-window numbers only when every non-demo assigned conversation of both windows was read with one revision (a row without the field — written before #2952 — counts as themes-v2, the only revision production ran before it: themes first shipped to production in v26.49.0 already on themes-v2), since a new revision moves the theme distribution over the same conversations; a theme part that does not parse still stores the paid answer, as uncategorized with theme_status degraded, and the analytics and the weekly summary exclude degraded rows (an absent field counts as degraded); otherwise, or when the flag or the theme set cannot be read, it runs v1 exactly as before. Off: every /analytics/voice/themes route answers 403 themes_disabled — its own code, after the role check and the Voice of Customer gate (whose refusals stay tier_not_eligible / feature_disabled, except that below Pro an org in its demo window (demo records and no real record yet) reads the GET /analytics/voice/themes list as demo rows with demoOnly true, #2816 — every theme write, the starter proposal and the weekly proposals stay refused) and the Themes panel is not rendered — on the web from /feature-flags, in embedded Bitrix24 from the session flags subset. The starter proposal (#845 PR 4, /analytics/voice/themes/bootstrap) sits behind the same flag and the same refusals: the routes are edits of the set (client, admin) and workflows-api re-checks the flag before the paid call. Uspacy does not refuse it: the history it reads, and the conversation count edge-api shows before it, leave out the conversations of every Uspacy installation of the organization, removed ones included, that is not active on instruction 1.10 or later (uspacyHistoryExclusions, #1608) — a new request over stored data is allowed only while the instruction itself is active; GET /analytics/voice/themes returns the connected one among them as uspacyExcludedPortals (a removed portal is left out of the requests too, but Settings cannot act on it; a failed read omits the field) and the Themes panel names it (#2031). The weekly proposals (#845 PR 5b, /analytics/voice/themes/proposals; cron on Mondays 04–06 UTC starting one ThemeProposalsWorkflow per organization, reconciliation of acceptance every 30 minutes) run only for organizations with this flag on, Voice of Customer open, an active theme and no auto-pause; the source and the denominator leave out the same Uspacy slice as the starter proposal, and the summary excludes degraded rows. The analytics (#845 PR 6, GET /analytics/voice?only=themes) sit behind the Voice of Customer gate and this flag: off — the answer carries the window bounds without a themes key, nothing is read, and the tabs draw no block and send no request; the answer is never cached. It counts only conversations with theme_status assigned; the quality cut needs aggregates.quality, the declines and unanswered-question cuts need customer questions open. The theme filter (#2098, themeCode on GET /analytics/voice, GET /agents/objections/stream and POST /analytics/voice/faq-draft) reads this flag only when themeCode is sent, after the Voice of Customer gate (the stream runs that gate first, having none of its own; the FAQ draft also its customer-questions gate): off — 403 themes_disabled; a code outside the active set — 400 theme_filter_invalid; otherwise every block counts only the theme's conversations (the theme row's K condition), the answer carries themeFilter { code, prevComparable, calls, windowCalls } and is never cached, and a theme based inside the previous window leaves that window unread (prevComparable false). The UI (#2229): the picker «All themes» sits in the section header beside the Themes button and is drawn only with the flag on and at least one theme above the threshold (or one already selected); it offers the themes of the set whose row has at least 10 conversations and at least 3 % of its N in the period (passesProposalThreshold, the threshold of a proposed theme), the selected one always stays. The choice is the address parameter voiceTheme (a theme code; any other shape is dropped), shared by all tabs, kept when coming back from a conversation (an embedded Bitrix24 tab link that does not name it resets it). The screen trusts the numbers only when the answer echoes the same code in themeFilter: no echo or another code — the tab shows no numbers and says the theme filter is not available (a whole stream is never passed off as the theme's), while the themes block, not narrowed, stays; an answer with prevComparable false draws no changes and the line says the theme was set N days ago (the row's since); 403 themes_disabled or 400 theme_filter_invalid on any request lifts the filter from the address with a message, together with the row and code it expanded (Site and content without questions narrows no request: there the set's list lifts a theme it does not hold active, and a themes answer closed by the server lifts it as themes_disabled); a FAQ draft is filed under the theme's revision (definition version and base), so a new definition hides a draft of the old conversations; a FAQ draft answered without the echo is marked as built without the theme filter. The themes block and its summary card are read without the filter (the card shows the chosen theme's row). Exports under a theme: the CSV and print file names get -theme-<code>, the printed report opens with the theme line, and without a comparable previous window there are no previous-period columns and no trends; the monthly Voice of Customer email is not filtered. Demo data (#2008): the demo seed writes a demo theme set (five themes in the demo's language, org_themes.is_demo, codes demo__…) and voice_extraction_v2 results of the demo conversations whose themes are read off their hand-made labels; the demo set is not the organization's set — never in the step's prompt for a real conversation, outside the 30-theme cap, the starter proposal and the weekly summary, not editable — and the analytics and the theme filter show it only with includeDemo yes; clearing the demo removes it with its journal. An organization seeded before #2008 gets it at the next demo reseed or swap.",
    },
    docs: [
      {
        label: 'Conversation themes spec',
        path: 'docs/plans/2026-10-02-conversation-themes.md',
      },
      {
        label: 'Release quality measurement',
        path: 'docs/eval/2026-10-10-conversation-themes-measurement.md',
      },
      {
        label: 'Voice of Customer guide',
        path: 'apps/website/src/content/docs/en/docs/user-guide/voice-of-customer.md',
      },
    ],
    code: [
      { label: 'Theme-set API', path: 'apps/edge-api/src/routes/voice-themes.ts' },
      databaseRepository('Theme set', 'OrgThemesRepository.ts'),
      {
        label: 'Theme key, code and overlap helpers',
        path: 'packages/core/src/voc/theme-key.ts',
      },
      {
        label: 'Theme-set contract',
        path: 'packages/api-types/src/schemas/org-themes.ts',
      },
      {
        label:
          'Theme assignment in the voice-extraction step (prompt block, schema, normalization)',
        path: 'apps/workflows-api/src/workers/voice-extraction/themes.ts',
      },
      {
        label: 'Stored step result v1/v2 contract',
        path: 'packages/api-types/src/schemas/voice-extraction.ts',
      },
      {
        label: 'Theme-set API client',
        path: 'apps/ui-worker/src/lib/api/domains/voice-themes.api.ts',
      },
      {
        label: 'Theme-set panel',
        path: 'apps/ui-worker/src/components/analytics/voice/themes/VoiceThemesSheet.tsx',
      },
      databaseRepository(
        'Theme analytics (coverage, rows, cuts)',
        'VoiceThemeAnalyticsRepository.ts'
      ),
      {
        label: 'Theme analytics gates (block, quality cut)',
        path: 'apps/edge-api/src/utils/voice-themes-analytics-gate.ts',
      },
      {
        label: 'Theme filter door (themeCode on three routes)',
        path: 'apps/edge-api/src/utils/voice-theme-filter.ts',
      },
      {
        label: 'Theme filter picker (Voice of Customer header)',
        path: 'apps/ui-worker/src/components/analytics/voice/VoiceThemeFilterPicker.tsx',
      },
      {
        label: 'Theme analytics block on the Sales and Site and content tabs',
        path: 'apps/ui-worker/src/components/analytics/voice/VoiceThemesPanel.tsx',
      },
      {
        label: 'Theme tables migration',
        path: 'apps/ops-tools/src/migrations/d1/202610082305_org_themes.sql',
      },
      {
        label: 'Starter proposal API (state, start, regenerate, approve)',
        path: 'apps/edge-api/src/routes/voice-themes-bootstrap.ts',
      },
      {
        label: 'Starter proposal generation (claim, ceiling, model call)',
        path: 'apps/workflows-api/src/workers/theme-bootstrap/run.ts',
      },
      {
        label: 'Starter proposal prompt and answer minimization',
        path: 'apps/workflows-api/src/workers/theme-bootstrap/prompt.ts',
      },
      {
        label: 'Starter proposal pairs, selection and shares',
        path: 'packages/core/src/voc/theme-bootstrap.ts',
      },
      databaseRepository('Starter proposal attempt', 'OrgThemeBootstrapsRepository.ts'),
      databaseRepository(
        'Starter proposal history',
        'ThemeBootstrapHistoryRepository.ts'
      ),
      {
        label: 'Starter proposal contract',
        path: 'packages/api-types/src/schemas/org-theme-bootstrap.ts',
      },
      {
        label: 'Starter proposal panel',
        path: 'apps/ui-worker/src/components/analytics/voice/themes/VoiceThemeBootstrap.tsx',
      },
      {
        label: 'Weekly proposals API (list, accept, reject)',
        path: 'apps/edge-api/src/routes/voice-theme-proposals.ts',
      },
      {
        label:
          'Weekly proposals run (gate, budget guard, model call, threshold, recompute)',
        path: 'apps/workflows-api/src/workers/theme-proposals/run.ts',
      },
      {
        label: 'Weekly proposals cron (enqueue, cleanup)',
        path: 'apps/workflows-api/src/scheduled/theme-proposals-weekly.ts',
      },
      {
        label: 'Proposal acceptance across D1 and Postgres, and its reconciliation',
        path: 'packages/database/src/services/theme-proposal-accept.ts',
      },
      databaseRepository('Weekly proposals', 'OrgThemeProposalsRepository.ts'),
      {
        label: 'Weekly proposals contract',
        path: 'packages/api-types/src/schemas/org-theme-proposals.ts',
      },
      {
        label: 'Weekly proposals panel',
        path: 'apps/ui-worker/src/components/analytics/voice/themes/VoiceThemeProposals.tsx',
      },
    ],
  },
  {
    id: 'search.global',
    title: 'Global Search',
    summary:
      'Search API and command-palette/search surfaces for finding product entities from the app shell. Includes search by meaning across calls and chats; how its search vectors are processed and kept is set out in the Privacy Policy and the DPA.',
    area: 'search',
    status: 'available',
    customerVisible: true,
    surfaces: ['saas_app', 'edge_api'],
    packages: includedEverywhere(),
    code: [
      { label: 'Search API', path: 'apps/edge-api/src/routes/search.ts' },
      {
        label: 'Sidebar command palette trigger',
        path: 'apps/ui-worker/src/components/layout/AppSidebar.tsx',
      },
      databaseRepository('Search repository', 'SearchRepository.ts'),
      {
        label: 'Search embeddings (redaction, vector metadata)',
        path: 'apps/workflows-api/src/workers/transcribe/embeddings.ts',
      },
      {
        label: 'Vector purge after record deletion',
        path: 'apps/workflows-api/src/scheduled/record-vector-purge.ts',
      },
    ],
    docs: [
      {
        label: 'Privacy Policy - search vectors (data we process, flow, sub-processors)',
        path: 'apps/website/src/pages/[lang]/legal/privacy.astro',
      },
      {
        label: 'DPA - semantic search processing, data categories, retention',
        path: 'apps/website/src/pages/[lang]/legal/dpa.astro',
      },
      {
        label: 'Sub-processors - OpenAI embeddings, Cloudflare Vectorize',
        path: 'apps/website/src/pages/[lang]/legal/sub-processors.astro',
      },
    ],
  },
  {
    id: 'alerts.rules',
    title: 'Alerts',
    summary:
      "Business and system alert rules, events, and resolution flow. A triggered alert's recipients are resolved from each org role's default plus each user's personal category subscription (RBAC-gated); above an org-configurable severity threshold (critical/high by default) it delivers instantly through the recipient's own opt-in channels (email, personal Telegram, personal webhook), otherwise it is held for the daily digest. A rule condition can read the organization's own call criteria (triggered, score or list value), built-in criteria included — the gallery templates on call recording disclosure and on the employee's profanity are built on them, and a rule on a paused criterion does not fire; an answer counts only when it is applicable, confident, given for this processing of the conversation and for the criterion's current wording — otherwise the rule does not fire. For such a condition the in-app alert card shows the criterion's own quote from the answer that raised the alert, to people who can open that call and its criteria, or 'No quote: event not found' when what the criterion looks for was not found or its answer has no evidence; notifications never carry it. For a rule on the attention flag the card shows the moment of the conversation the flag rests on — time, speaker, label and quote — when the analysis has pinned the flag to one moment and found its quote in the conversation, to people who can open that call; notifications never carry it either. Independently, a project-level team channel — a Telegram group, the org's own connected Bitrix24 group chat, or an org-configured webhook — can receive a reduced alert summary (rule, severity, call category, record link) with no transcript quote or caller name/phone.",
    area: 'automation',
    status: 'available',
    customerVisible: true,
    surfaces: ['saas_app', 'edge_api', 'workflows_api'],
    // 2026-07-18 owner decision: every implemented feature ships enabled in
    // every package — alerts included from Free up.
    packages: includedEverywhere(),
    flag: {
      key: 'enableAlerts',
      defaultValue: true,
      isImplemented: true,
      category: 'page',
      note: 'Controls alerts page visibility',
    },
    limitKeys: ['max_alert_rules'],
    code: [
      uiDomainClient('Alerts domain client', 'alerts.api.ts'),
      databaseRepository('Alert repository', 'AlertRepository.ts'),
      {
        label: 'Recipient resolution + delivery',
        path: 'apps/workflows-api/src/workflows/record-pipeline/steps/alert-step.ts',
      },
      {
        label: 'Recipient cascade (role default → personal subscription → RBAC)',
        path: 'packages/database/src/services/alert-recipients.ts',
      },
      {
        label: 'Instant vs. daily-digest severity routing',
        path: 'packages/alerts/src/routing.ts',
      },
      {
        label: 'Reduced team-channel card (Telegram/Bitrix24/webhook)',
        path: 'packages/alerts/src/delivery-templates/team-chat-message.ts',
      },
      {
        label:
          'Team channel settings UI (email/Telegram/webhook/Bitrix24 chat + severity threshold)',
        path: 'apps/ui-worker/src/routes/projects.$projectId.settings/components/AlertChannelsTab.tsx',
      },
    ],
  },
  {
    id: 'incidents.management',
    title: 'Incident Management',
    summary:
      'Incident CRUD, resolution, and breach-notification workflow tied to records and analyses.',
    area: 'compliance',
    status: 'available',
    customerVisible: true,
    surfaces: ['edge_api', 'saas_app'],
    packages: includedEverywhere(),
    code: [
      { label: 'Incident API', path: 'apps/edge-api/src/routes/incidents.ts' },
      uiDomainClient('Incidents domain client', 'incidents.api.ts'),
      { label: 'D1 incident schema', path: 'packages/database/src/schema/d1/state.ts' },
      databaseRepository('Incident repository', 'IncidentRepository.ts'),
    ],
  },
  {
    id: 'alerts.system_health_events',
    title: 'System Health Events',
    summary:
      'System alert events, queue backlog checks, provider-health alerts, and admin alert fan-out.',
    area: 'admin',
    status: 'available',
    customerVisible: false,
    surfaces: ['admin_console', 'edge_api', 'workflows_api'],
    packages: packageAvailability('internal'),
    code: [
      {
        label: 'System health check',
        path: 'apps/workflows-api/src/scheduled/system-health-check.ts',
      },
      {
        label: 'Queue backlog check',
        path: 'apps/workflows-api/src/scheduled/queue-backlog-check.ts',
      },
      { label: 'Health API', path: 'apps/edge-api/src/routes/health-system.ts' },
      uiDomainClient('Admin domain client', 'admin.api.ts'),
      databaseRepository('Alert repository', 'AlertRepository.ts'),
    ],
  },
  {
    id: 'agents.performance_leaderboard',
    // The id is load-bearing (flags, docs cross-references) and stays; the
    // customer-facing wording does not. Contest framing was retired 2026-08-12
    // because AI Act Art. 3(12) makes how we describe a system part of its
    // intended purpose, and Annex III 4(b) covers workforce performance
    // monitoring — see docs/legal/2026-08-11-ai-act-position.md §5.
    title: 'Call Quality By Team Member',
    summary:
      'Per-team-member call quality scores and trends, the individual agent view, and the APIs behind them. Presentation is a table of scores, not standings: no ranks, no medals.',
    area: 'coaching',
    status: 'available',
    customerVisible: true,
    surfaces: ['saas_app', 'edge_api'],
    packages: includedEverywhere(),
    code: [
      {
        label: 'Agent performance API',
        path: 'apps/edge-api/src/routes/agents/performance/index.ts',
      },
      uiDomainClient('Agents domain client', 'agents.api.ts'),
      {
        label: 'Agent detail UI',
        path: 'apps/ui-worker/src/routes/agents_.$agentId.tsx',
      },
      { label: 'Competition UI', path: 'apps/ui-worker/src/routes/competitions.tsx' },
      databaseRepository('Analytics repository', 'AnalyticsRepository.ts'),
    ],
  },
  {
    id: 'coaching.competitions',
    title: 'Agent Competitions (withdrawn)',
    summary:
      'Withdrawn 2026-08-12 and hidden on every surface. Was a teaser: mock leaderboard/competition data behind a Coming Soon overlay, no backend. Not a roadmap item — reviving it requires revisiting the AI Act position first, because ranking employees against each other is what that position undertakes not to build.',
    area: 'coaching',
    status: 'prototype',
    customerVisible: false,
    surfaces: ['saas_app'],
    // Not included in any package. Tier defaults derive from `packages`, so
    // leaving this at `includedEverywhere()` would have re-lit the flag through
    // the tier layer — the catalog invariant test caught exactly that.
    packages: unavailableEverywhere(),
    flag: {
      key: 'enableCompetitions',
      defaultValue: false,
      isImplemented: false,
      category: 'page',
      note: 'Hidden everywhere (2026-08-12 owner decision, supersedes the 2026-07-18 default-on). BOTH switches are set on purpose: `defaultValue: false` makes the platform default off, and `isImplemented: false` forces it off through the resolver even where an org row already says true — orgs have been able to enable this since July, and a default alone would leave the page live for them. A default is a preference; this needs to be a guarantee. `isImplemented: false` is also simply accurate: the page is mock data behind a Coming Soon overlay, with no backend. Reason for hiding: the page advertises cross-rep leaderboards and competitions, which docs/legal/2026-08-11-ai-act-position.md §5.2 lists under "do not build" — and under AI Act Art. 3(12) an advertised intention is part of the intended purpose, so a "Coming Soon" page is not a neutral placeholder. The flag stays in the registry so the feature can be revived deliberately rather than by accident.',
    },
    code: [uiDomainClient('Agents domain client', 'agents.api.ts')],
  },
  {
    id: 'automation.ai_tasks',
    title: 'AI Tasks (not available)',
    summary:
      'Hidden until tasks are derived from real analyses and their actions are connected end to end. The route contains only an honest unavailable state.',
    area: 'automation',
    status: 'prototype',
    customerVisible: false,
    surfaces: ['saas_app'],
    packages: unavailableEverywhere(),
    flag: {
      key: 'enableAITasks',
      defaultValue: false,
      isImplemented: false,
      category: 'page',
      note: 'Hidden everywhere from 2026-08-31 until #172 has a real task source and working actions. defaultValue:false removes the platform default; isImplemented:false forces old or future org overrides to false. The direct route remains an honest unavailable state with no fabricated tasks or actions.',
    },
    code: [
      {
        label: 'UI route',
        path: 'apps/ui-worker/src/routes/analytics/ai-tasks.tsx',
      },
    ],
  },
  {
    id: 'feedback.ai_feedback',
    title: 'AI Feedback',
    summary: 'Feedback and voting loop for AI-generated analysis quality.',
    area: 'coaching',
    status: 'prototype',
    customerVisible: false,
    surfaces: ['saas_app'],
    packages: unavailableEverywhere(),
    flag: {
      key: 'enableAIFeedback',
      defaultValue: false,
      isImplemented: false,
      category: 'page',
      note: 'UI built but backend vote storage API not implemented yet - see issue #215',
    },
  },
  {
    id: 'knowledge_base.workspace',
    title: 'Knowledge Base',
    summary: 'Customer knowledge base for scripts, policies, and coaching context.',
    area: 'coaching',
    status: 'prototype',
    customerVisible: false,
    surfaces: ['saas_app'],
    packages: unavailableEverywhere(),
    flag: {
      key: 'enableKnowledgeBase',
      defaultValue: false,
      isImplemented: false,
      category: 'page',
      note: 'Static prototype, no backend CRUD implemented yet - see issue #216',
    },
  },
  {
    id: 'coaching.workspace',
    title: 'Coaching Workspace',
    summary:
      "Supervisor coaching notes, 1:1 coaching sessions, coaching stats, and AI-generated coaching insights. When a supervisor, admin or client leaves feedback on an employee's call - a coaching note, an annotation on the call, or the overall comment added to a review that had none - the employee is notified in the app and by email (their personal notices, the Collaboration category: the email can be turned off with their own switch «Personal emails», and unticking the category in their notification categories turns the whole notification off, the Bitrix24 one included), and an employee linked to a Bitrix24 portal user also gets a Bitrix24 notification in that portal; each links to the call at the moment the feedback is about (the Bitrix24 one opens Aelo inside the portal), and neither the email nor the portal notification carries feedback text. The employee's notes card links straight to the call.",
    area: 'coaching',
    status: 'available',
    customerVisible: true,
    surfaces: ['saas_app', 'bitrix24_embedded', 'edge_api'],
    // Open in every tier (owner decision 2026-10-20, #758): tier flag defaults
    // derive from packages, so 'included' switches the flag on for every tier;
    // an organization override can still switch it off.
    packages: includedEverywhere(),
    flag: {
      key: 'enableCoaching',
      defaultValue: true,
      isImplemented: true,
      category: 'page',
      note: "On in every tier since #758 (owner decision 2026-10-20; packages: included everywhere); an organization override can still switch it off. Notes/sessions routes, coaching API, and D1 schema are live. It is also the gate of the feedback notification: `coaching_feedback` (in-app, plus email by default) is sent only for an organization with this flag on. It is a personal notice (category `collaboration`, `PERSONAL_NOTIFICATION_CATEGORIES`, #2630) pushed by the shared `deliverWithUserChannels`. The employee's category filter is checked before the notice is created (`resolveAlertCategoryEnabled`, null = on for a personal category): unticked, there is no in-app notice, no push and no Bitrix24 notification. The email follows their own switch «Personal emails» (`personalEmailEnabled === false` stops it; null = on), independent of the general Email channel, and with no address on file it is skipped quietly; Telegram refuses the type (`TELEGRAM_DELIVERY`); neither a webhook nor the Bitrix24 group chat ever carries a personal notice. The email is throttled per author and conversation, for any kind of feedback (note, annotation or review): one email per author and conversation within 30 minutes, counting only emails actually sent (the rest are in-app only), and feedback from the platform owner (Aelo staff) notifies no one. The email names who left feedback, the conversation and the minute, with no feedback text and no customer data. In the Bitrix24 frame only the unified dashboard's coaching blocks and the agent dashboard cards ride this flag (#2058); the notes and sessions screens are portal-only. The notification email links to the web app. The Bitrix24 notification (#2608, `utils/coaching-feedback-portal.ts`) goes to the employee's linked portal user (`user_external_ids`, provider bitrix24; no link, no notification) through the organization's active Bitrix24 installation (`im.notify`); only a link read on that installation's portal is addressed (#2659), since after a move to another portal the same user id names someone else: it names who left feedback and the conversation, with no feedback text, and links to the app page in the portal (`/marketplace/app/<id>/`) with the record, minute and clip in `params[...]`, which the entry turns into the embedded dashboard opened on that call. It has its own 30-minute throttle per author, conversation and employee, separate from the email's; «Personal emails» stops only the email; unticking the Collaboration category stops it too, and the employee can also mute it in Bitrix24's own notification settings.",
    },
    docs: [
      {
        label: 'Notifications settings',
        path: 'apps/website/src/content/docs/en/docs/user-guide/settings.md',
      },
    ],
    code: [
      { label: 'Coaching API', path: 'apps/edge-api/src/routes/agents/coaching.ts' },
      uiDomainClient('Agents domain client', 'agents.api.ts'),
      {
        label: 'Coaching notes UI',
        path: 'apps/ui-worker/src/routes/coaching/notes.tsx',
      },
      {
        label: 'Coaching sessions UI',
        path: 'apps/ui-worker/src/routes/coaching/sessions.tsx',
      },
      {
        label: 'D1 coaching schema',
        path: 'packages/database/src/schema/d1/coaching.ts',
      },
      databaseRepository('Coaching repository', 'CoachingRepository.ts'),
    ],
  },
  {
    id: 'coaching.scripts',
    title: 'Scripts',
    summary:
      'Sales script library, script parsing, project-script assignment, step/phrase management, adherence scoring, and org-level category bindings that auto-select a script per detected call category.',
    area: 'coaching',
    status: 'available',
    customerVisible: true,
    surfaces: ['saas_app', 'edge_api', 'workflows_api'],
    // 2026-07-18 owner decision: every implemented feature ships enabled in
    // every package — scripts included from Free up.
    packages: includedEverywhere(),
    flag: {
      key: 'enableScripts',
      defaultValue: true,
      isImplemented: true,
      category: 'page',
      note: 'Controls scripts page visibility',
    },
    limitKeys: ['max_prompt_profiles'],
    docs: [
      {
        label: 'User guide - scripts',
        path: 'apps/website/src/content/docs/en/docs/user-guide/scripts.md',
      },
    ],
    code: [
      { label: 'Scripts API', path: 'apps/edge-api/src/routes/scripts/index.ts' },
      {
        label: 'Scripts request schemas (binding)',
        path: 'apps/edge-api/src/routes/scripts/schemas.ts',
      },
      {
        label: 'Script binding schema (api-types)',
        path: 'packages/api-types/src/schemas/scripts.ts',
      },
      uiDomainClient('Scripts domain client', 'scripts.api.ts'),
      {
        label: 'Script parse route',
        path: 'apps/workflows-api/src/routes/script-parse.ts',
      },
      {
        label: 'Adherence worker step',
        path: 'apps/workflows-api/src/workflows/record-pipeline/steps/script-adherence-step.ts',
      },
      {
        label: 'Script adherence resolver (binding cascade)',
        path: 'apps/workflows-api/src/workers/analyze/script-adherence.ts',
      },
      { label: 'Scripts UI', path: 'apps/ui-worker/src/routes/scripts.tsx' },
      {
        label: 'Category bindings matrix (UI)',
        path: 'apps/ui-worker/src/features/scripts/components/ScriptBindingMatrix.tsx',
      },
      databaseRepository('Script repository', 'ScriptRepository.ts'),
      databaseRepository('Script adherence repository', 'ScriptAdherenceRepository.ts'),
    ],
    notes: [
      'A script can be bound to one of the 10 LLM-detected call categories; at most one active bound script per (org, category) — rebinding an already-taken category returns 409.',
      'Script resolution during analysis: project default script (active only) → org category-bound script (by detected call category — calls and chats alike) → no script resolved (adherence scoring is skipped). The project ranks ABOVE the binding since #702 — see settings.project_pinned_analysis_config for why scripts have no pin of their own.',
      'Which steps the team does not complete, and why, is the Analytics screen described in analytics.script_steps; it follows this flag.',
    ],
  },
  {
    id: 'settings.project_pinned_analysis_config',
    title: 'Project Pinned Analysis Config',
    summary:
      "Per-project opt-out from org category-bound auto-selection: pin the project's own prompt profile so every analysis in that project uses it regardless of the detected call category. Scripts have no pin — a project's own script already outranks the binding (#702, #707).",
    area: 'settings',
    status: 'available',
    customerVisible: true,
    surfaces: ['saas_app', 'edge_api', 'workflows_api'],
    packages: includedEverywhere(),
    docs: [
      {
        label: 'User guide - evaluation criteria',
        path: 'apps/website/src/content/docs/en/docs/user-guide/evaluation-criteria.md',
      },
      {
        label: 'User guide - scripts',
        path: 'apps/website/src/content/docs/en/docs/user-guide/scripts.md',
      },
    ],
    code: [
      {
        label: 'Project PATCH pin validation (422)',
        path: 'apps/edge-api/src/routes/projects/core.ts',
      },
      {
        label: 'Project update schema (pin flags)',
        path: 'packages/api-types/src/schemas/projects.ts',
      },
      {
        label: 'Prompt profile resolver (pinned step)',
        path: 'apps/workflows-api/src/workers/analyze/prompt-profile.ts',
      },
      {
        label: 'Project settings UI - Quality tab',
        path: 'apps/ui-worker/src/routes/projects.$projectId.settings/components/QualityTab.tsx',
      },
      databaseRepository('Project repository', 'ProjectRepository.ts'),
    ],
    notes: [
      'One per-project flag: promptProfilePinned. Pinning (true) requires promptProfileId to already be set, otherwise the request is rejected (422); unpinning (false) is always allowed.',
      'There is no script pin. `scriptPinned` existed until #707 and was removed because it decided no outcome in any of the four combinations: since #702 an assigned script outranks the category binding whether pinned or not, and a deactivated script is skipped either way. Profiles keep their pin — there the binding still outranks the project profile, so the pin is what opts a project out.',
      'Full resolution order for PROFILES: explicit re-analyze override (always wins) → project pinned profile → org category-bound profile (by detected call category — calls and chats alike) → project profile (not pinned) → platform default profile.',
      'Full resolution order for SCRIPTS: project default script (active only) → org category-bound script (by detected call category) → no script, adherence scoring is skipped. Scripts deliberately rank the project ABOVE the binding, unlike profiles: a script binding may have been attached to a seeded template by migration 202608292100 rather than chosen by anyone, while assigning a script to a project is always a human act (#702).',
      'A pinned profile that is deleted falls through the cascade (binding → project profile → default). A pinned profile that is merely deactivated also falls through the pinned step, but if no category binding matches, the project-profile step can still resolve that same inactive profile — deactivating is not a reliable way to unpin; unpin explicitly instead.',
      '"Pinned but attachment gone" is a defined, safe state, not an error: if a pinned profile is later detached while promptProfilePinned stays on, the pin becomes inert and resolution falls through the cascade. Recommend unpinning explicitly rather than relying on this fallback.',
    ],
  },
  {
    id: 'settings.project_quality_weights',
    title: 'Project Quality Criteria Weights',
    summary:
      "Per-project weight (0–10) of each of the six quality criteria in the call's quality score. By default every criterion counts equally (the plain average of the criteria rated in the call); a project with its own weights scores every call — the card, the call list, every dashboard aggregate and manager rating, the public API, routing and alert rules, digests and the CRM score field — with one weighted formula.",
    area: 'settings',
    status: 'available',
    customerVisible: true,
    surfaces: ['saas_app', 'edge_api', 'workflows_api'],
    packages: includedEverywhere(),
    docs: [
      {
        label: 'Spec - quality weights (#874)',
        path: 'docs/plans/2026-10-05-quality-weights.md',
      },
      {
        label: 'User guide - quality score',
        path: 'apps/website/src/content/docs/en/docs/user-guide/quality.md',
      },
    ],
    code: [
      {
        label: 'Weights routes (GET/PUT/preview)',
        path: 'apps/edge-api/src/routes/projects/quality-weights.ts',
      },
      {
        label: 'Weights schemas',
        path: 'packages/api-types/src/schemas/projects.ts',
      },
      {
        label: 'Weighted formula (TS)',
        path: 'packages/core/src/utils/quality-scale.ts',
      },
      databaseRepository('Weighted formula (SQL twins)', 'quality-weights-readers.ts'),
      databaseRepository(
        'Project repository (weights write + audit)',
        'ProjectRepository.ts'
      ),
      {
        label: 'Project settings UI - Quality tab weights section',
        path: 'apps/ui-worker/src/routes/projects.$projectId.settings/components/QualityWeightsSection.tsx',
      },
      {
        label: 'Search quality bands pass after a save (#2256)',
        path: 'apps/workflows-api/src/workflows/embeddings-rebuild-workflow.ts',
      },
      {
        label: 'Quality bands pass start and catch-up (#2256, #2417)',
        path: 'apps/workflows-api/src/workflows/project-quality-bands.ts',
      },
    ],
    notes: [
      'Stored as projects.quality_weights (JSON, NULL = equal weights); an equal sheet is canonicalized to NULL on save. A stored criterion missing from the sheet takes the rounded mean of the present weights; a criterion the call did not rate is left out of the average and its weight is redistributed over the criteria that were rated.',
      'Reads recompute: the card, the call list, dashboards, ratings and the public API compute the score with the current weights at read time, so past calls are rescored on the next read. A call whose rated criteria all weigh 0 has no score (null), not 0.',
      'Events keep the weights they fired with (D5): the CRM score field, alerts, routing decisions and digests already sent are not rewritten. The analysis records the weights it was finalized with (qualityWeightsAtAnalysis), and the card says so when they differ from the current weights for that call.',
      "The one stored value that is recalculated (#2256): saving changed weights starts a background pass that rewrites the quality band in the metadata of the project's assistant search vectors (metadata only, no re-embedding); in a large project it can take several hours, and until it finishes the assistant's \"low quality\" filter can disagree with the call list near band boundaries. The confirmation dialog says so. Saving the same weights again starts no pass; a pass the save fails to start does not fail the save (nor delay its response), and a catch-up on the five-minute cron starts it from the save's audit row — and starts it again if it failed on the way, or, once it finished, if the vector sync queue gave up on a record the pass handed it as busy or failed (#2417; at most twice per save). A later save's pass takes over from an earlier one still running.",
      "Saving goes through a confirmation dialog with a 30-day preview (average now/new, calls below 60, calls that lose their score, band changes). Requires the project 'update' permission; every change writes an audit_logs entry (project.quality_weights.update) with the previous and next weights.",
    ],
  },
  {
    id: 'billing.workspace',
    title: 'Billing Workspace',
    summary: 'Subscription, usage, credits, and billing controls inside the app.',
    area: 'billing',
    status: 'available',
    customerVisible: true,
    surfaces: ['saas_app', 'edge_api'],
    packages: includedEverywhere(),
    flag: {
      key: 'enableBilling',
      defaultValue: true,
      isImplemented: true,
      category: 'page',
      note: 'Controls billing page visibility',
    },
    docs: [{ label: 'Stripe setup', path: 'docs/integrations/STRIPE_SETUP.md' }],
    code: [
      uiDomainClient('Billing domain client', 'billing.api.ts'),
      databaseRepository('Billing repository', 'billing/index.ts'),
    ],
  },
  {
    id: 'billing.signup_minute_boost',
    title: 'Signup Minute Boost (experiment)',
    summary:
      'A/B experiment on the free trial shape: cohort B receives 150 included minutes usable in the first 7 days after signup instead of the standard 100 per month. The hypothesis is that a larger, shorter allowance gets a new organization to its first analysis sooner, and that reaching an analysis is what predicts conversion. Cohort is derived deterministically from the organization id, so an org never changes side. Off everywhere by default: shipping it before a conversion baseline exists would make the result unreadable, because there would be nothing to compare against. Because the boost changes the included-minute grant, it is not only a pipeline concern: every customer-facing surface that states the grant — the remaining-minutes card, the backlog price quote, the pause banner and the public-ingest pre-check — resolves it through the same helper, so switching the flag on for one organization cannot make a screen contradict what the billing path actually does.',
    area: 'billing',
    status: 'prototype',
    customerVisible: false,
    surfaces: ['workflows_api', 'edge_api'],
    // unavailableEverywhere: tier defaults derive from packages, so 'included'
    // would silently switch the experiment on for every org in every tier.
    packages: unavailableEverywhere(),
    flag: {
      key: 'signupMinuteBoost',
      defaultValue: false,
      isImplemented: true,
      category: 'business',
      note: 'Off by design, not unfinished. isImplemented stays true so the flag can be switched on per org once a conversion baseline exists — enforceUnimplemented would otherwise force it false and make the experiment unrunnable without a code change. The boost changes the included-minute grant, which both the pipeline gate and the per-minute charge read, so it is resolved in one shared helper (resolveIncludedMinutesWithBoost) rather than at each call site: a divergence there would block a customer the billing path still charges. The flag itself resolves through one shared helper too (isSignupBoostEnabled in the same module), because edge-api reads it as well and two readings could disagree. Only an org-level override row turns it on — defaultValue is false and package defaults are unavailableEverywhere. Metrics to read before deciding: share of orgs reaching a first analysis by T+7, and free→paid conversion by T+30.',
    },
    code: [
      {
        label: 'Boost resolution',
        path: 'packages/pipelines/src/cost-tracking/signup-boost.ts',
      },
      {
        label: 'Grant resolution (shared by gate and charge)',
        path: 'packages/pipelines/src/cost-tracking/billing-cycle.ts',
      },
      {
        label: 'Boost context for the customer-facing routes',
        path: 'apps/edge-api/src/utils/signup-boost-context.ts',
      },
    ],
  },
  {
    id: 'billing.pro_trial',
    title: 'Pro Trial For Free',
    summary:
      'A free organization gets every Pro feature for 14 days, counted from its next real (non-demo) analysis rather than from signup, because connecting a CRM can take days; a free organization that analysed calls before the trial existed starts it with its next analysis. Only the tier that decides feature locks changes: included minutes, the price per minute and plan limits (projects, users, alert rules, criteria) stay on the free tier, and the AI assistant runs under a smaller trial ceiling. The trial is recorded once per organization in its settings and never repeats; when it ends the locks close again on the next request, the data written during the trial stays stored but is not shown behind the lock, and access grants already issued keep working. Spec: docs/plans/2026-10-26-free-pro-trial.md (#2816).',
    area: 'billing',
    status: 'available',
    // Not customer-visible: the flag is on for every package (below), so a row
    // of the customer package matrix (pricing, Starlight) would claim the trial
    // on Business and Scale. The internal FEATURE_MATRIX lists it anyway — it
    // shows flags, not offers. Pricing, terms and the Plan & Billing docs
    // describe the trial by hand.
    customerVisible: false,
    surfaces: ['workflows_api', 'edge_api', 'saas_app', 'bitrix24_embedded'],
    // includedEverywhere, not free-only: a feature included on some tiers is a
    // tier gate (`TIER_FEATURE_GATES`), and a floor of `free` with nothing above
    // it means nothing. The start checks the real tier itself — Starter and the
    // paid tiers never get a trial (owner decision 26.10).
    packages: includedEverywhere(),
    flag: {
      key: 'enableProTrial',
      defaultValue: true,
      isImplemented: true,
      category: 'business',
      platformOwnerOnly: true,
      note: "On for every free organization since #2816 delivery step 4 (the trial is shown in the product and described on pricing, terms and docs). An org-level override row set by the platform owner to false stops new trials for that organization. The flag decides only whether a NEW trial starts: the pipeline starts it on a free organization's first non-demo analysis that is not a replay, the synthetic canary or a re-analysis of a record already analyzed (outcome `reanalysis`: another analysis row of the record exists — a retry of a failed first analysis does start it), before any step that reads a feature lock; while the connected Uspacy installation is active on a processing instruction before 1.20 (`USPACY_INSTRUCTION_PURPOSES.proTrial`) the start waits for the current one (outcome `processing_instruction_outdated`, AE warn `[ProTrial] Start waits for the current processing instruction`, the CRM in `extra.vendor`). Switching the flag off does not end trials already started — the promised 14 days would vanish mid-way — it only stops new ones. Starter never gets a trial (owner decision 26.10). While a trial runs, every feature lock — the flag ladder defaults and the tier floor, in edge-api and workflows-api, the public API included — reads the tier as Pro (`gateTier`); billing, minutes, prices, plan limits and the criteria-trial ceilings read the real tier. The AI assistant on a trial is capped at 3 questions a minute, 15 an hour and 30 a day (paid tiers 10 / 120 / 300), and GET /assistant/quota counts against that cap. A read the trial opened is sent uncacheable, so it does not outlive the trial; a trial ends on the first request after endsAt — a tier-gated flag of a free organization is never served from the per-isolate flag cache (paid organizations keep the 60-second cache), so neither the start nor the end waits for it. The trial key survives every settings write: whole-blob writes keep the stored `proTrial` inside the SQL statement, so a writer that read settings before the trial started cannot erase it and open a second one. Product signals in Analytics Engine at warn: `[ProTrial] Started` (org, start, end — the end is fixed at start, so there is no separate end event) and `[ProTrial] Converted to paid` (phase during / within_30d / later) when an organization first moves into an active paid subscription — from the Stripe webhooks or the checkout repair path; a failed (`incomplete`) payment is not a conversion.",
    },
    code: [
      { label: 'Trial state and lock tier', path: 'packages/core/src/pro-trial.ts' },
      {
        label: 'Lock tier for edge-api gates',
        path: 'apps/edge-api/src/utils/tier-feature-access.ts',
      },
      {
        label: 'Trial start in the record pipeline',
        path: 'apps/workflows-api/src/workflows/record-pipeline/steps/pro-trial-step.ts',
      },
      {
        label: 'Assistant trial ceiling',
        path: 'apps/workflows-api/src/assistant/quota.ts',
      },
      {
        label: 'Conversion signal',
        path: 'apps/edge-api/src/utils/pro-trial-conversion.ts',
      },
      {
        label: 'Trial view for GET /feature-flags and the embedded session',
        path: 'apps/edge-api/src/utils/feature-flag-resolver.ts',
      },
      {
        label: 'Trial banner (web and CRM iframe)',
        path: 'apps/ui-worker/src/components/layout/ProTrialBanner.tsx',
      },
      {
        label: 'Trial line on a lock screen',
        path: 'apps/ui-worker/src/components/ui/ProTrialLockNote.tsx',
      },
    ],
  },
  {
    id: 'billing.subscriptions_checkout',
    title: 'Stripe Subscriptions And Checkout',
    summary:
      'Stripe checkout, customer portal, webhooks, subscription status, cancel/reactivate, and embedded billing safeguards.',
    area: 'billing',
    status: 'available',
    customerVisible: true,
    surfaces: ['saas_app', 'edge_api'],
    packages: includedEverywhere(),
    docs: [{ label: 'Stripe setup', path: 'docs/integrations/STRIPE_SETUP.md' }],
    code: [
      { label: 'Stripe routes', path: 'apps/edge-api/src/routes/stripe/index.ts' },
      uiDomainClient('Billing domain client', 'billing.api.ts'),
      { label: 'Stripe package', path: 'packages/stripe/src/index.ts' },
      {
        label: 'Billing UI',
        path: 'apps/ui-worker/src/components/billing/BillingContent.tsx',
      },
      databaseRepository('Subscription repository', 'billing/SubscriptionRepository.ts'),
      databaseRepository(
        'Stripe webhook event repository',
        'billing/StripeWebhookEventRepository.ts'
      ),
    ],
  },
  {
    id: 'billing.budgets_credits_usage',
    title: 'Budgets, Credits, And Usage Charges',
    summary:
      'The organization owner (client role) sets a monthly budget limit and sees what remains of it. The limit raises alerts at fixed shares of it (50, 80, 95 and 100 %); a single charge that crosses several shares raises the highest one only. It refuses a bulk CRM import that would cross it, but does not stop ordinary call processing. The owner also buys prepaid credits through Stripe Checkout and can turn on automatic wallet top-ups (Enterprise is billed under its agreement instead). In prepaid mode each processed call or chat is charged against the included minutes first, then bonus minutes, then the wallet; in postpaid billing (Enterprise) calls and chats are charged at their flat rates. Per-minute and per-chat rates are set by Aelo; billing screens do not show customers provider costs or margin.',
    area: 'billing',
    status: 'available',
    customerVisible: true,
    surfaces: ['saas_app', 'edge_api', 'workflows_api', 'admin_console'],
    packages: packageAvailability('included', { enterprise: 'custom' }),
    code: [
      { label: 'Billing router', path: 'apps/edge-api/src/routes/billing/index.ts' },
      uiDomainClient('Billing domain client', 'billing.api.ts'),
      {
        label: 'Pipeline charges',
        path: 'packages/pipelines/src/cost-tracking/per-minute-charge.ts',
      },
      {
        label: 'Billing repositories',
        path: 'packages/database/src/repositories/billing/index.ts',
      },
      databaseRepository(
        'Credit purchase repository',
        'billing/CreditPurchaseRepository.ts'
      ),
      databaseRepository('Provider costs repository', 'ProviderCostsRepository.ts'),
    ],
    notes: [
      "A chat draws half a minute (CHAT_GRANT_MINUTES) from the plan's included-minute grant in prepaid mode, the same pool a call minute comes from — included minutes first, then the one-time bonus, then the wallet. Once the grant is exhausted, only the uncovered share of the chat is charged, at the plan's flat per-chat rate rather than its per-minute rate; a chat fully covered by the grant costs nothing extra. In postpaid billing mode a chat is always charged the flat per-chat rate, with no grant step.",
    ],
  },
  {
    id: 'billing.tier_pricing_admin',
    title: 'Tier Pricing Admin',
    summary:
      'Platform-owner pricing administration for tier rates, per-minute pricing, credits, and org-specific billing controls.',
    area: 'billing',
    status: 'available',
    customerVisible: false,
    surfaces: ['admin_console', 'edge_api'],
    packages: packageAvailability('internal'),
    code: [
      {
        label: 'Admin tier pricing API',
        path: 'apps/edge-api/src/routes/admin/tier-pricing.ts',
      },
      uiDomainClient('Billing domain client', 'billing.api.ts'),
      {
        label: 'Admin pricing UI',
        path: 'apps/ui-worker/src/routes/admin/-pricing.lazy.tsx',
      },
      {
        label: 'Postgres pricing schema',
        path: 'packages/database/src/schema/postgres/index.ts',
      },
    ],
  },
  {
    id: 'sharing.record_links',
    title: 'Share Links',
    summary: 'Shareable links for records, analysis, or reports.',
    area: 'sharing',
    status: 'planned',
    customerVisible: false,
    surfaces: ['saas_app', 'edge_api'],
    packages: unavailableEverywhere(),
    flag: {
      key: 'enableShareLinks',
      defaultValue: false,
      isImplemented: false,
      category: 'page',
      note: 'Feature not yet built',
    },
  },
  {
    id: 'analytics.advanced',
    title: 'Advanced Analytics',
    summary:
      'Advanced analytics beyond the baseline dashboard and call statistics views.',
    area: 'analytics',
    status: 'planned',
    customerVisible: false,
    surfaces: ['saas_app', 'edge_api'],
    packages: proPlus(),
    flag: {
      key: 'enableAdvancedAnalytics',
      defaultValue: false,
      isImplemented: false,
      category: 'business',
      note: 'Not connected to backend logic',
    },
  },
  {
    id: 'operations.bulk_actions',
    title: 'Bulk Operations',
    summary: 'Bulk record and workflow actions for larger teams.',
    area: 'data',
    status: 'planned',
    customerVisible: false,
    surfaces: ['saas_app', 'edge_api'],
    packages: proPlus(),
    flag: {
      key: 'enableBulkOperations',
      defaultValue: false,
      isImplemented: false,
      category: 'business',
      note: 'Not connected to UI logic',
    },
  },
  {
    id: 'data.export',
    title: 'Export',
    summary: 'Export controls for charts, analytics, and customer data views.',
    area: 'data',
    status: 'available',
    customerVisible: true,
    surfaces: ['saas_app'],
    // 2026-07-18 owner decision: every implemented feature ships enabled in
    // every package — export included from Free up.
    packages: includedEverywhere(),
    flag: {
      key: 'enableExport',
      defaultValue: true,
      isImplemented: true,
      category: 'business',
      note: 'Connected to ExportButton visibility',
    },
  },
  {
    id: 'settings.organization_week_start',
    title: 'Organization First Day of the Week',
    summary:
      "Organization setting for the first day of the week (Monday through Sunday; Monday when never set, so existing organizations see no change), changed in organization settings by a role holding organization.update (client, admin). For every member it decides where a week begins: the dashboards' Week period, its navigation, label and return to the current week; the weekly series on My Progress and in the Criteria report; and the weekly email digest, which goes out at 08:00 in the organization's time zone on that day and reports the calendar week that has just ended, trending against the week before.",
    area: 'settings',
    status: 'available',
    customerVisible: true,
    surfaces: ['saas_app', 'bitrix24_embedded', 'edge_api', 'workflows_api'],
    packages: includedEverywhere(),
    docs: [
      {
        label: 'User guide - settings',
        path: 'apps/website/src/content/docs/en/docs/user-guide/settings.md',
      },
      {
        label: 'User guide - dashboard',
        path: 'apps/website/src/content/docs/en/docs/user-guide/dashboard.md',
      },
    ],
    code: [
      {
        label: 'Week math (browser and workers)',
        path: 'packages/core/src/week-start.ts',
      },
      {
        label: 'Setting schema',
        path: 'packages/api-types/src/schemas/settings.ts',
      },
      {
        label: 'Org settings API (read and write)',
        path: 'apps/edge-api/src/routes/settings.ts',
      },
      {
        label: 'Organization settings UI',
        path: 'apps/ui-worker/src/routes/settings/components/OrganizationTab.tsx',
      },
      {
        label: 'Dashboard week period',
        path: 'apps/ui-worker/src/hooks/usePeriodNavigation.ts',
      },
      {
        label: 'Weekly digest send day and window',
        path: 'apps/workflows-api/src/digests/send.ts',
      },
    ],
  },
  {
    id: 'settings.service_status',
    title: 'Settings: Service Status',
    summary:
      "Settings → Organization → Service status (#1341): how the organization's own records move through processing, readable by the client and admin roles without contacting support. It shows the analysis mode (Automatic or Manual, read-only, with the system pause reason and moment), the queue now by status with the age of the oldest waiting record, processed / completed / stopped counts for a 7- or 30-day period, stage runs with average time, failures by stage with the last reason, stopped records by source (Bitrix24, Uspacy, other), and the ten latest stopped records with a per-row Retry button. A stopped record reaches the customer as a reason (no recording in the CRM, the recording did not arrive in time, the audio could not be downloaded, transcription / analysis / CRM delivery failed, a failure on our side), never as the raw pipeline text; that text goes to the platform owner only. The figures are the whole organization's, so a reader whose access is narrower than the organization (a supervisor, an agent, a project-scoped grant) is refused with 403, and demo records are left out. Numbers only read: every action is a separate button on its own screen — start waiting records (Records), change the mode (Organization), import (Data Source → Import), access requests (Team & Access), and the monthly budget and top-up (Plan & Billing) only for a role with billing rights. A block whose data failed to load says so instead of showing a zero.",
    area: 'settings',
    status: 'available',
    customerVisible: true,
    surfaces: ['saas_app', 'edge_api'],
    packages: includedEverywhere(),
    docs: [
      {
        label: 'User guide - settings (Service Status)',
        path: 'apps/website/src/content/docs/en/docs/user-guide/settings.md',
      },
    ],
    code: [
      {
        label: 'Service status API (GET /api/v1/dashboard/service)',
        path: 'apps/edge-api/src/routes/dashboard-service.ts',
      },
      {
        label: 'Service status schema',
        path: 'packages/api-types/src/schemas/service-status.ts',
      },
      {
        label: 'Failure reasons (no pipeline text to the customer)',
        path: 'packages/core/src/processing-error-reason.ts',
      },
      {
        label: 'Settings section',
        path: 'apps/ui-worker/src/routes/settings/components/ServiceSection.tsx',
      },
      {
        label: 'Settings sidebar (section, permission)',
        path: 'apps/ui-worker/src/routes/settings/components/SettingsSidebar.tsx',
      },
    ],
  },
  {
    id: 'settings.access_log',
    title: 'Settings: Access Log',
    summary:
      "Settings → Organization → Access log: who changed whose access in the organization, and when — member added or removed, role changed, project assignment, org structure, CRM account link, accounts merged, analytics access granted, changed or revoked. Newest first, with «show more», filtered by kind of event (members and roles, analytics access, projects, org structure). Visible to whoever sees the team list (client, admin), for the selected organization only. Each entry shows the action, the person it was about, the role before and after or the analytics preset, and who made the change; a person is named only while a member of the organization — the same name the team list shows — otherwise «former member»; Aelo staff outside the organization appear as «Aelo (platform)», and a change the system made (access withdrawn because its department or project went away) as «Aelo (automatically)». A change of several people (a project's supervisor list, a CRM user sync, an org-structure import, a department head) names up to ten of them, with the total. Events are kept for 365 days. Not the routing log of Analysis Setup → Distribution, which lists how incoming records were routed.",
    area: 'settings',
    status: 'available',
    customerVisible: true,
    // Not `bitrix24_embedded`: the embedded shell has no settings page (#1703).
    surfaces: ['saas_app', 'edge_api'],
    packages: includedEverywhere(),
    docs: [
      {
        label: 'User guide - settings (Access Log)',
        path: 'apps/website/src/content/docs/en/docs/user-guide/settings.md',
      },
    ],
    code: [
      {
        label: 'Access log API (GET /api/v1/access/events)',
        path: 'apps/edge-api/src/routes/access.ts',
      },
      {
        label: 'Access log schema and the closed action list',
        path: 'packages/api-types/src/schemas/access-log.ts',
      },
      {
        label: 'Journal page and people lookup',
        path: 'packages/database/src/repositories/AccessAuditRepository.ts',
      },
      {
        label: 'Settings section',
        path: 'apps/ui-worker/src/routes/settings/components/AccessLogSection.tsx',
      },
    ],
  },
  {
    id: 'settings.number_blocking',
    title: 'Number Blocking',
    summary:
      'Project-level controls for excluding caller numbers from analysis. UI prototype exists, but persistence and pipeline enforcement are not wired.',
    area: 'settings',
    status: 'prototype',
    customerVisible: false,
    surfaces: ['saas_app', 'workflows_api'],
    packages: unavailableEverywhere(),
    flag: {
      key: 'enableNumberBlocking',
      defaultValue: false,
      isImplemented: false,
      category: 'business',
      note: 'UI prototype only; no storage or pipeline skip enforcement',
    },
    code: [
      {
        label: 'Project overview settings UI',
        path: 'apps/ui-worker/src/routes/projects.$projectId.settings/components/OverviewTab.tsx',
      },
    ],
  },
  {
    id: 'reporting.scheduled_email_reports',
    title: 'Scheduled Email Reports',
    summary:
      'Project-level scheduled report controls. Current UI is a prototype and is separate from the implemented digest delivery pipeline.',
    area: 'reporting',
    status: 'prototype',
    customerVisible: false,
    surfaces: ['saas_app'],
    packages: unavailableEverywhere(),
    flag: {
      key: 'enableScheduledEmailReports',
      defaultValue: false,
      isImplemented: false,
      category: 'business',
      note: 'UI prototype only; implemented digests are tracked as notifications.email_digests',
    },
    code: [
      {
        label: 'Project overview settings UI',
        path: 'apps/ui-worker/src/routes/projects.$projectId.settings/components/OverviewTab.tsx',
      },
    ],
  },
  {
    id: 'integrations.webhooks',
    title: 'Outbound Webhooks',
    summary: 'Customer-configurable outbound webhooks for product events.',
    area: 'integrations',
    status: 'planned',
    customerVisible: false,
    surfaces: ['edge_api'],
    packages: proPlus(),
    flag: {
      key: 'enableWebhooks',
      defaultValue: false,
      isImplemented: false,
      category: 'business',
      note: 'Webhook infrastructure not built',
    },
    code: [
      databaseRepository('Outgoing webhook repository', 'OutgoingWebhookRepository.ts'),
    ],
  },
  {
    id: 'prompting.prompt_builder',
    title: 'Prompt Builder',
    summary:
      "Prompt profile editor and prompt preview tools. Profiles are organization-scoped; the preview may additionally be scoped to a project, and then it applies exactly what the runtime reads out of that project — the analysis language, the project's name and description.",
    area: 'prompting',
    status: 'available',
    customerVisible: true,
    surfaces: ['saas_app'],
    packages: includedEverywhere(),
    flag: {
      key: 'enablePromptBuilder',
      defaultValue: true,
      isImplemented: true,
      category: 'beta',
      note: 'Connected to sidebar + page visibility',
    },
    limitKeys: ['max_prompt_profiles'],
    code: [
      uiDomainClient('Settings domain client', 'settings.api.ts'),
      databaseRepository('Prompt profile repository', 'PromptProfileRepository.ts'),
    ],
  },
  // Контур «запись» выпущен PR 3b (18.09.2026, docs/plans/2026-09-11-assistant.md
  // §9.1): `isImplemented` и `customerVisible` переключены одним изменением
  // вместе с legal-раскрытием (privacy/DPA/sub-processors) — включение раньше
  // раскрытия было бы недокументированной обработкой, раскрытие раньше
  // включения — обещанием впереди кода. Контур «карточка CRM» (сделка, лид,
  // контакт, компания) выпущен PR 5b (25.09.2026) тем же порядком: запись в
  // `ASSISTANT_RELEASED_SCOPES` и legal одним изменением. Контур «вся база»
  // выпущен PR 7b (#765 Ф3) тем же порядком — с ним возможность `available`.
  {
    id: 'assistant.ai',
    title: 'AI Assistant',
    summary:
      'Grounded questions and answers about a call, about a CRM deal, lead, contact or company across its calls and chats, or about the analysed calls and chats you can see — narrowed by period, project, agent, type, category or customer — with quotes from the transcripts and playable timecodes.',
    area: 'automation',
    status: 'available',
    customerVisible: true,
    surfaces: ['saas_app', 'bitrix24_embedded'],
    packages: proPlus(),
    flag: {
      key: 'enableAIAssistant',
      defaultValue: false,
      isImplemented: true,
      category: 'business',
      // The platform's word, and only its (#2107): the owner's own choice is
      // `settings_json.assistantEnabled`. An org owner with `manage_settings`
      // could otherwise write this row through PUT/DELETE /feature-flags and
      // lift a platform switch-off.
      platformOwnerOnly: true,
      note: "Each scope released with its own legal disclosure: record (PR 3b), CRM entity — deal, lead, contact, company (PR 5b), whole base (PR 7b). The organization owner switches it off and on in Settings → Organization → AI assistant (#921, #2107): that choice is the organization setting `settings_json.assistantEnabled`, apart from this flag, which stays the platform's word (the platform admin screen is unchanged) — the assistant works only when the plan, the flag and the owner's choice all allow it, so the owner cannot lift a platform switch-off and an admin edit of the flag does not erase the owner's choice (`resolveOrgFlags`, step 5b); admins see the state",
    },
    docs: [
      { label: 'Assistant spec', path: 'docs/plans/2026-09-11-assistant.md' },
      {
        label: 'Whole-base UI and release',
        path: 'docs/plans/2026-09-30-assistant-org-ui-release.md',
      },
    ],
    code: [
      {
        label: 'assistant_turns migration',
        path: 'apps/ops-tools/src/migrations/postgres/202609120600_assistant_turns.sql',
      },
      uiDomainClient('Assistant domain client', 'assistant.api.ts'),
      databaseRepository('Assistant turn repository', 'AssistantTurnRepository.ts'),
    ],
  },
  // #766 «Состояние сделки», программа «Уровень сделки». Строилась по PR
  // (спека §12): PR 1 — методика продаж воронки, PR 2 — само состояние, PR 3 —
  // комментарий в CRM, PR 4 — очередь и карточка сделок, PR 5 — выпуск
  // (06.10.2026: `isImplemented` и `customerVisible` переключены одним
  // изменением вместе с раскрытием в legal и страницей Starlight — раскрытие
  // раньше включения было бы обещанием впереди кода, включение раньше
  // раскрытия — недокументированной обработкой). Тариф — Pro и выше (решение
  // владельца 03.10, R6): гейт строится из `packages` ниже.
  {
    id: 'deals.state_summary',
    title: 'Deal State Summary',
    summary:
      "A running state of every CRM deal or lead after each analysed conversation — status (at risk, needs attention, on track) with the chance to close, what changed, why, the next action with its due date and what to say at the next contact — recommended by the sales methodology of the deal's pipeline. In Aelo: a deal queue (deals and leads in three buckets with counts and CRM amounts, the reason for the status, the chance to close, the next action, its due date and the employee; closed deals counted apart) and a deal card (the current state, the history of states, the deal's conversations, «Open in CRM» and a one-click CRM task for the next action — in Uspacy a planned activity assigned to the deal's or lead's responsible employee, when Uspacy names one and the processing instruction is version 1.17 or later (otherwise Uspacy assigns it), only while the portal's processing is active); Uspacy deals show no amount. On request the deal card also writes a draft message to the client from the current state — one short follow-up in the project's analysis language, shown in Aelo to check, edit and copy, not stored and not sent to the CRM; it works on demo deals too and is limited to 60 drafts per user and 200 per organization in 24 hours. In Bitrix24: the Aelo tab of a deal or lead card shows the same deal card of that deal or lead (the state, the history, the one-click CRM task and the draft; no queue, no «Open in CRM») over a list of the card's conversations — calls and open-line chats, each opening its full report — inside the viewer's own access in Aelo; a deal's list also holds the conversations of the lead it grew from, and a task created from the tab carries no link to the Aelo app. In the CRM: after each new state a separate comment in the timeline of that deal or lead (Bitrix24, Uspacy; only the organization's active CRM, the one that receives call reports), only for conversations up to 7 days old, with its own organization switch in Analysis settings → CRM updates (on by default). The methodology is a setting of each pipeline (a Bitrix24 pipeline or a Uspacy funnel) on its own step, Analysis settings → Sales methodology, which the Deals screen also links to for an owner or an admin — a standard framework (BANT, SPIN, MEDDIC, Challenger, or BANT + SPIN by default) plus optional company rules (the product, approved answers to objection reasons, next-step rules and contact-pause norms per stage, prohibitions); methodologies belong to the CRM they were saved in — up to 100 pipelines of a connected CRM and up to 200 in all per CRM, those of pipelines since deleted in the CRM included (reset on the same step); with both Bitrix24 and Uspacy connected the step shows the Bitrix24 pipelines, and a Uspacy deal still follows its own funnel's methodology; leads follow the default, and so does a deal whose pipeline cannot be read from the CRM. A deal converted from a lead (in Uspacy, the linked lead Uspacy marks as converted) continues the lead's state as of the deal's first conversation, and its card links to the lead for a viewer who can open the lead's card. A new organization's demo data includes demo deals (no CRM link, no CRM task). Aelo does not charge for the state or the draft.",
    area: 'analytics',
    status: 'available',
    customerVisible: true,
    // The queue and the card are screens of the standalone app (sidebar,
    // command palette); in Bitrix24 the card lives in the Aelo tab of a deal or
    // lead (#745, `/api/v1/embedded/deals`, no queue there) and the CRM also
    // receives the comment and the task. `enableDealStateSummary` is deliberately
    // NOT in `EMBEDDED_SESSION_FLAG_KEYS`: the tab asks the server and reads its
    // 403 (tier or flag), the session's copy of the flag cannot tell them apart.
    surfaces: ['saas_app', 'bitrix24_embedded', 'edge_api', 'workflows_api'],
    // Pro and up, like the assistant on a deal (owner's decision 03.10, R6).
    packages: proPlus(),
    flag: {
      key: 'enableDealStateSummary',
      defaultValue: false,
      isImplemented: true,
      category: 'business',
      note: 'Pro and up (owner decision 03.10, R6), released 06.10 with the legal disclosure in the same change. Gates the deal-state step of the record pipeline and the comment in the CRM timeline, GET/PUT/DELETE /api/v1/crm/pipelines/:pipelineId/sales-methodology, GET /api/v1/crm/sales-methodology, the /api/v1/deals routes and their mirror /api/v1/embedded/deals for the Bitrix24 tab (no queue there; GET /deals/by-crm-entity is embedded-only) — all deal and methodology routes answer 403 tier_not_eligible below Pro or feature_disabled when the flag is off, except (#2816) the web READ routes of the demo opening: below Pro an org in its demo window (demo records and no real record yet) is answered 200 with demoOnly true and demo deals only by GET /api/v1/deals, GET /deals/:stateId and its /conversations, unless it asks includeDemo=no (the card of a real deal stays 403; a flag switched off by an override never opens the demo, the refusal stays 403 tier_not_eligible), while the client-message, CRM-task and methodology routes and the embedded mirror stay closed below Pro, and the screen shows the demo under a «This is demo data» banner with those actions disabled — the «Deals» menu item, command and screens, the «Sales methodology» step of Analysis settings (with its button on the Deals screen and its link on the Deal Stages tab) and the deal-state comment switch, which lives in Analysis settings → CRM updates (not on the Connection tab), and the draft message to the client (POST /api/v1/deals/:stateId/client-message, the card button; its provider cost is the unbilled cost type deal_client_message); for a CRM without field mapping (Uspacy) that step is there on every plan (its report rows), and only the comment row needs the deal state',
    },
    docs: [
      {
        label: 'Deal state guide',
        path: 'apps/website/src/content/docs/en/docs/user-guide/deals.md',
      },
      {
        label: 'Aelo tab in the Bitrix24 deal card spec',
        path: 'docs/plans/2026-10-17-bitrix24-deal-tab.md',
      },
      {
        label: 'Deal client message draft spec',
        path: 'docs/plans/2026-10-14-deal-client-message-draft.md',
      },
      {
        label: 'Deal state release plan',
        path: 'docs/plans/2026-10-06-deal-state-pr5-release.md',
      },
      { label: 'Deal state spec', path: 'docs/plans/2026-10-03-deal-state.md' },
      {
        label: 'Deal state step plan',
        path: 'docs/plans/2026-10-03-deal-state-pr2-step.md',
      },
      {
        label: 'Deal state CRM comment plan',
        path: 'docs/plans/2026-10-04-deal-state-pr3-crm-comment.md',
      },
      {
        label: 'Deal queue and card API plan',
        path: 'docs/plans/2026-10-04-deal-state-pr4-deal-queue.md',
      },
      {
        label: 'Deal queue and card screens plan',
        path: 'docs/plans/2026-10-05-deal-state-pr4b-deal-ui.md',
      },
      {
        label: 'Deal screens edges plan',
        path: 'docs/plans/2026-10-05-deal-screens-edges.md',
      },
      {
        label: 'Demo deals plan',
        path: 'docs/plans/2026-10-05-deal-state-pr4c-demo-seed.md',
      },
    ],
    code: [
      {
        label: 'Sales methodology contract',
        path: 'packages/api-types/src/schemas/sales-methodology.ts',
      },
      {
        label: 'Sales methodology API',
        path: 'apps/edge-api/src/routes/crm-sales-methodology.ts',
      },
      databaseRepository(
        'Pipeline sales methodology repository',
        'CrmPipelineSalesMethodologyRepository.ts'
      ),
      {
        label: 'Sales methodology block',
        path: 'apps/ui-worker/src/features/prompt-builder/components/PipelineSalesMethodology.tsx',
      },
      {
        label: 'Deal state contract',
        path: 'packages/api-types/src/schemas/deal-state.ts',
      },
      {
        label: 'deal_states migration',
        path: 'apps/ops-tools/src/migrations/postgres/202610032218_deal_states.sql',
      },
      databaseRepository('Deal state repository', 'DealStateRepository.ts'),
      databaseRepository('Deal state lease repository', 'DealStateLeaseRepository.ts'),
      {
        label: 'Deal state step (one attempt)',
        path: 'apps/workflows-api/src/workers/deal-state/step.ts',
      },
      {
        label: 'Deal state prompt',
        path: 'apps/workflows-api/src/workers/deal-state/prompt.ts',
      },
      {
        label: 'Deal state model call and normalization',
        path: 'apps/workflows-api/src/workers/deal-state/extract.ts',
      },
      {
        label: 'Deal state gate, provider and costs',
        path: 'apps/workflows-api/src/workers/deal-state/deps.ts',
      },
      {
        label: 'Deal state enqueue step (record pipeline)',
        path: 'apps/workflows-api/src/workflows/record-pipeline/steps/deal-state-step.ts',
      },
      {
        label: 'Deal state workflow (per-deal queue)',
        path: 'apps/workflows-api/src/workflows/deal-state-workflow.ts',
      },
      {
        label: 'Deal state retention',
        path: 'apps/workflows-api/src/scheduled/deal-state-retention.ts',
      },
      {
        label: 'deal_states CRM comment migration',
        path: 'apps/ops-tools/src/migrations/postgres/202610041055_deal_states_crm_comment.sql',
      },
      {
        label: 'Deal state CRM comment renderer',
        path: 'apps/workflows-api/src/workers/deal-state/comment.ts',
      },
      {
        label: 'Deal state CRM comment publish (gates, claim, receipt)',
        path: 'apps/workflows-api/src/workers/deal-state/publish-comment.ts',
      },
      {
        label: 'Deal state CRM comment copy (4 locales)',
        path: 'packages/core/src/i18n/deal-state-comment.ts',
      },
      {
        label: 'Deal state CRM comment switch (settings contract)',
        path: 'packages/api-types/src/schemas/organizations.ts',
      },
      {
        label: 'Deal state CRM comment switch (Analysis settings → CRM updates)',
        path: 'apps/ui-worker/src/features/analysis-setup/crm-updates/CrmActionsList.tsx',
      },
      {
        label: 'Deal queue, card and CRM task API',
        path: 'apps/edge-api/src/routes/deals/index.ts',
      },
      {
        label:
          'Deal state of a CRM entity for the Bitrix24 deal tab (GET /deals/by-crm-entity)',
        path: 'apps/edge-api/src/routes/deals/by-crm-entity.ts',
      },
      {
        label: 'Deal queue, card and CRM task API client',
        path: 'apps/ui-worker/src/lib/api/domains/deals.api.ts',
      },
      {
        label: 'Deal card link for the CRM comment and task (dealCardUrl)',
        path: 'packages/core/src/constants/app-urls.ts',
      },
      {
        label: 'Deal queue screen (/deals)',
        path: 'apps/ui-worker/src/routes/-deals.lazy.tsx',
      },
      {
        label: 'Deal card screen (/deals/$stateId) with the CRM task button',
        path: 'apps/ui-worker/src/routes/-deal-card.lazy.tsx',
      },
      {
        label: 'Aelo tab in a Bitrix24 deal or lead card (deal card + conversations)',
        path: 'apps/ui-worker/src/routes/embedded/crm-tab/-CrmTabScreen.tsx',
      },
    ],
  },
  {
    id: 'collaboration.realtime',
    title: 'Real-Time Collaboration',
    summary: 'Real-time collaborative editing and presence primitives.',
    area: 'collaboration',
    status: 'planned',
    customerVisible: false,
    surfaces: ['saas_app'],
    packages: unavailableEverywhere(),
    flag: {
      key: 'enableRealTimeCollaboration',
      defaultValue: false,
      isImplemented: false,
      category: 'beta',
      note: 'Real-time infrastructure not built',
    },
  },
  {
    id: 'prompting.call_criteria',
    title: 'Custom Call Criteria',
    // Built in parts (#854): the entity and its CRUD API with the per-tier
    // count limit max_call_criteria — the first count limit here that a route
    // actually enforces (#857); the Postgres store of results, record_signals,
    // shared later with Voice of Customer codes (#858); the pipeline step —
    // one provider call per record after PII redaction and the final
    // category, a third outcome ("unsure"), quotes grounded by turn number
    // rather than matched as text (#860); the call panel and PDF section with
    // four distinguishable states (#862); the constructor (#861), the records
    // list filter (#865) and the dashboard report (#866). Open in every tier
    // since #867. Demo criteria with sample answers in the demo seed (#1502),
    // written under DEMO_CRITERIA_SEED — on in production since #2263. Demo
    // answers of the organization's own built-ins since #2856.
    summary:
      'Questions asked of every conversation — did something happen (yes or no), a 0–100 score, or one of your answer options. An answer that points at something in the conversation comes with a verbatim quote. Answers appear in the call panel and the PDF report, filter the call list, feed the Criteria tab of the dashboard and can be a field of an alert rule, whose in-app card then shows the quote of the criterion. Every plan includes them, up to its criteria limit; the built-in criteria worded by Aelo sit beside them outside that limit. Criteria are created and edited in the web app, and a new one can start from a ready-made template or from a description in your own words that the AI turns into a draft — either only fills in the form, and the wording is then changed freely; the check on recent calls lets you mark answers as wrong and get a suggested edit of the wording to accept or reject; in the embedded Bitrix24 app the answers appear in the call panel and the PDF report of a call, and the Criteria tab is a report only. Demo data comes with two demo criteria and sample answers on the demo conversations (demo data loaded before they were added has none) — shown only with the results, outside the criteria limit, and removed with the demo data. The demo conversations also carry answers of the switched-on built-in criteria of your organization; the built-in criteria themselves belong to the organization and stay when the demo data is removed.',
    area: 'prompting',
    status: 'available',
    customerVisible: true,
    // saas_app since #862: the reading surface is the call panel and the PDF
    // report, both in ui-worker; since #861 also the constructor step.
    // bitrix24_embedded since #862 as well: the embedded Records tab opens the
    // same call panel and PDF (its records router serves /:id/criteria under
    // the organization's flag); since #1577 also the Criteria tab, report only.
    surfaces: ['edge_api', 'workflows_api', 'saas_app', 'bitrix24_embedded'],
    // Open in every tier since #867 (owner's decision 01.10): tier flag
    // defaults derive from packages, so 'included' switches the flag on for
    // every tier; how many criteria each tier keeps is `max_call_criteria`.
    packages: includedEverywhere(),
    limitKeys: ['max_call_criteria'],
    flag: {
      key: 'enableCallCriteria',
      defaultValue: true,
      isImplemented: true,
      category: 'business',
      note: "On in every tier since #867 (packages: included everywhere); an organization override can still switch it off. Gates every /api/v1/call-criteria route, reads included — and the per-record results the call panel reads (#862): an organization without the flag has no criteria to read, AND the pipeline step that answers them (#860) — it reads the flag itself and fails closed, so an unreadable flag costs nothing. Switching the flag on now means an organization's criteria are answered on every new conversation and billed inside the minute price, and the answers are readable in the call panel and the PDF report. It also opens POST /api/v1/call-criteria/trial (#861), the wording trial on recent calls: up to 20 provider calls per click, paid by the PLATFORM rather than the client (cost type criteria_trial), bounded by a per-organization ceiling of 2 clicks a minute, 10 an hour and 20 a day on paid tiers — at most 400 calls a day — and 1 / 3 / 3 on the free tier (owner decision 01.10, #867: at most 60 calls a day, since free organizations can be created without limit). It also shows the Criteria step of the analysis-setup hub (#861): the owner creates, edits, pauses, scopes and archives criteria there and runs the wording trial from the form; an admin sees the list read-only. The form also carries the wording assistant (#1416, parts 2–3): POST /api/v1/call-criteria/draft turns a description of up to 500 characters into a draft wording, and POST /api/v1/call-criteria/revise turns up to 10 trial answers the user marked as wrong (with optional reasons and fragments of those calls around the quoted turns) into a suggested edit of the question, guidance and option labels — one provider call each (a draft that does not validate is retried once), the same model as the step at effort medium, nothing stored, paid on the same account as the trial (cost types criteria_draft / criteria_revise, *_byok on an organization's own key), each bounded by its own per-organization ceiling of 3 a minute, 15 an hour and 30 a day, 1 / 3 / 5 on the free tier (owner decision Р3). Each criterion in that list shows how it answered over the last 7 days of calls — current wording only, counted in Postgres and served by GET /api/v1/call-criteria/activity to org-wide roles only (client, admin, platform owner), since the counts span every project. It also enables the Criteria section of the records list filter (#865): up to three conditions joined by AND — triggered / did not trigger, a score range, list values including archived ones and not found — sent as repeated ?criterion= and checked against the organization's criteria (archived allowed); without the flag the parameter is refused (403) and criteria-filter-options offers no criteria. The filter narrows the list inside the viewer's own visibility boundary. It also shows the Criteria tab of the client and supervisor dashboards (#866): every criterion over the dashboard period with its denominators (evaluated, not applicable, no answer, not evaluated), when it took effect and when its wording changed, the selected one by day or week, its answer distribution and a per-manager table that keeps managers with zero — counted in Postgres by GET /api/v1/reports/criteria and /criteria/:id under the Highlights report scope (agent.list, a supervisor's own projects), its answer distribution, the per-manager numbers and (flag and score criteria) the trend bars opening the calls they counted — the four header counts are plain numbers. In embedded Bitrix24 a call opened in the Records tab shows the answers in its call panel and PDF report, as in the web app (the embedded records router serves the same per-record route, gated by this flag); the session carries this flag in its flags subset (#1347, #1577) and the Criteria tab shows there too — the report only: its numbers do not open the call list there (the embedded Records tab has no criteria filter), and creating and editing criteria stays in the web app.",
    },
    docs: [
      { label: 'Spec', path: 'docs/plans/2026-09-18-custom-criteria.md' },
      {
        label: 'Demo criteria spec',
        path: 'docs/plans/2026-10-15-demo-criteria-seed.md',
      },
    ],
    code: [
      {
        label: 'Definition and edit rules',
        path: 'packages/core/src/call-criteria/stored.ts',
      },
      { label: 'Demo criteria seed', path: 'apps/edge-api/src/utils/demo-criteria.ts' },
      { label: 'Call criteria API', path: 'apps/edge-api/src/routes/call-criteria.ts' },
      {
        label: 'Wording trial on recent calls',
        path: 'apps/workflows-api/src/workers/criteria/trial.ts',
      },
      {
        label: 'Wording assistant: draft and edit',
        path: 'apps/workflows-api/src/workers/criteria/assist.ts',
      },
      {
        label: 'Wording assistant prompts',
        path: 'packages/prompt-builder/src/criterion-assist.ts',
      },
      {
        label: 'Answer → stored signal',
        path: 'packages/core/src/call-criteria/signal.ts',
      },
      {
        label: 'Evaluation step',
        path: 'apps/workflows-api/src/workers/criteria/evaluate.ts',
      },
      {
        label: 'Per-record results API',
        path: 'apps/edge-api/src/routes/records/criteria.ts',
      },
      {
        label: 'Call-panel group',
        path: 'apps/ui-worker/src/components/details/v2/analysis/CriteriaSection.tsx',
      },
      {
        label: 'PDF section',
        path: 'apps/ui-worker/src/lib/print-report/sections/criteria.ts',
      },
      {
        label: 'Constructor step',
        path: 'apps/ui-worker/src/features/call-criteria/components/CallCriteriaStep.tsx',
      },
      {
        label: 'Criterion templates (catalog ×4 locales)',
        path: 'packages/core/src/call-criteria/templates.ts',
      },
      {
        label: 'Template picker in the form',
        path: 'apps/ui-worker/src/features/call-criteria/components/TemplatePicker.tsx',
      },
      uiDomainClient('Call criteria domain client', 'call-criteria.api.ts'),
      {
        label: '7-day activity in the constructor',
        path: 'apps/ui-worker/src/features/call-criteria/activity-summary.ts',
      },
      {
        label: 'Records list filter by criteria',
        path: 'apps/ui-worker/src/components/filters/CriteriaFilterSection.tsx',
      },
      {
        label: 'Dashboard Criteria tab (web and embedded)',
        path: 'apps/ui-worker/src/components/dashboards/shared/criteria-report/CriteriaReportTab.tsx',
      },
      {
        label: 'Criteria tab availability and data',
        path: 'apps/ui-worker/src/hooks/useCriteriaReport.ts',
      },
      {
        label: 'Criteria report API',
        path: 'apps/edge-api/src/routes/reports-criteria.ts',
      },
      {
        label: 'Criteria filter condition check',
        path: 'apps/edge-api/src/utils/criteria-filter.ts',
      },
      databaseRepository('Call criteria repository', 'CallCriterionRepository.ts'),
      databaseRepository('Criterion results repository', 'RecordSignalRepository.ts'),
    ],
  },
  {
    id: 'prompting.builtin_criteria',
    title: 'Built-in Call Criteria',
    // #868: stage E1 answered built-ins "in the shadow"; stage E2 (#2490) moved
    // every consumer onto their answers and, with its last PR (#2521), made
    // them visible to every customer by default.
    summary:
      "Eight criteria worded by Aelo — company named, next contact agreed, recording disclosed, satisfaction guarantee, employee profanity, a forbidden word from the organization's own list (#2784), TCPA compliance on outbound sales calls and a data-privacy violation — that sit beside an organization's own criteria in Analysis Setup → Criteria, outside the plan's criteria limit. Their wording is fixed — the forbidden-words criterion takes the organization's list of up to 100 words, and stays paused while the list is empty; the owner pauses them and narrows their scope within the limits Aelo set. Their answers feed the attention flag, the CRM report, the mapped Bitrix24 card fields Recording Disclaimer, TCPA Compliance and Data Privacy (1 passed, 0 failed), the Compliance Results card, the Highlights tab, the call panel and PDF, the records filter and the alert gallery, and they are the only place the said / not said checks happen: the main analysis and the analysis profile no longer carry mandatory phrases, profanity, the recording disclaimer or the compliance block (a new organization gets them from code defaults — company named, next contact agreed and satisfaction guarantee for product-inquiry, purchase, scheduling and follow-up calls, employee profanity for all calls, recording disclosed paused until the customer turns it on). The data-privacy criterion (calls, organizations with a concrete jurisdiction) is active: its wording counts asking for card secrets and naming the customer's data after the person who answered said they ordered nothing, not an ordinary order call to the customer. The satisfaction guarantee is active as well (#3137): it applies only when the customer asks whether they can return or exchange, or says they fear the item will not suit them — on every other call it answers not applicable, not \"not said\"; no built-in is paused by Aelo.",
    area: 'prompting',
    status: 'available',
    customerVisible: true,
    surfaces: ['edge_api', 'workflows_api', 'saas_app', 'bitrix24_embedded'],
    // Included everywhere since #2521: tier flag defaults derive from packages,
    // so the flag is on in every tier; the effective value still needs
    // enableCallCriteria and no own provider key (builtinCriteriaEffective).
    packages: includedEverywhere(),
    flag: {
      key: 'builtinCriteriaVisible',
      defaultValue: true,
      isImplemented: true,
      category: 'business',
      // The customer must not switch it themselves: it hides the built-ins from
      // the customer routes (plan E2, decisions 1 and 11) — it stays the
      // platform owner's per-org switch.
      platformOwnerOnly: true,
      note: "Stage E2 switch per organization (#868, plan docs/plans/2026-10-18-builtin-criteria-e2.md decisions 1 and 11), on by default since #2521. On: built-ins are visible and editable within BUILTIN_FIXED_FIELDS on every customer route, and every consumer reads their answers — the attention flag, the CRM report, the mapped CRM card compliance fields (#2860), the Compliance Results card, the Highlights block, the call panel and PDF, the records filter (answer confirmed by the current analysis run) and alert rules on criteria.<uuid> paths. Since stage E3 (#2846, owner decision О1) the built-ins are the only place the said / not said checks happen: the main analysis writes no mandatory_phrases, profanity_detection, recording_disclaimer or compliance fields and the profile has no toggles for them, so this flag is no longer a rollback of E2 — switched off, it only hides the built-ins and the organization has none of these checks (as with forbidden words since #2784). Off (an org override by the platform owner): every customer route builds the criteria repository in customer mode (apps/edge-api/src/utils/customer-criteria.ts), which adds origin = 'custom' to every read and refuses to edit or archive a built-in (404); consumers read the old analysis fields, which exist only in analyses made before E3. The effective value (builtinCriteriaEffective, #2515) also needs enableCallCriteria and no own OpenAI key of the org: a BYOK org's built-ins are not evaluated, so since E3 it has none of these checks (evaluating built-ins on the customer's own key is an open question). New organizations get their built-ins from reconciliation once the platform latch criteria.builtinSeeding is on, seeded from BUILTIN_DEFAULT_SEED; reconciliation never rewrites the scope or pause of an existing row.",
    },
    docs: [
      { label: 'Spec', path: 'docs/plans/2026-10-01-builtin-criteria.md' },
      { label: 'Stage E1 plan', path: 'docs/plans/2026-10-02-builtin-criteria-e1.md' },
      { label: 'Stage E2 plan', path: 'docs/plans/2026-10-18-builtin-criteria-e2.md' },
      {
        label: 'Criteria user guide',
        path: 'apps/website/src/content/docs/en/docs/user-guide/call-criteria.md',
      },
    ],
    code: [
      {
        label: 'Built-in criteria catalog',
        path: 'packages/core/src/call-criteria/builtin-catalog.ts',
      },
      {
        label: 'Customer-mode criteria repository',
        path: 'apps/edge-api/src/utils/customer-criteria.ts',
      },
      {
        label: 'Seeding and reconciliation',
        path: 'apps/edge-api/src/services/builtin-criteria.ts',
      },
      {
        label: 'Per-record results API',
        path: 'apps/edge-api/src/routes/records/criteria.ts',
      },
      {
        label: 'Call panel: old rows yield to built-ins',
        path: 'apps/ui-worker/src/lib/records/builtin-legacy-rows.ts',
      },
      databaseRepository('Call criteria repository', 'CallCriterionRepository.ts'),
    ],
  },
  {
    id: 'prompting.xml_prompts',
    title: 'XML Prompt Templates',
    summary: 'XML prompt templates used by the analysis worker.',
    area: 'prompting',
    status: 'available',
    customerVisible: false,
    surfaces: ['workflows_api'],
    packages: includedEverywhere(),
    flag: {
      key: 'enableXmlPrompts',
      defaultValue: true,
      isImplemented: true,
      category: 'beta',
      note: 'Connected to analyze worker',
    },
  },
  {
    id: 'projects.workspace',
    title: 'Projects Workspace',
    summary:
      'Project list/create/detail/settings, project lifecycle actions, project stats, and project-scoped configuration.',
    area: 'settings',
    status: 'available',
    customerVisible: true,
    surfaces: ['saas_app', 'edge_api'],
    packages: includedEverywhere(),
    limitKeys: ['max_projects'],
    code: [
      { label: 'Projects API', path: 'apps/edge-api/src/routes/projects/index.ts' },
      uiDomainClient('Projects domain client', 'projects.api.ts'),
      { label: 'Projects UI', path: 'apps/ui-worker/src/routes/projects.tsx' },
      {
        label: 'Project settings UI',
        path: 'apps/ui-worker/src/routes/projects.$projectId.settings/index.tsx',
      },
      databaseRepository('Project repository', 'ProjectRepository.ts'),
    ],
  },
  {
    id: 'access.team_members_invitations',
    title: 'Team Members And Invitations',
    summary:
      'User, team, membership, invitation, role visibility, and invitation-acceptance workflows.',
    area: 'access',
    status: 'available',
    customerVisible: true,
    surfaces: ['saas_app', 'edge_api'],
    packages: includedEverywhere(),
    limitKeys: ['max_users'],
    code: [
      { label: 'Users API', path: 'apps/edge-api/src/routes/users/index.ts' },
      uiDomainClient('Users domain client', 'users.api.ts'),
      { label: 'Invitations API', path: 'apps/edge-api/src/routes/invitations.ts' },
      uiDomainClient('Invitations domain client', 'invitations.api.ts'),
      { label: 'My access API', path: 'apps/edge-api/src/routes/access.ts' },
      uiDomainClient('My access domain client', 'access.api.ts'),
      {
        label: 'Team settings UI',
        path: 'apps/ui-worker/src/routes/settings/components/TeamSection.tsx',
      },
      databaseRepository('User repository', 'UserRepository.ts'),
      databaseRepository(
        'Organization member repository',
        'OrganizationMemberRepository.ts'
      ),
      databaseRepository('Invite repository', 'InviteRepository.ts'),
    ],
  },
  {
    id: 'access.embedded_access_requests',
    title: 'Embedded Access Requests',
    summary:
      'Bitrix24 embedded-user access request flow and platform/admin approval surfaces.',
    area: 'access',
    status: 'available',
    customerVisible: true,
    surfaces: ['bitrix24_embedded', 'saas_app', 'edge_api'],
    packages: includedEverywhere(),
    code: [
      {
        label: 'Access request API',
        path: 'apps/edge-api/src/routes/access-requests.ts',
      },
      {
        label: 'Access gate UI',
        path: 'apps/ui-worker/src/features/onboarding/AccessRequestGate.tsx',
      },
      {
        label: 'Pending requests UI',
        path: 'apps/ui-worker/src/routes/settings/components/PendingRequestsSection.tsx',
      },
    ],
  },
  {
    id: 'access.org_hierarchy_supervision',
    title: 'Org-Hierarchy Supervision',
    summary:
      "A department head sees the records handled by everyone under them in the imported org tree, in addition to (not instead of) their assigned projects — for the record list, search, and every detail surface (card, transcript, analysis). Subordinates count whether or not they have an Aelo account: employees the import keeps as pending members (no account to link to) are matched by their CRM identity, qualified by the record's own provider. The head still needs an account, because access is granted to a person. Demo data loaded from October 2026 on comes with a sample tree — a demo company with Sales and Customer support — whose demo employees are matched to their demo calls; removing the demo data removes it.",
    area: 'access',
    status: 'available',
    customerVisible: true,
    surfaces: ['saas_app', 'edge_api', 'workflows_api', 'bitrix24_embedded'],
    packages: includedEverywhere(),
    flag: {
      key: 'orgHierarchySupervision',
      defaultValue: true,
      isImplemented: true,
      category: 'business',
      note: "Kill-switch for the org-hierarchy branch only — narrows it, never widens it. The project-membership boundary underneath is unaffected by this flag. Read once per request in the route (isOrgFeatureEnabled in edge-api, isOrgHierarchySupervisionEnabled in workflows-api), not inside recordVisibleTo/buildViewerScopeCondition — avoids a D1 read on every visibility check. recordVisibleTo's hierarchyEnabled option is optional and defaults to enabled: a defensive fallback for a call site added later that forgets to read the flag, not a statement that either app's current caller skips it.",
    },
    docs: [
      {
        label: 'Team management guide — roles, permissions, and org structure',
        path: 'apps/website/src/content/docs/en/docs/user-guide/team.md',
      },
    ],
    code: [
      {
        label: 'Org structure tables + repository',
        path: 'packages/database/src/repositories/OrgStructureRepository.ts',
      },
      {
        label: 'Record visibility rule (recordVisibleTo)',
        path: 'packages/database/src/access/record-visibility.ts',
      },
      {
        label: 'List/search viewer-scope predicate (buildViewerScopeCondition)',
        path: 'packages/database/src/repositories/record-metadata-queries.ts',
      },
      { label: 'Org structure API', path: 'apps/edge-api/src/routes/org-structure.ts' },
      {
        label: 'Bitrix24 department-tree connector',
        path: 'packages/connectors/src/crm/field-service/bitrix24-department.ts',
      },
      uiDomainClient('Org structure domain client', 'org-structure.api.ts'),
      {
        label: 'Org structure settings UI',
        path: 'apps/ui-worker/src/routes/settings/components/OrgStructureSection.tsx',
      },
      databaseRepository('Org structure repository', 'OrgStructureRepository.ts'),
    ],
    notes: [
      "workflows-api narrows GET /records/:recordId through the same recordVisibleTo rule, but that app's hand-rolled RBAC matrix has no visibleViaHierarchy concept — a department head without project membership gets 403 there before the hierarchy branch is ever reached. edge-api does not have this gap.",
      "Analysis annotations and reviews are not covered by this layer and stay on the pre-existing project-membership gate: a department head can read a subordinate's analysis but cannot see its annotations, annotate or review it except as a supervisor assigned to the record's project (the annotations and review routes answer 403 to a supervisor and 404 to an agent there; the record panel keeps the analysis and says the notes are closed).",
      'A department with no head assigned (no UF_HEAD in Bitrix24, or the head not yet matched to an Aelo user) grants no access through this branch — import first resolves email matches, then imports the department tree, so a head must exist as a matched user before the import can wire them up.',
      "edge-api and workflows-api resolve this flag through two different paths that happen to agree only because this feature ships includedEverywhere() with isImplemented: true: edge-api layers platform default → tier default → org override → the isImplemented guard (resolveOrgFlags), while workflows-api reads only the raw D1 override row (it cannot pull in @aica/stripe without inverting its package graph) — changing defaultValue or isImplemented here moves edge-api's resolution but not workflows-api's.",
    ],
  },
  // E1.5 «узкие права на аналитику» (#1336), шесть PR; PR 6 — выпуск
  // (06.10.2026). Тариф — Pro и выше (решение владельца 06.10). Первый ответ
  // владельца в тот же день был «все тарифы, включая Free» и стоял на неверной
  // посылке «грант только сужает доступ»: «Руководитель группы» у агента
  // открывает записи, расшифровки, аудио и оценки коллег в выбранных отделах, а
  // «Аналитик» на организацию у супервайзера без проектов — агрегаты по всей
  // организации; роль всего этого не даёт. Грант ЗАМЕНЯЕТ то, что роль
  // показывает в аналитике, на то, что открывает пресет в выбранной области, —
  // уже или шире роли. Назначает только владелец или админ. Гейт строится из
  // `packages` ниже, как у `deals.state_summary`: сначала тариф, потом флаг.
  {
    id: 'access.analytics_grants',
    title: 'Narrow Analytics Access Grants',
    summary:
      "An owner (client role) or an admin gives a supervisor or an agent an analytics grant that replaces what their role shows for analytics, and revokes it. Two presets: «Analyst» — aggregates over the whole organization or over chosen projects — and «Group lead» — aggregates, team scores, records and evidence (transcripts, audio) of chosen departments. A grant can be narrower or wider than the role: a Group lead grant lets an agent read colleagues' records in those departments, an Analyst grant on the whole organization lets a supervisor without projects see organization-wide aggregates. In Settings → Team, an «Access» column shows each member's grant; an owner or an admin assigns, replaces or revokes it there, or invites a new member with limited access in one step. While a grant is active the person sees analytics only as the grant allows; their role, project assignments and billing stay as they were, but an action on a project — editing, archiving or deleting it, its scripts, supervisors, alert channels, routing and delivery rules — goes through only for a project the grant covers (any project of a whole-organization Analyst grant, the chosen projects of a project-scoped one, none for a Group lead grant, whose scope is departments), and creating a project only with a whole-organization Analyst grant; reading a project's scripts, routing and delivery rules stops at the same boundary — a project outside it is refused, and the lists show only the projects the grant covers; revoking returns them to the role. Deleting a project or a department — including a department an org-structure import drops — takes it out of every grant that names it at once: the grant keeps the projects or departments left, and a grant left with none is revoked, returning the person to their role. A grant rides only on a supervisor or an agent: changing such a person to owner or admin is refused until the grant is revoked. Every assignment and revoke is written to the audit log (there is no screen for it yet).",
    area: 'access',
    status: 'available',
    customerVisible: true,
    // The embedded surface is the API only: `usersRouter` is mounted under the
    // embedded data router, so `/api/v1/embedded/users/:id/access` answers an
    // Owner/Admin session with the same `user.manage_access` right and the same
    // tier-and-flag gate. The embedded app has no Team screen and its session
    // flag map (`embeddedSessionFlagsSchema`) does not carry the flag.
    surfaces: ['saas_app', 'bitrix24_embedded', 'edge_api'],
    // Pro and up (owner decision 06.10), like deal state.
    packages: proPlus(),
    flag: {
      key: 'enableAccessGrants',
      defaultValue: false,
      isImplemented: true,
      category: 'business',
      note: 'Pro and up (owner decision 06.10, #1336 PR 6; the first answer «every plan» rested on the false premise that a grant only narrows access — a Group lead grant on an agent widens reads). The tier first, then the flag, as for deal state: PUT /api/v1/users/:id/access (and its embedded mirror) and an invite carrying `accessGrant` (POST /api/v1/users/invite) answer 403 tier_not_eligible below Pro and 403 feature_disabled when the flag is off for the organization (a per-organization override row is a platform-admin action: /admin/feature-flags); the Team screen then offers neither the assign form nor the invite option, and shows the «Access» column only when someone already holds a grant. Gates assignment only: reading a grant (GET /api/v1/users/:id/access, GET /api/v1/access/grants, GET /api/v1/access/departments), revoking one (DELETE /api/v1/users/:id/access) and enforcement of grants already stored do not depend on the tier or the flag — an organization that falls below Pro keeps its grants enforced and can revoke them. On the embedded Bitrix24 surface the API also answers under the embedded prefix (/api/v1/embedded/users/:id/access) for an Owner/Admin session, with the same right and the same gate; the embedded app has no Team screen and its session flag map does not carry the flag.',
    },
    docs: [
      {
        label: 'Team guide: narrow analytics access',
        path: 'apps/website/src/content/docs/en/docs/user-guide/team.md',
      },
      {
        label: 'Access grants spec',
        path: 'docs/plans/2026-10-01-dashboard-e15-access-grants.md',
      },
    ],
    code: [
      { label: 'Access grants API', path: 'apps/edge-api/src/routes/users/access.ts' },
      {
        label: 'Access grants list and departments reads',
        path: 'apps/edge-api/src/routes/access.ts',
      },
      {
        label: 'Invite with access (POST /users/invite)',
        path: 'apps/edge-api/src/routes/users/core.ts',
      },
      {
        label: 'Team screen: «Access» column, invite option',
        path: 'apps/ui-worker/src/routes/settings/components/TeamSection.tsx',
      },
      {
        label: 'Access dialog and picker (presets, scope, departments)',
        path: 'apps/ui-worker/src/routes/settings/components/AccessGrantSheet.tsx',
      },
      {
        label: 'Access grant contract',
        path: 'packages/api-types/src/schemas/access.ts',
      },
      databaseRepository('Access grant repository', 'AccessGrantRepository.ts'),
    ],
  },
  {
    id: 'onboarding.saas_wizard',
    title: 'SaaS Onboarding Wizard',
    summary:
      'SaaS onboarding, quick context collection, compliance setup gate, first-login redirect, and preview flow.',
    area: 'onboarding',
    status: 'available',
    customerVisible: true,
    surfaces: ['saas_app', 'edge_api'],
    packages: includedEverywhere(),
    docs: [
      {
        label: 'Onboarding redesign',
        path: 'docs/plans/2026-04-15-saas-onboarding-redesign.md',
      },
    ],
    code: [
      { label: 'Onboarding API', path: 'apps/edge-api/src/routes/onboarding.ts' },
      {
        label: 'Onboarding UI',
        path: 'apps/ui-worker/src/features/onboarding/SaaSOnboardingWizard.tsx',
      },
      { label: 'Route', path: 'apps/ui-worker/src/routes/onboarding.tsx' },
    ],
  },
  {
    id: 'onboarding.embedded_bitrix24',
    title: 'Embedded Bitrix24 Onboarding',
    summary:
      'Embedded auth/session, install/onboard status, embedded consent gate, Smart Process step, and embedded dashboard entry.',
    area: 'onboarding',
    status: 'available',
    customerVisible: true,
    surfaces: ['bitrix24_embedded', 'edge_api'],
    packages: includedEverywhere(),
    code: [
      { label: 'Embedded auth', path: 'apps/edge-api/src/routes/embedded-auth.ts' },
      { label: 'Embedded onboard', path: 'apps/edge-api/src/routes/embedded-onboard.ts' },
      {
        // Клиента `embedded.api.ts` больше нет: он не вызывался ни разу и
        // удалён (ecef6e676). Embedded-режим ходит прямым fetch через этот
        // хук — он и есть точка, где живёт его авторизация.
        label: 'Embedded auth fetch',
        path: 'apps/ui-worker/src/hooks/useEmbeddedAuthFetch.ts',
      },
      {
        label: 'Embedded dashboard',
        path: 'apps/ui-worker/src/routes/embedded/dashboard/index.tsx',
      },
      databaseRepository('Embedded install repository', 'EmbeddedInstallRepository.ts'),
      databaseRepository('Embedded session repository', 'EmbeddedSessionRepository.ts'),
    ],
  },
  {
    id: 'compliance.consent_management',
    title: 'Consent Management',
    summary:
      'Consent status, grant/withdraw events, embedded consent gate, and organization compliance confirmation.',
    area: 'compliance',
    status: 'available',
    customerVisible: true,
    surfaces: ['saas_app', 'bitrix24_embedded', 'edge_api'],
    packages: includedEverywhere(),
    docs: [{ label: 'Security policy', path: 'SECURITY.md' }],
    code: [
      { label: 'Consent API', path: 'apps/edge-api/src/routes/consent.ts' },
      uiDomainClient('Consent domain client', 'consent.api.ts'),
      {
        label: 'Embedded consent API',
        path: 'apps/edge-api/src/routes/embedded-consent.ts',
      },
      {
        label: 'Consent settings UI',
        path: 'apps/ui-worker/src/routes/settings/components/ConsentSection.tsx',
      },
      databaseRepository('Consent event repository', 'ConsentEventRepository.ts'),
    ],
  },
  {
    id: 'compliance.dsr_requests',
    title: 'Data Subject Requests',
    summary:
      'DSR request list/create/approve/reject/delete flow, with execution still requiring manual handling.',
    area: 'compliance',
    status: 'partial',
    customerVisible: true,
    surfaces: ['saas_app', 'edge_api'],
    packages: includedEverywhere(),
    code: [
      { label: 'DSR API', path: 'apps/edge-api/src/routes/dsr.ts' },
      uiDomainClient('DSR domain client', 'dsr.api.ts'),
      {
        label: 'DSR settings UI',
        path: 'apps/ui-worker/src/routes/settings/components/DSRSection.tsx',
      },
      { label: 'D1 DSR schema', path: 'packages/database/src/schema/d1/state.ts' },
      databaseRepository('DSR request repository', 'DSRRequestRepository.ts'),
    ],
    notes: [
      'Route returns 501 for automated execution; approval/rejection/request tracking is implemented.',
    ],
  },
  {
    id: 'compliance.dsr_execution',
    title: 'Automated DSR Execution',
    summary:
      'Automated execution of approved data subject requests across records, metadata, transcripts, and storage objects.',
    area: 'compliance',
    status: 'not_implemented',
    customerVisible: false,
    surfaces: ['saas_app', 'edge_api'],
    packages: unavailableEverywhere(),
    flag: {
      key: 'enableDSRExecution',
      defaultValue: false,
      isImplemented: false,
      category: 'beta',
      note: 'Backend route returns 501; execute button remains hidden unless the flag is explicitly present and true',
    },
    docs: [{ label: 'Security policy', path: 'SECURITY.md' }],
    code: [
      { label: 'DSR execute route', path: 'apps/edge-api/src/routes/dsr.ts' },
      uiDomainClient('DSR domain client', 'dsr.api.ts'),
      {
        label: 'DSR settings UI',
        path: 'apps/ui-worker/src/routes/settings/components/DSRSection.tsx',
      },
      databaseRepository('DSR request repository', 'DSRRequestRepository.ts'),
    ],
  },
  {
    id: 'admin.organizations_console',
    title: 'Admin Organizations Console',
    summary:
      'Platform-owner organization management, org-level inspection, credits, API keys, and cross-org administrative views.',
    area: 'admin',
    status: 'available',
    customerVisible: false,
    surfaces: ['admin_console', 'edge_api'],
    packages: packageAvailability('internal'),
    code: [
      { label: 'Admin org API', path: 'apps/edge-api/src/routes/admin/organizations.ts' },
      uiDomainClient('Admin domain client', 'admin.api.ts'),
      uiDomainClient('Feature flags domain client', 'feature-flags.api.ts'),
      { label: 'Admin UI', path: 'apps/ui-worker/src/routes/admin/index.tsx' },
      {
        label: 'Admin sidebar',
        path: 'apps/ui-worker/src/routes/admin/components/AdminSidebar.tsx',
      },
      databaseRepository('Organization repository', 'OrganizationRepository.ts'),
      databaseRepository('Feature flags repository', 'FeatureFlagsRepository.ts'),
    ],
  },
  {
    id: 'admin.platform_defaults_credentials',
    title: 'Platform Defaults And Provider Credentials',
    summary:
      'Platform settings, global provider credentials, organization provider credentials, and admin API key surfaces.',
    area: 'admin',
    status: 'available',
    customerVisible: false,
    surfaces: ['admin_console', 'edge_api'],
    packages: packageAvailability('internal'),
    code: [
      {
        label: 'Platform settings API',
        path: 'apps/edge-api/src/routes/admin/platform-settings.ts',
      },
      uiDomainClient('Admin domain client', 'admin.api.ts'),
      {
        label: 'Global credentials API',
        path: 'apps/edge-api/src/routes/admin/global-credentials.ts',
      },
      {
        label: 'Credential schema',
        path: 'packages/database/src/schema/d1/operational.ts',
      },
      databaseRepository('Platform settings repository', 'PlatformSettingsRepository.ts'),
      databaseRepository(
        'Organization provider credentials repository',
        'OrganizationProviderCredentialsRepository.ts'
      ),
      databaseRepository(
        'Platform provider credentials repository',
        'PlatformProviderCredentialsRepository.ts'
      ),
    ],
  },
  {
    id: 'admin.reports_console',
    title: 'Admin Reports Console',
    summary:
      'Platform-owner report surfaces for operational, billing, and system oversight.',
    area: 'admin',
    status: 'available',
    customerVisible: false,
    surfaces: ['admin_console', 'edge_api'],
    packages: packageAvailability('internal'),
    code: [
      { label: 'Admin reports API', path: 'apps/edge-api/src/routes/admin/reports.ts' },
      uiDomainClient('Admin domain client', 'admin.api.ts'),
      { label: 'Admin reports UI', path: 'apps/ui-worker/src/routes/admin/reports.tsx' },
    ],
  },
  {
    id: 'operations.backfill_recovery',
    title: 'Backfill And Recovery Operations',
    summary:
      'Backfill workflows, embedding backfill, idempotency reset, media recovery, checkpoints, and operational debug routes.',
    area: 'operations',
    status: 'available',
    customerVisible: false,
    surfaces: ['admin_console', 'workflows_api', 'edge_api'],
    packages: packageAvailability('internal'),
    code: [
      { label: 'Admin backfill API', path: 'apps/edge-api/src/routes/admin/backfill.ts' },
      { label: 'Workflow admin API', path: 'apps/workflows-api/src/routes/admin.ts' },
      {
        label: 'Backfill workflow',
        path: 'apps/workflows-api/src/workflows/backfill-workflow.ts',
      },
      databaseRepository('Ingest checkpoint repository', 'IngestCheckpointRepository.ts'),
    ],
  },
  {
    id: 'operations.pipeline_observability',
    title: 'Pipeline Observability And Ingest Log',
    summary:
      'Ingest activity log, stage statuses, DLQ events, traces, and operator-facing pipeline diagnostics.',
    area: 'operations',
    status: 'available',
    customerVisible: false,
    surfaces: ['admin_console', 'edge_api', 'workflows_api'],
    packages: packageAvailability('internal'),
    code: [
      { label: 'Ingest log API', path: 'apps/edge-api/src/routes/ingest-log.ts' },
      uiDomainClient('Ingest log domain client', 'ingest-log.api.ts'),
      {
        label: 'Record audit envelope',
        path: 'apps/workflows-api/src/utils/record-audit-envelope.ts',
      },
      { label: 'D1 pipeline schema', path: 'packages/database/src/schema/d1/state.ts' },
      databaseRepository(
        'Ingest activity log repository',
        'IngestActivityLogRepository.ts'
      ),
    ],
  },
  {
    id: 'operations.provider_model_catalog',
    title: 'Provider Model Catalog And Routing Policies',
    summary:
      'Provider cost catalog, model routing policies, provider credential hierarchy, and cost-aware provider selection.',
    area: 'operations',
    status: 'available',
    customerVisible: false,
    surfaces: ['admin_console', 'edge_api', 'workflows_api'],
    packages: packageAvailability('internal'),
    docs: [
      {
        label: 'Provider catalog schema',
        path: 'packages/core/src/schemas/provider-catalog.ts',
      },
    ],
    code: [
      {
        label: 'Provider catalog',
        path: 'packages/core/src/schemas/provider-catalog.ts',
      },
      { label: 'Provider registry', path: 'packages/connectors/src/registry.ts' },
      { label: 'D1 lookup schema', path: 'packages/database/src/schema/d1/lookups.ts' },
      databaseRepository('Provider costs repository', 'ProviderCostsRepository.ts'),
    ],
  },
  {
    id: 'admin.audit_log',
    title: 'Audit Log',
    summary: 'Admin-visible audit event history.',
    area: 'admin',
    status: 'planned',
    customerVisible: false,
    surfaces: ['admin_console', 'edge_api'],
    packages: proPlus(),
    flag: {
      key: 'enableAuditLog',
      defaultValue: false,
      isImplemented: false,
      category: 'admin',
      note: 'Audit log system not built',
    },
    code: [databaseRepository('Audit log repository', 'AuditLogRepository.ts')],
  },
  {
    id: 'admin.advanced_permissions',
    title: 'Advanced Permissions',
    summary: 'Configurable permissions beyond the current centralized RBAC model.',
    area: 'admin',
    status: 'planned',
    customerVisible: false,
    surfaces: ['admin_console', 'edge_api'],
    packages: unavailableEverywhere(),
    // No feature flag: centralized RBAC is always active. A toggle will be
    // added here if/when configurable per-org permissions are built.
  },
  {
    id: 'compliance.emotion_analysis_controls',
    title: 'Employee Emotion Scoring Removed',
    summary:
      'Agent/employee emotion scoring is structurally removed from the pipeline (schema, prompts, UI, alerts). Only customer sentiment is collected, with transparency, per EU AI Act Art. 50.',
    area: 'compliance',
    status: 'available',
    customerVisible: false,
    surfaces: ['edge_api', 'workflows_api', 'admin_console'],
    packages: includedEverywhere(),
    docs: [
      { label: 'Security policy', path: 'SECURITY.md' },
      { label: 'EU AI Act plan', path: 'docs/compliance/eu-ai-act-compliance-plan.md' },
    ],
    notes: [
      'Inferring employee emotion in the workplace is a prohibited practice under EU AI Act Art. 5(1)(f). The agent_start/agent_end sentiment fields were removed from prompt-builder schema, analysis types, alert fields, UI, and PDF/print exports — agent emotion is no longer requested from the LLM or rendered anywhere.',
      'The legacy D1 FeatureFlagKey `disableEmotionAnalysis` is now redundant (structural removal supersedes the per-tenant kill switch); it is retained only so existing PUT/DELETE of the legacy key does not 400.',
    ],
  },
  {
    id: 'compliance.call_compliance_results',
    title: 'Call Compliance Results',
    summary:
      'Org-level pass-rates on the admin dashboard for three per-call checks — call recording disclosed, the TCPA checklist on outbound sales calls (company identified, purpose stated, opt-out offered) and personal-data handling — counted from the answers of the matching built-in criteria, and from the AI analysis for calls without such an answer; a check Aelo has paused is marked under the card. Calls analyzed earlier without such an answer are counted from the stored AI analysis; newer analyses carry no compliance section.',
    area: 'compliance',
    status: 'available',
    customerVisible: true,
    surfaces: ['workflows_api', 'edge_api', 'saas_app'],
    packages: includedEverywhere(),
    docs: [
      { label: 'Security policy', path: 'SECURITY.md' },
      {
        label: 'Bitrix24 user guide',
        path: 'apps/website/src/content/docs/en/docs/user-guide/bitrix24.md',
      },
    ],
    code: [
      {
        label: 'Compliance analysis (analyze worker)',
        path: 'apps/workflows-api/src/workers/analyze/worker.ts',
      },
      databaseRepository(
        'Analysis repository (getComplianceResults)',
        'AnalysisRepository.ts'
      ),
      { label: 'Dashboard route', path: 'apps/edge-api/src/routes/dashboard.ts' },
      {
        label: 'Compliance results card',
        path: 'apps/ui-worker/src/components/dashboards/admin/OverviewTab.tsx',
      },
      {
        label: 'Built-in criteria behind the card',
        path: 'apps/edge-api/src/utils/builtin-reports.ts',
      },
    ],
    notes: [
      'Stage E2 of built-in criteria (#2490, on for every organization since #2521): the three rates are counted from recording_disclosed, tcpa_compliant and data_privacy_violation — passed means disclosed, met, and NO violation (inverted polarity) — with the old compliance.* field (applicable only) for calls without an answer of the current analysis; unsure and not-applicable answers leave the denominator. A check paused by Aelo (catalog) carries pausedByAelo and the card says new calls are not evaluated by it. Without E2 (BYOK, or the flag switched off for the org) the card reads the analysis as described below — which, since E3 (#2846), only holds data for analyses made before it.',
      'The dashboard states pass-rates as an AI assessment of the transcript, NOT a legal determination — a permanent disclaimer accompanies the card and the widget never asserts "you are compliant".',
      'Denominator is only calls that actually carried a compliance section (category-dependent — non-call analyses omit it); the evaluated-call count (n) is shown on the widget. n=0 renders an empty state, never a fabricated 100%.',
      'Organization owner, admin and platform owner only (same RBAC gate as adminOps); since stage E3 (#2846) the main analysis no longer evaluates compliance — the built-in criteria do, regardless of tier, on every call where a check applies: recording_disclosed where the customer has turned it on, TCPA where the jurisdiction of the organization is US, data privacy where the jurisdiction is concrete; new analyses carry no compliance section.',
      "The recording disclaimer is the built-in criterion recording_disclosed (calls only — there is no call recording to disclose in a chat): a new organization gets it paused by the customer reason and turns it on in Analysis Setup → Criteria. Before E3 it followed the profile's recordingDisclaimer toggle (#853, migration 080); the stored toggle values are no longer read, and analyses made before E3 keep the verdict they stored.",
    ],
  },
  {
    id: 'provider_health.monitoring',
    title: 'Provider Health Monitoring',
    summary:
      'Provider health probes, snapshots, admin alerts, and system health aggregation.',
    area: 'admin',
    status: 'available',
    customerVisible: false,
    surfaces: ['admin_console', 'workflows_api', 'edge_api'],
    packages: packageAvailability('internal'),
    docs: [
      { label: 'Architecture', path: 'ARCHITECTURE.md' },
      { label: 'Project snapshot', path: 'PROJECT_SNAPSHOT.md' },
    ],
  },
  {
    id: 'demo.sample_data',
    title: 'Demo Data',
    summary: 'Localized demo seed datasets and API/UI flow for product demos.',
    area: 'demo',
    status: 'available',
    customerVisible: false,
    surfaces: ['saas_app', 'edge_api'],
    packages: includedEverywhere(),
    docs: [
      {
        label: 'Demo data revamp',
        path: 'docs/superpowers/plans/2026-05-15-demo-data-revamp-plan-a.md',
      },
      {
        // Demo departments, heads and employees matched by external id (#1500).
        label: 'Demo org structure',
        path: 'docs/plans/2026-10-14-demo-org-structure.md',
      },
    ],
    code: [uiDomainClient('Demo domain client', 'demo.api.ts')],
  },
  {
    id: 'public.website_docs',
    title: 'Public Website And Product Docs',
    summary:
      'Astro marketing/legal site and Starlight customer-facing documentation under localized docs routes.',
    area: 'reporting',
    status: 'available',
    customerVisible: true,
    surfaces: ['website', 'public_docs'],
    packages: includedEverywhere(),
    docs: [{ label: 'Docs index', path: 'docs/README.md' }],
    code: [
      { label: 'Website app', path: 'apps/website/src' },
      { label: 'Public docs content', path: 'apps/website/src/content/docs' },
    ],
  },
  {
    id: 'billing.cost_transparency',
    title: 'Cost Transparency',
    summary:
      'What the organization owner (client role) is charged: wallet balance, the minutes and chats used in the selected period and what they cost, the per-minute and per-chat rates, and a ledger of charges, credits and bonus minutes. On billing screens, provider costs and margin are visible to Aelo only; only the organization owner opens the billing screens (budget alerts with spend and limit also reach administrators).',
    area: 'billing',
    status: 'available',
    customerVisible: true,
    surfaces: ['saas_app', 'edge_api'],
    packages: includedEverywhere(),
    code: [
      { label: 'Billing router', path: 'apps/edge-api/src/routes/billing/index.ts' },
      uiDomainClient('Billing domain client', 'billing.api.ts'),
      databaseRepository('Provider costs repository', 'ProviderCostsRepository.ts'),
    ],
  },
] as const satisfies readonly ProductFeatureDefinition[];

export type ProductFeatureWithFlag = Extract<
  (typeof PRODUCT_FEATURE_CATALOG)[number],
  { readonly flag: ProductFeatureFlagSpec }
>;
export type ProductFeatureFlag = ProductFeatureWithFlag['flag'];
export type ProductFeatureFlagKey = ProductFeatureFlag['key'];

/** Есть ли у записи рычаг, которым её вообще можно ограничить (см. starterPlus). */
export function hasFeatureFlag(
  feature: (typeof PRODUCT_FEATURE_CATALOG)[number]
): feature is ProductFeatureWithFlag {
  return 'flag' in feature;
}

export const PRODUCT_FEATURES_BY_ID: Readonly<Record<string, ProductFeatureDefinition>> =
  Object.freeze(
    Object.fromEntries(PRODUCT_FEATURE_CATALOG.map((feature) => [feature.id, feature]))
  );

/**
 * Flag spec enriched with the owning feature's catalog status, so surfaces
 * like the admin flags page can show "prototype"/"beta"/"planned" instead of
 * the misleading binary isImplemented (a teaser keeps isImplemented: true so
 * an explicit preview override is not force-hidden by enforceUnimplemented,
 * yet is anything but "implemented" to an operator).
 */
export interface ProductFeatureFlagSpecWithStatus extends ProductFeatureFlagSpec {
  readonly featureStatus: ProductFeatureStatus;
}

export const PRODUCT_FEATURE_FLAG_SPECS: readonly ProductFeatureFlagSpecWithStatus[] =
  PRODUCT_FEATURE_CATALOG.filter(hasFeatureFlag).map((feature) => ({
    ...feature.flag,
    featureStatus: feature.status,
  }));

export function getProductFeature(id: string): ProductFeatureDefinition | undefined {
  return PRODUCT_FEATURES_BY_ID[id];
}

export function getFeaturesByArea(
  area: ProductFeatureArea
): readonly ProductFeatureDefinition[] {
  return PRODUCT_FEATURE_CATALOG.filter((feature) => feature.area === area);
}

export function isFeatureIncludedByDefault(
  availability: ProductFeatureAvailability
): boolean {
  return availability === 'included' || availability === 'custom';
}

export function getFeatureFlagDefaultsForPackage(
  packageKey: ProductPackageKey
): Record<string, boolean> {
  return Object.fromEntries(
    PRODUCT_FEATURE_CATALOG.flatMap((feature) => {
      if (!hasFeatureFlag(feature)) return [];
      return [
        [
          feature.flag.key,
          isFeatureIncludedByDefault(feature.packages[packageKey]),
        ] as const,
      ];
    })
  );
}
